import React, { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  collection, getDocs, getDoc,
  doc, onSnapshot, query, orderBy, where, serverTimestamp,
  runTransaction,
} from 'firebase/firestore';
import type { DocumentReference, Transaction, Firestore } from 'firebase/firestore';
import { createdStamp, updatedStamp, logActivity, addedByName } from '../utils/activity';
import AuthorLine from '../components/AuthorLine';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { useNewParam } from '../hooks/useNewParam';
import {
  Button, Modal, ModalBanner, ConfirmModal, FormField, Dropdown, EntityCombobox, PageTabs, TotalsFigures,
} from '../components/ui';
import { EntityFormModal } from '../components/EntityFormModal';
import { useSettings } from '../hooks/useSettings';
import { isIndian, withBusinessSystem, lineNet, invoiceGst, gstInvoiceFields, spreadGstOverGroups, gstConflict, gstMode, defaultGst, NO_GST, type GstPercents, gstInputFrom, gstPercentsOf, type GstInput } from '../utils/tradingSystem';
import { GstBoxes, GstSummary } from '../components/GstBoxes';
import { useGridKeyNav } from '../hooks/useGridKeyNav';
import { OptionCombobox } from '../components/ui/OptionCombobox';
import { rememberLooseOptions } from '../utils/looseOptions';
import { useCategoryFilter } from '../context/CategoryFilterContext';
import { fmtMoney } from '../utils/currency';
import { buildEntry, postEntry, entryId, linesPurchaseInvoice, linesBrokerAccrual, linesPurchaseExpense } from '../utils/ledger';
import { downloadCSV } from '../utils/csv';
import {
  downloadImportWorkbook, loadWorkbook, readSheetRows, matchOption, cleanNum, parseImportDate, xlsxUtcDate,
  XLSX_GROUP_ARGB, type XlsxCol, type XlsxSampleRow,
} from '../utils/excelImport';
import { computeDueGroups, lineDueDate } from '../utils/dueGroups';
import { planDummyDelta, applyDummyDelta } from '../utils/dummyCode';
import type {
  Entity, BusinessSettings,
  PurchaseInvoice, PurchaseLineItem, PurchaseType, DueDateMode,
  DiamondShape, DiamondColor, DiamondClarity, DiamondLab,
  PaymentStatus, InventoryItem, InventoryStatus, Sku, ItemCode, CodeEventType, TransactionCategory,
  PurchaseExpense, PurchaseExpenseTreatment, BankAccount, BrokerPayment,
} from '../types';
import RecordPaymentModal from '../components/RecordPaymentModal';
import { PurchaseDiscountsSection } from '../components/PurchaseDiscounts';
import { invoiceNoMatches } from '../utils/invoiceNo';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { useFitHeight } from '../hooks/useFitHeight';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';

// ── Option lists ──────────────────────────────────────────────────────────────

const SHAPES: DiamondShape[]      = ['Round','Princess','Oval','Cushion','Emerald','Pear','Marquise','Radiant','Asscher','Heart','Other'];
const COLORS: DiamondColor[]      = ['D','E','F','G','H','I','J','K','L','M','Fancy'];
const CLARITIES: DiamondClarity[] = ['FL','IF','VVS1','VVS2','VS1','VS2','SI1','SI2','I1','I2','I3'];
const LABS: DiamondLab[]          = ['GIA','IGI','HRD','AGS','GCAL','EGL','None'];
const CUT_GRADES = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor']; // also used for Polish/Symmetry — same 5-point scale
const FLUORESCENCE_GRADES = ['None', 'Faint', 'Medium', 'Strong', 'Very Strong'];
const PAGE_SIZE                   = 50;

type DateFilter = 'this-month' | '3-months' | 'this-year' | 'custom';
const DATE_FILTER_LABELS: Record<DateFilter, string> = {
  'this-month': 'This Month', '3-months': '3 Months', 'this-year': 'This Year', custom: 'Custom',
};
type RowType    = 'certified' | 'loose';

type SortKey = 'category' | 'invoiceNo' | 'purchaseDate' | 'supplierInvoiceNo' | 'company'
  | 'carat' | 'pricePerCt' | 'netAmount' | 'gstAmount' | 'totalAmount' | 'dueDate' | 'payStatus' | 'addedBy';

const PAY_RANK: Record<PaymentStatus, number> = { unpaid: 0, partial: 1, paid: 2 };

// ── Customize Columns (list table) ───────────────────────────────────────────
// Personal, per-browser preference (localStorage) — deliberately NOT a
// BusinessSettings/Firestore field, since column layout shouldn't sync
// across every user of the business. The selection checkbox and Actions
// columns are excluded on purpose: they're controls, not data, and always
// render. Mirrors the same pattern used on Inventory's list table.

type ColumnKey = 'category' | 'invoiceNo' | 'purchaseDate' | 'company' | 'carat'
  | 'pricePerCt' | 'netAmount' | 'gstAmount' | 'totalAmount' | 'supplierInvoiceNo' | 'dueDate' | 'payStatus' | 'addedBy';

interface ColumnDef { key: ColumnKey; label: string; sortKey: SortKey; skeletonWidth: number; minWidth?: number }

// Order here = column order in the table.
const COLUMNS: ColumnDef[] = [
  // Same icons as Sales' Cat. column: By Books & Real, Cash & Real, By Books & Dummy (or mixed).
  { key: 'category',          label: 'Cat.',         sortKey: 'category',          skeletonWidth: 24 },
  { key: 'invoiceNo',         label: 'Lot No',       sortKey: 'invoiceNo',         skeletonWidth: 90 },
  { key: 'purchaseDate',      label: 'Date',         sortKey: 'purchaseDate',      skeletonWidth: 80 },
  { key: 'company',           label: 'Company',      sortKey: 'company',           skeletonWidth: 130, minWidth: 220 },
  { key: 'carat',             label: 'Total ct',     sortKey: 'carat',             skeletonWidth: 70 },
  { key: 'pricePerCt',        label: 'Cost/ct',      sortKey: 'pricePerCt',        skeletonWidth: 65 },
  // Indian trading system only: Amount (stones) and GST, then Total Amount.
  { key: 'netAmount',         label: 'Amount',       sortKey: 'netAmount',         skeletonWidth: 85 },
  { key: 'gstAmount',         label: 'GST',          sortKey: 'gstAmount',         skeletonWidth: 70 },
  { key: 'totalAmount',       label: 'Total Amount', sortKey: 'totalAmount',       skeletonWidth: 85 },
  { key: 'supplierInvoiceNo', label: 'Supplier Inv', sortKey: 'supplierInvoiceNo', skeletonWidth: 90 },
  { key: 'dueDate',           label: 'Due Date',     sortKey: 'dueDate',           skeletonWidth: 80 },
  { key: 'payStatus',         label: 'Pay Status',   sortKey: 'payStatus',         skeletonWidth: 85 },
  { key: 'addedBy',           label: 'Added by',     sortKey: 'addedBy',           skeletonWidth: 80 },
];
const GST_COLUMN_KEYS = new Set<ColumnKey>(['netAmount', 'gstAmount']);
const CHECKBOX_SKELETON_WIDTH = 20;

const COLUMN_VISIBILITY_KEY = 'purchase-visible-columns';
// "Added by" starts hidden; switch it on from Columns.
const DEFAULT_VISIBLE: Record<ColumnKey, boolean> =
  Object.fromEntries(COLUMNS.map(c => [c.key, c.key !== 'addedBy'])) as Record<ColumnKey, boolean>;

// Merges the saved blob over the all-visible default, key-by-key, so a
// column removed from COLUMNS in a future change is silently dropped and a
// column newly added to COLUMNS defaults to visible for existing users —
// never trust the raw parsed object wholesale.
function IconNoteInfo() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 7.2v3.6M8 5.1v.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>;
}
function loadVisibleColumns(): Record<ColumnKey, boolean> {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (!raw) return { ...DEFAULT_VISIBLE };
    const saved = JSON.parse(raw) as Partial<Record<ColumnKey, boolean>>;
    const merged = { ...DEFAULT_VISIBLE };
    for (const c of COLUMNS) if (typeof saved[c.key] === 'boolean') merged[c.key] = saved[c.key]!;
    return merged;
  } catch { return { ...DEFAULT_VISIBLE }; }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Local calendar day — never toISOString(), which is UTC: in any timezone
// ahead of UTC (India, Europe…) local midnight is still the previous day in
// UTC, so every computed date (due dates, today, filter ranges) came out a day early.
function toDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(n: number) {
  const d = new Date(); d.setDate(d.getDate() + n); return toDateStr(d);
}
function todayISO() { return toDateStr(new Date()); }
function addDaysToDate(base: string, n: number): string {
  if (!base) return addDays(n);
  const d = new Date(base + 'T00:00:00');
  if (isNaN(d.getTime())) return addDays(n);
  d.setDate(d.getDate() + n);
  return toDateStr(d);
}
function num(s: string) { return parseFloat(s) || 0; }
function fmtInvoiceNo(prefix: string, count: number) {
  return `${prefix}-${new Date().getFullYear()}-${String(count).padStart(4, '0')}`;
}
// Lot No is stored in full, but the list only shows the prefix + last 4
// digits to keep the column compact. Keeping the prefix matters — a bare
// last-4 truncation collapses "PC-2026-0001" and "PL-2026-0001" into the
// same "0001", making certified and loose lots indistinguishable (and
// certified lots have no SKU to fall back on as an alternate identifier).
function lotNoDisplay(invoiceNo: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}
function dueDateDisplay(dateStr: string) {
  const due   = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff  = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  const label = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return {
    label,
    badge:     diff === 0 ? 'Today' : diff > 0 ? `${diff}d left` : `${Math.abs(diff)}d over`,
    isOverdue: diff < 0,
    isSoon:    diff >= 0 && diff <= 7,
  };
}
// Deep-clean: also strips undefined out of nested plain objects/arrays (e.g. the
// lineItems array embedded in an invoice doc), which a shallow, top-level-only
// filter misses — Firestore rejects a write with ANY undefined anywhere in it, no
// matter how deeply nested. Firestore sentinels (serverTimestamp(), Timestamp, etc.)
// are class instances, not plain object literals, so they pass through untouched.
function stripUndef<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(v => stripUndef(v)) as unknown as T;
  if (obj !== null && typeof obj === 'object' && (obj as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndef(v)])
    ) as T;
  }
  return obj;
}
function invoiceMetrics(inv: PurchaseInvoice) {
  const ct = inv.totalCarat ?? 0;
  const pricePerCt = ct > 0 ? ((inv.totalAmountUSD ?? 0) - (inv.taxTotalUSD ?? 0)) / ct : 0;
  const costPerCt  = ct > 0 ? (inv.totalCostingUSD ?? 0) / ct : 0;
  return { ct, pricePerCt, costPerCt };
}
// Deterministic Firestore doc ID for a SKU string — lets every write resolve
// tx.get(doc(db,'skus', skuId)) directly inside a transaction, no pre-read query.
function slugifySku(sku: string): string {
  return sku.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
// Same idea as slugifySku, but for Code — normalizes to a Firestore doc ID so
// every purchase/edit/merge write can check tx.get(doc(db,'itemCodes', id))
// for a collision before committing (Code must be globally unique; SKU isn't).
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
// Walks a shared counter forward to the next Code that isn't already taken —
// by an existing itemCodes doc, or by another row in this same save (tracked
// via `reserved`). `cursor` is mutated in place so a run of calls (one per
// blank-Code row) keeps advancing instead of restarting each time; the caller
// persists cursor.n to counters/{businessId}.itemCode once, after the loop.
async function nextFreeCode(
  tx: Transaction, db: Firestore, cursor: { n: number }, reserved: Set<string>,
): Promise<string> {
  for (let attempts = 0; attempts < 200; attempts++) {
    cursor.n += 1;
    const candidate   = String(cursor.n);
    const candidateId = normalizeCode(candidate);
    if (reserved.has(candidateId)) continue;
    const snap = await tx.get(doc(db, 'itemCodes', candidateId));
    if (!snap.exists()) { reserved.add(candidateId); return candidate; }
  }
  throw new Error('Could not auto-generate a unique Code — please enter one manually.');
}

// ── Date range ────────────────────────────────────────────────────────────────

function computeDateRange(
  dateFilter: DateFilter, customFrom: string, customTo: string,
): { start: string; end: string } | null {
  const now = new Date();
  switch (dateFilter) {
    case 'this-month':
      return { start: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), end: toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 1)) };
    case '3-months':
      return { start: toDateStr(new Date(now.getFullYear(), now.getMonth() - 2, 1)), end: toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 1)) };
    case 'this-year':
      return { start: toDateStr(new Date(now.getFullYear(), 0, 1)), end: toDateStr(new Date(now.getFullYear() + 1, 0, 1)) };
    case 'custom': {
      if (!customFrom || !customTo) return null;
      const endDate = new Date(customTo + 'T00:00:00');
      endDate.setDate(endDate.getDate() + 1);
      return { start: customFrom, end: toDateStr(endDate) };
    }
  }
}

// ── Batch item types ──────────────────────────────────────────────────────────

interface BatchHeader {
  fromEntityId: string; purchaseDate: string; terms: number;
  dueDate: string; supplierInvoiceNo: string; notes: string;
  // Optional broker + commission % the buyer owes on this purchase.
  brokerEntityId: string; brokerCommissionPercent: string;
  // 'average' (default) = today's exact behavior, one invoice-level due
  // date. 'separate' = each line item's own Terms produces its own due
  // date, grouped into DueGroups and paid independently — see
  // computeDueGroups in utils/dueGroups.ts.
  dueDateMode: DueDateMode;
}

const EMPTY_BATCH_HEADER: BatchHeader = {
  fromEntityId: '', purchaseDate: todayISO(), terms: 30, dueDate: addDays(30),
  supplierInvoiceNo: '', notes: '', brokerEntityId: '', brokerCommissionPercent: '',
  dueDateMode: 'average',
};

function round2(n: number) { return Math.round(n * 100) / 100; }

// ── Per-line broker commission (Broker1/Broker2 on each line item) ─────────
// Completes the per-line broker feature left at "captured, not computed" —
// one broker can appear on several lines (or both slots of one line), so
// this sums every line's own share for each distinct broker it finds,
// keyed by brokerEntityId. Lives ALONGSIDE the invoice's own legacy
// header-level broker (brokerEntityId/brokerCommissionPercent) — that one
// is untouched by this, kept exactly as it already worked (see the
// brokerAdded/brokerRemoved diff elsewhere in this file). Each broker found
// here gets its OWN broker_accrual journal entry, keyed by a composite id
// (`${invoiceId}_${brokerEntityId}`) so multiple brokers on one invoice —
// and the invoice's own separate header-broker entry — never collide.
function computeLineBrokerCommissions(
  lineItems: Pick<PurchaseLineItem, 'amountUSD' | 'broker1EntityId' | 'broker1CommissionPercent' | 'broker2EntityId' | 'broker2CommissionPercent'>[],
): Map<string, number> {
  const totals = new Map<string, number>();
  for (const li of lineItems) {
    if (li.broker1EntityId && li.broker1CommissionPercent) {
      const amt = round2((li.amountUSD || 0) * li.broker1CommissionPercent / 100);
      totals.set(li.broker1EntityId, round2((totals.get(li.broker1EntityId) ?? 0) + amt));
    }
    if (li.broker2EntityId && li.broker2CommissionPercent) {
      const amt = round2((li.amountUSD || 0) * li.broker2CommissionPercent / 100);
      totals.set(li.broker2EntityId, round2((totals.get(li.broker2EntityId) ?? 0) + amt));
    }
  }
  return totals;
}
const lineBrokerAccrualId = (invoiceId: string, brokerEntityId: string) =>
  entryId('broker_accrual', `${invoiceId}_${brokerEntityId}`);

// ── Additional Details — Purchase Expenses embedded in the invoice ──────────
// Reuses the existing purchaseExpenses collection/PurchaseExpense type
// (see PurchaseExpenses.tsx, unmodified) — this is just a second entry
// point into the same data, so freight/duty/etc. tied to THIS invoice can
// be entered in one save instead of a separate trip to that page. Always
// treatment:'expense' here (hits Profit now) — "Capitalise into stock
// cost" stays a Purchase Expenses page-only option; see that page for why.
const ADDL_EXPENSE_CATEGORIES = ['Freight', 'Duty / Customs', 'Certification', 'Insurance', 'Bank / Transfer Fees', 'Other'];

interface AddlExpenseRow {
  _key:        string;
  // Set only when this row was loaded from an existing purchaseExpenses
  // doc (editing an invoice that already has some) — lets save-time tell
  // "still here, maybe changed" apart from "brand new" and "removed".
  _existingId?: string;
  category:      string;
  description:   string;
  amount:        string;
  date:          string;
  vendorEntityId: string;
  payNow:        boolean;
  bankAccountId: string;
  dueDate:       string;
}

let _addlExpenseKey = 0;
function newAddlExpenseRow(purchaseDate: string): AddlExpenseRow {
  return {
    _key: String(++_addlExpenseKey),
    category: 'Freight', description: '', amount: '', date: purchaseDate,
    vendorEntityId: '', payNow: false, bankAccountId: '', dueDate: '',
  };
}
function addlExpenseToRow(pe: PurchaseExpense): AddlExpenseRow {
  return {
    _key: pe.id, _existingId: pe.id,
    category: pe.category, description: pe.description,
    amount: String(pe.amountUSD), date: pe.date,
    vendorEntityId: pe.vendorEntityId ?? '',
    payNow: pe.paymentStatus === 'paid',
    bankAccountId: pe.bankAccountId ?? '',
    dueDate: pe.dueDate ?? '',
  };
}

interface BatchItem {
  _key:             string;
  itemType:         RowType;   // per-row type — lets certified and loose rows sit in the same invoice
  lab:              string;
  shape:            string;
  carat:            string;
  color:            string;
  clarity:          string;
  certificateNo:    string;
  listPriceAmount:  string;    // certified only — List Price per carat ($/ct) input
  priceListPercent: string;    // certified only — Discount % (negative = discount, positive = premium)
  // Certified-only grading detail.
  cut:              string;
  polish:           string;
  symmetry:         string;
  fluorescence:     string;
  measLength:       string;
  measWidth:        string;
  measDepth:        string;
  size:             string;
  subSize:          string;
  sku:              string;    // loose only — Item Number/SKU, for merge matching
  code:             string;    // loose only — unique per-item Code, required
  description:      string;    // free-text line note — same preset list + widget as Sales' line-item description
  // Price/ct — both types now. For certified rows this is kept in sync with
  // List Price/ct + Discount % by resolveCertPrice() unless the user is
  // editing it directly (priceMode === 'price'); for loose rows it's the
  // plain direct-entry field it always was.
  pricePerCt:       string;
  // Indian only: Less 1 % then Less 2 % on Cost/ct ('' = none).
  less1Percent:     string;
  less2Percent:     string;
  // Certified only — which of Discount%/Price-per-ct the user last edited
  // directly. undefined/'discount' = Discount-driven (List × (1+Disc%) →
  // Price); 'price' = Price-driven (back-computes Discount% instead).
  priceMode:        'discount' | 'price' | undefined;
  addedValuePct:    string;
  // Optional per-line payment-term override (days) — blank = inherit the
  // invoice's own terms. Wired into the data model now (Phase 1); the
  // payment-tracking UI that actually uses it lands in Phase 2.
  terms:            string;
  // Up to two brokers per parcel, each with their own % — loose rows only
  // for now (see PurchaseLineItem.broker1EntityId in types/index.ts for why
  // this is captured/saved but not yet posted to the ledger or shown on
  // Brokers.tsx/AP).
  broker1EntityId:          string;
  broker1CommissionPercent: string;
  broker2EntityId:          string;
  broker2CommissionPercent: string;
  // costingPercent removed — formula is: amount × (1 + AV%) = cost
  // How this stone's movement is classified — see TransactionCategory in
  // types/index.ts. Plain string here (like lab/shape) so it fits the
  // generic updateItem(key, field, value: string) setter; cast to
  // TransactionCategory in buildLineItems. '' = not yet chosen.
  category:         TransactionCategory | '';
}

const EMPTY_ITEM: Omit<BatchItem, '_key' | 'itemType'> = {
  lab: '', shape: '', carat: '', color: '', clarity: '',
  certificateNo: '', listPriceAmount: '', priceListPercent: '',
  cut: '', polish: '', symmetry: '', fluorescence: '',
  measLength: '', measWidth: '', measDepth: '',
  size: '', subSize: '', sku: '', code: '', description: '', pricePerCt: '', priceMode: undefined,
  less1Percent: '', less2Percent: '',
  addedValuePct: '3', terms: '',
  broker1EntityId: '', broker1CommissionPercent: '', broker2EntityId: '', broker2CommissionPercent: '',
  // Defaults every newly-added row to "By Books & Real" — the common case —
  // so only Cash/Dummy stones need an explicit change. A row loaded from an
  // existing (pre-this-feature) invoice keeps whatever it actually has
  // instead (see lineItemToBatchItem's `li.category ?? ''`) — never
  // fabricate a category for historical data that was never categorized.
  category: 'books-real',
};

let _batchKey = 0;
function newBatchItem(itemType: RowType = 'certified'): BatchItem {
  return { _key: String(++_batchKey), itemType, ...EMPTY_ITEM };
}

function lineItemToBatchItem(li: PurchaseLineItem, fallbackType: RowType): BatchItem {
  const itemType = (li.itemType ?? fallbackType) as RowType;
  const isCert   = itemType === 'certified';
  // Certified rows: Price/ct always reconstructs from amountUSD/carat —
  // amountUSD is always priceCt × carat (see calcBatchCert), so this is
  // exact regardless of doc age. rapPricePerCt is NOT the right source here:
  // it holds the List (Rapaport) price, a different number from the
  // negotiated Price/ct — using it as a Price/ct fallback was the bug that
  // made the Rap/ct and Price/ct columns always read identical.
  // Indian lines keep the agreed price before Less 1 / Less 2.
  const certPrice = li.grossPricePerCt ?? (li.carat > 0 ? +(li.amountUSD / li.carat).toFixed(4) : 0);
  return {
    _key:             li.id || String(++_batchKey),
    itemType,
    lab:              li.lab              ?? '',
    shape:            li.shape,
    carat:            String(li.carat),
    color:            li.color,
    clarity:          li.clarity,
    certificateNo:    li.certificateNo    ?? '',
    listPriceAmount:  li.priceListAmount != null ? String(li.priceListAmount)  : '',
    priceListPercent: li.priceListPercent != null ? String(li.priceListPercent) : '',
    cut:              li.cut          ?? '',
    polish:           li.polish       ?? '',
    symmetry:         li.symmetry     ?? '',
    fluorescence:     li.fluorescence ?? '',
    measLength:       li.measLength   ?? '',
    measWidth:        li.measWidth    ?? '',
    measDepth:        li.measDepth    ?? '',
    size:             li.size    ?? '',
    subSize:          li.subSize ?? '',
    sku:              li.sku     ?? '',
    code:             li.code    ?? '',
    description:      li.description ?? '',
    pricePerCt:       isCert
      ? (certPrice > 0 ? String(certPrice) : '')
      : (li.grossPricePerCt != null ? String(li.grossPricePerCt) : li.carat > 0 ? String(+(li.amountUSD / li.carat).toFixed(4)) : ''),
    priceMode:        li.priceMode,
    less1Percent:     li.less1Percent ? String(li.less1Percent) : '',
    less2Percent:     li.less2Percent ? String(li.less2Percent) : '',
    addedValuePct:    String(li.addedValuePercent),
    terms:            li.terms != null ? String(li.terms) : '',
    broker1EntityId:          li.broker1EntityId ?? '',
    broker1CommissionPercent: li.broker1CommissionPercent != null ? String(li.broker1CommissionPercent) : '',
    broker2EntityId:          li.broker2EntityId ?? '',
    broker2CommissionPercent: li.broker2CommissionPercent != null ? String(li.broker2CommissionPercent) : '',
    category:         li.category ?? '',
  };
}

// ── Per-item calc — formula: amount × (1 + AV%) = cost ───────────────────────

// Keeps List Price/ct, Discount %, and Price/ct in sync for a certified row
// after any one of the three is edited. `it.priceMode` records which of
// Discount%/Price the user is directly driving:
//  - 'discount' (or unset, the default) — Case 1: List Price/ct + Discount%
//    are the inputs, Price/ct is derived: price = list × (1 + discount/100).
//  - 'price' — Case 2/3: Price/ct is the input. If a List Price/ct is also
//    present, Discount% is back-derived: discount = (list-price)/list × 100
//    (Case 2). If there's no List Price/ct (e.g. a Lab=None row), Discount%
//    is simply left alone — nothing to compute against (Case 3).
// A no-op for loose rows.
function resolveCertPrice(it: BatchItem): BatchItem {
  if (it.itemType !== 'certified') return it;
  const listPerCt = num(it.listPriceAmount);
  if (it.priceMode === 'price') {
    const price = num(it.pricePerCt);
    if (listPerCt <= 0) return it;
    // Inverse of price = list × (1 + discount/100), so the sign convention
    // matches Case 1 exactly: negative = discount (price below list),
    // positive = premium (price above list).
    const discountPct = +(((price - listPerCt) / listPerCt) * 100).toFixed(4);
    return { ...it, priceListPercent: String(discountPct) };
  }
  const discountPct = num(it.priceListPercent);
  const price = listPerCt * (1 + discountPct / 100);
  return { ...it, pricePerCt: listPerCt > 0 ? String(+price.toFixed(4)) : it.pricePerCt };
}

function calcBatchCert(it: BatchItem) {
  const listPerCt = num(it.listPriceAmount), ct = num(it.carat);
  const priceCt = num(it.pricePerCt);   // kept in sync with List/Discount by resolveCertPrice on every edit
  const avPct = num(it.addedValuePct);
  // Indian Less 1 / Less 2 (empty for Belgian, so this is just priceCt).
  const amount  = lineNet(priceCt, num(it.less1Percent), num(it.less2Percent)) * ct;
  const costAmt = amount * (1 + avPct / 100);
  // rapPerCt (saved as rapPricePerCt, shown as the "Rap/ct" column) is the
  // Rapaport list price before the discount — i.e. the List Price/ct input
  // itself — not priceCt, which is the post-discount negotiated Price/ct.
  // Using priceCt here was the bug: it made Rap/ct always read identical to
  // Price/ct instead of the actual list reference price.
  return { listAmt: listPerCt, rapPerCt: listPerCt, amount, costAmt, costPerCt: ct > 0 ? costAmt / ct : 0 };
}

function calcBatchLoose(it: BatchItem) {
  const ppc = num(it.pricePerCt), ct = num(it.carat);
  const avPct = num(it.addedValuePct);
  const amount  = lineNet(ppc, num(it.less1Percent), num(it.less2Percent)) * ct;
  const costAmt = amount * (1 + avPct / 100);
  return { listAmt: 0, rapPerCt: 0, amount, costAmt, costPerCt: ct > 0 ? costAmt / ct : 0 };
}

function calcRow(it: BatchItem) {
  return it.itemType === 'certified' ? calcBatchCert(it) : calcBatchLoose(it);
}

// A single line item's own certified/loose flavor — invoices can now mix both,
// but each stone's inventory record still needs its own precise type.
function lineItemPurchaseType(li: PurchaseLineItem): PurchaseType {
  return li.itemType === 'loose' ? 'polished-loose' : 'polished-certified';
}

// ── Shared: validate + build line items + create-invoice transaction ─────────
// Extracted from BatchAddModal so the CSV importer (ImportPurchaseModal)
// writes byte-identical data through the exact same code path — Lot No
// counter, item Code auto-numbering, inventory docs, SKU history and the
// GL entry all behave the same whether a purchase is typed or imported.

function validateBatch(header: BatchHeader, items: BatchItem[]): string {
  if (!header.fromEntityId) return 'Select a supplier / broker.';
  if (!header.dueDate)      return 'Set a due date.';
  // A new invoice starts genuinely empty now — this is the actual guard
  // against saving with nothing entered (the old "can't remove the last
  // row" UI guard is gone, since zero rows is a legitimate in-progress
  // state right up until save).
  if (items.length === 0) return 'Add at least one line item (Loose or Certified).';
  for (let i = 0; i < items.length; i++) {
    const it = items[i], row = i + 1;
    const isCert = it.itemType === 'certified';
    if (!it.shape)                        return `Row ${row}: Select diamond shape.`;
    if (!it.carat || num(it.carat) <= 0) return `Row ${row}: Enter carat weight.`;
    if (!it.color)                        return `Row ${row}: Select color.`;
    if (!it.clarity)                      return `Row ${row}: Select clarity.`;
    if (isCert) {
      if (!it.lab)                        return `Row ${row}: Select lab.`;
      if (it.lab !== 'None' && !it.certificateNo.trim()) return `Row ${row}: Enter certificate number.`;
      if (!it.pricePerCt || num(it.pricePerCt) <= 0) return `Row ${row}: Enter Cost/ct (or List Price/ct + Discount %).`;
    } else {
      if (!it.pricePerCt || num(it.pricePerCt) <= 0) return `Row ${row}: Enter price per carat.`;
      if (!it.sku.trim() && !isDummyPurchaseLine(it)) return `Row ${row}: Enter SKU (parcel code).`;
    }
  }
  const allCodes = items.filter(it => it.code.trim() && !isDummyPurchaseLine(it)).map(it => normalizeCode(it.code));
  const dupeCode = allCodes.find((c, i) => allCodes.indexOf(c) !== i);
  if (dupeCode) return `Two rows use the same Code (${dupeCode}). Each item needs its own unique Code.`;
  return '';
}

// Required cells of the Add/Edit grids, tinted red while empty — the same
// rules validateBatch enforces on save, shown per cell instead of one
// message at a time.
type BatchReqField = 'sku' | 'shape' | 'carat' | 'pricePerCt' | 'color' | 'clarity' | 'lab' | 'certificateNo';
const BATCH_REQUIRED: Record<RowType, BatchReqField[]> = {
  loose:     ['sku', 'shape', 'carat', 'pricePerCt', 'color', 'clarity'],
  certified: ['lab', 'certificateNo', 'shape', 'carat', 'pricePerCt', 'color', 'clarity'],
};
function batchCellMissing(it: BatchItem, f: BatchReqField): boolean {
  if (f === 'sku' && isDummyPurchaseLine(it)) return false;   // a Dummy line holds no SKU
  if (f === 'carat' || f === 'pricePerCt') return num(it[f]) <= 0;
  if (f === 'certificateNo') return it.lab !== 'None' && !it.certificateNo.trim();
  return !it[f].trim();
}
// "Started" = the user has typed something real. Code, Category and Added
// Value % arrive pre-filled on every new row, so they don't count — a fresh
// row stays white until it's actually being filled in.
function batchRowStarted(it: BatchItem): boolean {
  return [it.sku, it.description, it.shape, it.size, it.subSize, it.carat, it.pricePerCt, it.color, it.clarity,
    it.lab, it.certificateNo, it.listPriceAmount, it.priceListPercent, it.cut, it.polish, it.symmetry,
    it.fluorescence, it.measLength, it.measWidth, it.measDepth, it.terms, it.broker1EntityId, it.broker2EntityId,
  ].some(v => v.trim());
}

// ── 'books-dummy' purchase lines — paid for, but no stone arrives ──────────
// A Books & Dummy line is a real invoice line (it's on the invoice, in AP,
// in the ledger) but creates NO inventory item, Code or SKU: its carats and
// cost (amountUSD / costingAmountUSD) are added onto the shared Dummy /
// Books Adjustment balance instead (utils/dummyCode.ts) — topping it back up
// when Books Adjustment sales took it below zero, and re-averaging its
// cost/ct (value ÷ carats). Credited once per invoice under history id
// `dummypurchase_{invoiceId}`, so edit/delete apply only the difference.
const isDummyPurchaseLine = (li: { category?: TransactionCategory | '' }) => li.category === 'books-dummy';
const DUMMY_ROW_HINT = 'Books & Dummy line — no stone is added to stock; its carats and cost go onto the Dummy / Books Adjustment code.';
function dummyPurchaseAggregate(lineItems: PurchaseLineItem[]) {
  return lineItems.filter(isDummyPurchaseLine).reduce((s, li) => ({
    carat: round2(s.carat + li.carat),
    costUSD: round2(s.costUSD + li.amountUSD),
    costingUSD: round2(s.costingUSD + li.costingAmountUSD),
  }), { carat: 0, costUSD: 0, costingUSD: 0 });
}

function buildLineItems(items: BatchItem[]): PurchaseLineItem[] {
  return items.map(it => {
    const isCert = it.itemType === 'certified';
    const lineTerms = it.terms.trim() && num(it.terms) > 0 ? num(it.terms) : undefined;
    if (isCert) {
      const calc = calcBatchCert(it);
      const hasListPrice = num(it.listPriceAmount) > 0;
      return stripUndef({
        id: it._key, itemType: 'certified',
        shape: it.shape as DiamondShape, carat: num(it.carat),
        color: it.color as DiamondColor, clarity: it.clarity as DiamondClarity,
        code:             isDummyPurchaseLine(it) ? undefined : (it.code.trim() || undefined),
        description:      it.description.trim() || undefined,
        lab:              it.lab as DiamondLab,
        certificateNo:    it.certificateNo.trim() || undefined,
        rapPricePerCt:    calc.rapPerCt,
        priceListPercent: hasListPrice ? num(it.priceListPercent) : undefined,
        priceListAmount:  hasListPrice ? calc.listAmt : undefined,
        priceMode:        it.priceMode,
        cut:              it.cut          || undefined,
        polish:           it.polish       || undefined,
        symmetry:         it.symmetry     || undefined,
        fluorescence:     it.fluorescence || undefined,
        measLength:       it.measLength.trim() || undefined,
        measWidth:        it.measWidth.trim()  || undefined,
        measDepth:        it.measDepth.trim()  || undefined,
        terms:            lineTerms,
        broker1EntityId:          it.broker1EntityId || undefined,
        broker1CommissionPercent: it.broker1EntityId && it.broker1CommissionPercent ? num(it.broker1CommissionPercent) : undefined,
        broker2EntityId:          it.broker2EntityId || undefined,
        broker2CommissionPercent: it.broker2EntityId && it.broker2CommissionPercent ? num(it.broker2CommissionPercent) : undefined,
        amountUSD:        calc.amount,
        less1Percent:     num(it.less1Percent) > 0 ? num(it.less1Percent) : undefined,
        less2Percent:     num(it.less2Percent) > 0 ? num(it.less2Percent) : undefined,
        grossPricePerCt:  num(it.less1Percent) > 0 || num(it.less2Percent) > 0 ? num(it.pricePerCt) : undefined,
        addedValuePercent: num(it.addedValuePct),
        costingPercent:   0,
        costingAmountUSD: calc.costAmt,
        costPerCt:        calc.costPerCt,
        category:         it.category || undefined,
      }) as PurchaseLineItem;
    } else {
      const calc = calcBatchLoose(it);
      return stripUndef({
        id: it._key, itemType: 'loose',
        shape: it.shape as DiamondShape, carat: num(it.carat),
        color: it.color as DiamondColor, clarity: it.clarity as DiamondClarity,
        size:    it.size.trim()    || undefined,
        subSize: it.subSize.trim() || undefined,
        // A Dummy line never becomes a stone, so it holds no SKU or Code.
        sku:     isDummyPurchaseLine(it) ? undefined : (it.sku.trim()  || undefined),
        code:    isDummyPurchaseLine(it) ? undefined : (it.code.trim() || undefined),
        description: it.description.trim() || undefined,
        terms:   lineTerms,
        broker1EntityId:          it.broker1EntityId || undefined,
        broker1CommissionPercent: it.broker1EntityId && it.broker1CommissionPercent ? num(it.broker1CommissionPercent) : undefined,
        broker2EntityId:          it.broker2EntityId || undefined,
        broker2CommissionPercent: it.broker2EntityId && it.broker2CommissionPercent ? num(it.broker2CommissionPercent) : undefined,
        amountUSD:        calc.amount,
        less1Percent:     num(it.less1Percent) > 0 ? num(it.less1Percent) : undefined,
        less2Percent:     num(it.less2Percent) > 0 ? num(it.less2Percent) : undefined,
        grossPricePerCt:  num(it.less1Percent) > 0 || num(it.less2Percent) > 0 ? num(it.pricePerCt) : undefined,
        addedValuePercent: num(it.addedValuePct),
        costingPercent:   0,
        costingAmountUSD: calc.costAmt,
        costPerCt:        calc.costPerCt,
        category:         it.category || undefined,
      }) as PurchaseLineItem;
    }
  });
}

// The "create a brand-new purchase invoice" transaction — verbatim the
// non-edit branch that used to live inline in BatchAddModal.handleSave.
// (The edit branch stays in the modal; it reconciles against existing
// inventory docs and isn't reused anywhere.)
async function createPurchaseInvoice(opts: {
  db:       Firestore;
  business: { id: string };
  user:     { id: string; name?: string };
  settings: BusinessSettings | null;
  header:   BatchHeader;
  items:    BatchItem[];
  addlExpenses?: AddlExpenseRow[];
  /** Indian trading system: GST % for this invoice (on the stones' final total). */
  gst?: GstPercents;
}): Promise<{ invoiceId: string; invoiceNo: string }> {
  const { db, business, user, settings, header, items, addlExpenses = [] } = opts;

  const lineItems       = buildLineItems(items);
  const itemCount       = lineItems.length;
  const totalCarat      = lineItems.reduce((s, li) => s + li.carat, 0);
  // Stones' final total; Indian GST goes on top of it (what we owe the supplier).
  const goodsUSD        = lineItems.reduce((s, li) => s + li.amountUSD, 0);
  const tax             = isIndian(settings) && opts.gst ? invoiceGst(round2(goodsUSD), opts.gst) : NO_GST;
  const totalAmountUSD  = tax.total > 0 ? round2(goodsUSD + tax.total) : goodsUSD;
  const totalCostingUSD = lineItems.reduce((s, li) => s + li.costingAmountUSD, 0);

  // 'separate' mode: group line items by their own computed due date; the
  // invoice's own dueDate becomes the earliest group's date. 'average'
  // mode (default): today's exact behavior, no dueGroups written at all.
  const dueGroups = spreadGstOverGroups(header.dueDateMode === 'separate'
    ? computeDueGroups(lineItems, header.purchaseDate, header.terms, li => li.terms, li => li.amountUSD)
    : undefined, tax.total, goodsUSD);
  const effectiveDueDate = dueGroups && dueGroups.length > 0 ? dueGroups[0].dueDate : header.dueDate;

  const hasCert  = items.some(it => it.itemType === 'certified');
  const hasLoose = items.some(it => it.itemType === 'loose');
  const invoiceType: PurchaseType = hasCert && hasLoose ? 'polished-mixed' : hasCert ? 'polished-certified' : 'polished-loose';

  const brokerCommissionPercent = header.brokerEntityId ? parseFloat(header.brokerCommissionPercent) || 0 : undefined;
  const brokerCommissionUSD     = header.brokerEntityId ? round2(goodsUSD * (brokerCommissionPercent ?? 0) / 100) : undefined;

  const prefix       = hasCert && hasLoose ? 'PM' : hasCert ? (settings?.certifiedPrefix ?? 'PC') : (settings?.loosePrefix ?? 'PL');
  const counterField = hasCert && hasLoose ? 'polishedMixed' : hasCert ? 'polishedCertified' : 'polishedLoose';
  const counterRef  = doc(db, 'counters', business.id);
  const invRef      = doc(collection(db, 'invoices'));
  const invItemRefs = lineItems.map(() => doc(collection(db, 'inventory')));

  const skuIds  = [...new Set(lineItems.filter(li => li.itemType === 'loose' && li.sku).map(li => slugifySku(li.sku!)))];
  const skuRefs = new Map(skuIds.map(id => [id, doc(db, 'skus', id)]));

  const codeIds  = [...new Set(lineItems.filter(li => li.code).map(li => normalizeCode(li.code!)))];
  const codeRefs = new Map(codeIds.map(id => [id, doc(db, 'itemCodes', id)]));

  // ── Additional Details (Purchase Expenses) — same shape PurchaseExpenses.tsx
  // itself writes, just created alongside the invoice in this one transaction.
  const peRefs = addlExpenses.map(() => doc(collection(db, 'purchaseExpenses')));
  const peTxRefs = addlExpenses.map(row => row.payNow ? doc(collection(db, 'bankTransactions')) : null);
  const peAccRefs = new Map<string, DocumentReference>();
  addlExpenses.forEach(row => {
    if (row.payNow && row.bankAccountId && !peAccRefs.has(row.bankAccountId)) {
      peAccRefs.set(row.bankAccountId, doc(db, 'bankAccounts', row.bankAccountId));
    }
  });

  let createdInvoiceNo = '';

  await runTransaction(db, async tx => {
    const snap  = await tx.get(counterRef);
    const count = snap.exists() ? (snap.data()[counterField] ?? 0) : 0;
    const peStart = snap.exists() ? (snap.data().purchaseExpense ?? 0) : 0;

    const peAccSnaps = new Map<string, BankAccount>();
    for (const [id, ref] of peAccRefs) {
      const s = await tx.get(ref);
      if (!s.exists()) throw new Error('A selected bank account no longer exists.');
      peAccSnaps.set(id, s.data() as BankAccount);
    }

    const skuSnaps = new Map<string, { exists: boolean; avg: number }>();
    for (const [id, ref] of skuRefs) {
      const s = await tx.get(ref);
      skuSnaps.set(id, { exists: s.exists(), avg: s.exists() ? ((s.data() as Sku).currentCostingPerCt ?? 0) : 0 });
    }
    for (const [id, ref] of codeRefs) {
      const s = await tx.get(ref);
      if (s.exists()) {
        const owner = lineItems.find(li => li.code && normalizeCode(li.code) === id);
        throw new Error(`Code "${owner?.code ?? id}" is already used by another item.`);
      }
    }

    const needsAutoCode = lineItems.filter(li => !li.code && !isDummyPurchaseLine(li));
    const storedCursor = snap.exists() ? (snap.data().itemCode ?? settings?.itemCodeStart ?? 0) : (settings?.itemCodeStart ?? 0);
    const cursor = { n: storedCursor };
    const reserved = new Set(codeIds);
    for (const li of needsAutoCode) {
      li.code = await nextFreeCode(tx, db, cursor, reserved);
    }
    for (const li of lineItems) {
      const n = li.code ? Number(li.code) : NaN;
      if (Number.isFinite(n) && n > cursor.n) cursor.n = n;
    }
    // Books & Dummy lines top up the shared Dummy balance (last read of the
    // read phase — may lazily create the Dummy item off the same cursor).
    const dummyPlan = await planDummyDelta(tx, db, {
      businessId: business.id, historyId: `dummypurchase_${invRef.id}`,
      aggregate: dummyPurchaseAggregate(lineItems), cursor, reserved, nextCode: nextFreeCode,
    });
    const counterUpdate: Record<string, number> = { [counterField]: count + 1 };
    if (cursor.n !== storedCursor) counterUpdate.itemCode = cursor.n;
    if (addlExpenses.length > 0) counterUpdate.purchaseExpense = peStart + addlExpenses.length;
    tx.set(counterRef, counterUpdate, { merge: true });

    const invoiceNo = fmtInvoiceNo(prefix, count + 1);
    createdInvoiceNo = invoiceNo;
    tx.set(invRef, stripUndef({
      businessId:        business.id,
      type:              invoiceType,
      invoiceNo,
      fromEntityId:      header.fromEntityId,
      supplierInvoiceNo: header.supplierInvoiceNo.trim() || undefined,
      purchaseDate:      header.purchaseDate || undefined,
      terms:             header.terms,
      dueDate:           effectiveDueDate,
      dueDateMode:       header.dueDateMode === 'separate' ? 'separate' : undefined,
      dueGroups,
      itemCount, totalCarat, totalAmountUSD, totalCostingUSD,
      ...(tax.total > 0 ? gstInvoiceFields(goodsUSD, opts.gst ?? {}, tax) : {}),
      paidUSD:           0,
      paymentStatus:     'unpaid' as PaymentStatus,
      brokerEntityId:          header.brokerEntityId || undefined,
      brokerCommissionPercent: brokerCommissionPercent,
      brokerCommissionUSD:     brokerCommissionUSD,
      brokerPaidUSD:       header.brokerEntityId ? 0 : undefined,
      brokerPaymentStatus: header.brokerEntityId ? ('unpaid' as PaymentStatus) : undefined,
      lineItems,
      notes:             header.notes.trim() || undefined,
      createdAt:         serverTimestamp(),
      updatedAt:         serverTimestamp(),
      ...createdStamp(user),
    } as Record<string, unknown>));

    logActivity(tx, db, business.id, user, {
      module: 'purchase', action: 'create', recordId: invRef.id, recordNo: invoiceNo,
      partyId: header.fromEntityId || undefined, itemCount, carat: totalCarat, amountUSD: totalAmountUSD,
    });

    applyDummyDelta(tx, db, dummyPlan, {
      type: 'purchase', date: header.purchaseDate || todayISO(), entityId: header.fromEntityId,
      category: 'books-dummy', notes: `Dummy purchase — Lot ${invoiceNo}`, createdBy: user.id,
      extra: { purchaseInvoiceId: invRef.id, purchaseInvoiceNo: invoiceNo },
    });

    lineItems.forEach((li, i) => {
      if (isDummyPurchaseLine(li)) return;   // no stone — credited to the Dummy balance above
      tx.set(invItemRefs[i], stripUndef({
        businessId: business.id,
        purchaseId: invRef.id,
        purchaseNo: invoiceNo,
        lineItemId: li.id,
        type:       lineItemPurchaseType(li),
        status:     'in-stock' as InventoryStatus,
        shape: li.shape, carat: li.carat, color: li.color, clarity: li.clarity,
        lab: li.lab, certificateNo: li.certificateNo,
        cut: li.cut, polish: li.polish, symmetry: li.symmetry, fluorescence: li.fluorescence,
        measLength: li.measLength, measWidth: li.measWidth, measDepth: li.measDepth,
        description: li.description || null,
        size: li.size, subSize: li.subSize,
        sku:   li.itemType === 'loose' ? (li.sku || undefined) : undefined,
        skuId: li.itemType === 'loose' && li.sku ? slugifySku(li.sku) : undefined,
        code:   li.code || undefined,
        codeId: li.code ? normalizeCode(li.code) : undefined,
        category: li.category,
        costUSD: li.amountUSD, costingUSD: li.costingAmountUSD,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      } as Record<string, unknown>));

      if (li.code) {
        const codeId = normalizeCode(li.code);
        tx.set(doc(db, 'itemCodes', codeId), stripUndef({
          businessId: business.id, code: li.code, inventoryItemId: invItemRefs[i].id,
          createdAt: serverTimestamp(), createdBy: user.id,
        }));
        // Inventory Activity's 'purchase' entry — the item's arrival, so
        // the Code's history ledger reads "purchased → sold/memoed/..."
        // as one continuous timeline from day one, same as every other
        // event this ledger already tracks.
        tx.set(doc(collection(db, 'itemCodes', codeId, 'history')), stripUndef({
          type: 'purchase' as CodeEventType, date: header.purchaseDate || todayISO(),
          carat: li.carat, entityId: header.fromEntityId,
          pricePerCt: li.carat > 0 ? li.amountUSD / li.carat : 0,
          amountUSD: li.amountUSD, inventoryItemId: invItemRefs[i].id,
          category: li.category,
          notes: `Lot ${invoiceNo}`,
          purchaseInvoiceId: invRef.id, purchaseInvoiceNo: invoiceNo,
          createdAt: serverTimestamp(), createdBy: user.id,
        }));
      }

      if (li.itemType !== 'loose' || !li.sku) return;
      const skuId  = slugifySku(li.sku);
      const skuRef = skuRefs.get(skuId)!;
      const info   = skuSnaps.get(skuId)!;
      if (!info.exists) {
        tx.set(skuRef, stripUndef({
          businessId: business.id, sku: li.sku,
          shape: li.shape, color: li.color, clarity: li.clarity, size: li.size, subSize: li.subSize,
          currentAvgCostPerCt: 0, currentAddedValuePercent: 0, currentCostingPerCt: 0,
          lastActivityAt: todayISO(), createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        }));
      } else {
        tx.update(skuRef, { lastActivityAt: todayISO(), updatedAt: serverTimestamp() });
      }
      const unitCostPerCt = li.carat > 0 ? li.amountUSD / li.carat : 0;
      tx.set(doc(db, 'skus', skuId, 'history', li.id), stripUndef({
        type: 'purchase', date: header.purchaseDate || todayISO(),
        supplierEntityId: header.fromEntityId,
        purchaseId: invRef.id, purchaseNo: invoiceNo, lineItemId: li.id,
        quantityCarat: li.carat, unitCostPerCt, totalCost: li.amountUSD,
        inventoryItemId: invItemRefs[i].id,
        previousAvgCostPerCt: info.avg, newAvgCostPerCt: info.avg,
        createdAt: serverTimestamp(), createdBy: user.id,
      }));
    });

    if (totalAmountUSD > 0.005) {
      postEntry(tx, db, buildEntry({
        businessId: business.id, sourceType: 'purchase_invoice', sourceId: invRef.id,
        sourceCollection: 'invoices', date: header.purchaseDate || todayISO(),
        currency: settings?.currency ?? 'USD', createdBy: user.id,
        note: `Purchase ${invoiceNo}`,
        lines: linesPurchaseInvoice(totalAmountUSD, header.fromEntityId, tax.total),
      }), entryId('purchase_invoice', invRef.id));
    }

    // Broker commission accrues as its own entry — Dr Broker Commission
    // Expense / Cr Broker Commission Payable — settled independently of the
    // supplier's own payment (mirrors the Sales-side broker accrual).
    if (header.brokerEntityId && (brokerCommissionUSD ?? 0) > 0.005) {
      postEntry(tx, db, buildEntry({
        businessId: business.id, sourceType: 'broker_accrual', sourceId: invRef.id,
        sourceCollection: 'invoices', date: header.purchaseDate || todayISO(),
        currency: settings?.currency ?? 'USD', createdBy: user.id,
        note: `Broker commission — ${invoiceNo}`,
        lines: linesBrokerAccrual(brokerCommissionUSD!, header.brokerEntityId),
      }), entryId('broker_accrual', invRef.id));
    }

    // Per-line Broker1/Broker2 commission — one accrual entry per distinct
    // broker found across the line items, alongside (not instead of) the
    // header broker entry above. See computeLineBrokerCommissions.
    for (const [brokerEntityId, commissionUSD] of computeLineBrokerCommissions(lineItems)) {
      if (commissionUSD <= 0.005) continue;
      postEntry(tx, db, buildEntry({
        businessId: business.id, sourceType: 'broker_accrual', sourceId: invRef.id,
        sourceCollection: 'invoices', date: header.purchaseDate || todayISO(),
        currency: settings?.currency ?? 'USD', createdBy: user.id,
        note: `Broker commission (per-line) — ${invoiceNo}`,
        lines: linesBrokerAccrual(commissionUSD, brokerEntityId),
      }), lineBrokerAccrualId(invRef.id, brokerEntityId));
    }

    // Additional Details — each row becomes a real purchaseExpenses doc,
    // linked to the invoice being created here, exactly like adding one
    // from the standalone Purchase Expenses page would (same numbering,
    // same paid/unpaid + bank-transaction + GL shape) — just in one save.
    const peBalanceDelta = new Map<string, number>();
    addlExpenses.forEach((row, i) => {
      const amt = round2(parseFloat(row.amount) || 0);
      if (!(amt > 0)) return;
      const n = peStart + i + 1;
      const expenseNo = `${(settings?.purchaseExpensePrefix || 'PE').toUpperCase()}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`;
      const peRef = peRefs[i];
      const txRef = peTxRefs[i];
      const acct  = row.payNow ? peAccSnaps.get(row.bankAccountId) : undefined;
      const currency = acct?.currency ?? settings?.currency ?? 'USD';
      const date = row.date || header.purchaseDate || todayISO();

      tx.set(peRef, stripUndef({
        businessId: business.id, expenseNo, category: row.category, description: row.description.trim(),
        amountUSD: amt, currency, date, treatment: 'expense' as PurchaseExpenseTreatment,
        invoiceId: invRef.id, invoiceNo,
        vendorEntityId: row.vendorEntityId || undefined,
        paymentStatus: row.payNow ? 'paid' : 'unpaid', paidUSD: row.payNow ? amt : 0,
        bankAccountId: row.payNow ? row.bankAccountId : undefined,
        bankAccountName: row.payNow ? acct?.name : undefined,
        paidDate: row.payNow ? date : undefined,
        bankTransactionId: row.payNow ? txRef!.id : undefined,
        dueDate: row.payNow ? undefined : (row.dueDate || undefined),
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: user.id,
      }));

      if (row.payNow && txRef && acct) {
        tx.set(txRef, stripUndef({
          businessId: business.id, accountId: row.bankAccountId, accountName: acct.name,
          currency: acct.currency, direction: 'out', amount: amt, date,
          description: row.description.trim(), referenceType: 'purchase_expense',
          referenceId: peRef.id, category: row.category,
          createdAt: new Date().toISOString(), createdBy: user.id,
        }));
        peBalanceDelta.set(row.bankAccountId, (peBalanceDelta.get(row.bankAccountId) ?? 0) + amt);
      }

      postEntry(tx, db, buildEntry({
        businessId: business.id, sourceType: 'purchase_expense', sourceId: peRef.id,
        sourceCollection: 'purchaseExpenses', date, currency, createdBy: user.id,
        note: `${expenseNo} — ${row.description.trim()}`,
        lines: linesPurchaseExpense(amt, 'expense', row.payNow ? { bankAccountId: row.bankAccountId } : 'accrued', row.vendorEntityId || undefined),
      }), entryId('purchase_expense', peRef.id));
    });
    // One update per account, not one per row — a transaction can't modify
    // the same doc twice, and two rows can share an account.
    peBalanceDelta.forEach((delta, accId) => {
      const ref  = peAccRefs.get(accId)!;
      const cur  = peAccSnaps.get(accId)!;
      tx.update(ref, { currentBalance: cur.currentBalance - delta });
    });
  });

  return { invoiceId: invRef.id, invoiceNo: createdInvoiceNo };
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconEdit() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>;
}
function IconTrash() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M2 3.5h10M5.5 3.5V2.5h3v1M4 3.5l.667 8h4.666L10 3.5"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconPlus() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>;
}
// ── Transaction Category — icon + picker + badge ─────────────────────────────
// Hand-drawn SVGs matching this app's own stroke-outline icon style (every
// icon in this codebase is a duplicated inline <svg>, never a third-party
// icon library) — asked for as react-icons' FaCheckCircle/AiFillStop/
// LiaFileInvoiceDollarSolid, but this app has no icon-library dependency
// anywhere and explicit permission was given to substitute ("if we are
// using other icon then you can use that"), so these three stand in for
// the same green-check / red-stop / grey-invoice meaning instead.
function IconCatBooksReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M4.2 7.2l2 2 3.6-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCatCashReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3.6 3.6l6.8 6.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconCatBooksDummy() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M3.5 1.5h4.5l2.5 2.5v8a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-9.5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 1.5v2.5h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M5 8.3c.3-.5 1-.6 1.4-.3.5.3.5.9 0 1.2-.6.3-.6.9 0 1.2.4.3 1.1.2 1.4-.3M6.6 7v5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
  </svg>;
}

const CATEGORY_META: Record<TransactionCategory, { label: string; short: string; color: string; icon: React.ReactNode }> = {
  'books-real':  { label: 'By Books & Real',  short: 'By Books', color: 'var(--success)', icon: <IconCatBooksReal /> },
  'cash-real':   { label: 'Cash & Real',      short: 'Cash',     color: 'var(--danger)',  icon: <IconCatCashReal /> },
  'books-dummy': { label: 'By Books & Dummy', short: 'Dummy',    color: 'var(--text-2)',  icon: <IconCatBooksDummy /> },
};
const CATEGORY_ORDER: TransactionCategory[] = ['books-real', 'cash-real', 'books-dummy'];

function IconCalendarSm() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconChevronDownSm() {
  return <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M3 5.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCheckSm() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7.5l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
// A real dropdown (the shared Dropdown component, same as this app's Export/
// Columns menus) rather than a native <select> — a native select can't
// render a colored icon per option. Icons only, no label text anywhere —
// each icon's `title` tooltip carries the full name instead. Defaults to
// "By Books & Real" via EMPTY_ITEM.category, so most rows never need
// touching.
function CategoryPicker({ value, onChange }: { value: TransactionCategory | ''; onChange: (v: TransactionCategory | '') => void }) {
  const current = value ? CATEGORY_META[value] : null;
  return (
    <Dropdown
      panelClassName="icon-only"
      trigger={() => (
        <button type="button" title={current?.label ?? 'Select category'} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
          height: 22, padding: '0 6px', borderRadius: 5, boxSizing: 'border-box', flexShrink: 0,
          border: `1.5px solid ${current ? current.color : 'var(--border)'}`,
          background: current ? `color-mix(in srgb, ${current.color} 14%, transparent)` : 'var(--surface)',
          color: current ? current.color : 'var(--text-3)',
          cursor: 'pointer', fontFamily: 'inherit',
        }}>
          {current ? current.icon : <IconCatBooksReal />}
          <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconChevronDownSm /></span>
        </button>
      )}
    >
      {CATEGORY_ORDER.map(key => {
        const meta = CATEGORY_META[key];
        return (
          <button
            key={key} type="button" className="dropdown-item" title={meta.label}
            style={{ color: meta.color, justifyContent: 'center' }}
            onClick={() => onChange(key)}
          >
            {meta.icon}
          </button>
        );
      })}
    </Dropdown>
  );
}

// Read-only badge — the icon "at the start of the entry" in a list/table row.
// One category for the whole invoice, or 'mixed' when its lines differ (same as Sales).
const CATEGORY_RANK = ['books-real', 'cash-real', 'books-dummy', 'mixed'];
function purchaseCategoryOf(inv: PurchaseInvoice): TransactionCategory | 'mixed' | undefined {
  if (!inv.lineItems || inv.lineItems.length === 0) return undefined;
  const first = inv.lineItems[0].category ?? 'books-real';
  return inv.lineItems.every(li => (li.category ?? 'books-real') === first) ? first : 'mixed';
}
function InvoiceCategoryBadge({ category }: { category?: TransactionCategory | 'mixed' }) {
  if (category !== 'mixed') return <CategoryBadge category={category} />;
  return (
    <span title="Mixed categories across this invoice's lines" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <span style={{ color: CATEGORY_META['books-real'].color, display: 'flex' }}>{CATEGORY_META['books-real'].icon}</span>
      <span style={{ color: CATEGORY_META['cash-real'].color, display: 'flex' }}>{CATEGORY_META['cash-real'].icon}</span>
    </span>
  );
}

function CategoryBadge({ category }: { category?: TransactionCategory }) {
  if (!category) return <span style={{ opacity: 0.4 }}>—</span>;
  const meta = CATEGORY_META[category];
  return (
    <span title={meta.label} style={{ display: 'inline-flex', alignItems: 'center', color: meta.color }}>
      {meta.icon}
    </span>
  );
}

function IconPay() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1 6h12" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="4" cy="9" r="1" fill="currentColor" />
  </svg>;
}
function IconSearch() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconEmptyDiamond() {
  return <svg width="36" height="36" viewBox="0 0 16 16" fill="none">
    <polygon points="4,2 12,2 14.5,6 8,14.5 1.5,6" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 14.5V6M1.5 6h13M4 2l4 4M12 2l-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconEmptySearch() {
  return <svg width="36" height="36" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.1" />
    <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>;
}
function IconColumns() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <rect x="5.9" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <rect x="10.3" y="2" width="2.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
  </svg>;
}
function IconExport() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1v8M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconImport() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 9V1M4 5.5L7 8.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" transform="rotate(180 7 4.75)" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconEmptyCalendar() {
  return <svg width="28" height="28" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M2 6.5h12M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>;
}
function IconSortArrow({ dir }: { dir: 'asc' | 'desc' }) {
  return <svg width="9" height="9" viewBox="0 0 10 10" fill="none"
    style={{ transform: dir === 'asc' ? 'rotate(180deg)' : undefined, transition: 'transform 0.12s' }}>
    <path d="M2 3.5L5 7l3-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// ── Sortable column header ────────────────────────────────────────────────────

function SortableTh({ label, sortKey, activeKey, dir, onSort, align, minWidth, width, filter }: {
  label: string; sortKey: SortKey; activeKey: SortKey; dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void; align?: 'right' | 'center'; minWidth?: number;
  /** A fixed width (frozen and name columns): the column never resizes. */
  width?: number;
  /** Excel-style filter button for this column (ColumnFilterButton). */
  filter?: ReactNode;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      className={`th-sortable${active ? ' active' : ''}`}
      style={{ textAlign: align, minWidth: width ?? minWidth, width, maxWidth: width }}
      onClick={() => onSort(sortKey)}
    >
      <span className="th-sort-inner" style={{ justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start' }}>
        {label}
        <span className="th-sort-arrow"><IconSortArrow dir={active ? dir : 'desc'} /></span>
        {filter}
      </span>
    </th>
  );
}

// ── Shared inline table styles ────────────────────────────────────────────────

const TI: React.CSSProperties = {
  width: '100%', minWidth: 0, padding: '6px 9px', fontSize: 11,
  height: 32,
  border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
  background: 'var(--surface)', color: 'var(--text-1)',
  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};
const TH: React.CSSProperties = {
  padding: '7px 10px', fontWeight: 600, fontSize: 10.5,
  color: 'var(--text-2)', textAlign: 'left', whiteSpace: 'nowrap',
  background: 'var(--surface-2)', borderBottom: '1.5px solid var(--border)',
};
const TD: React.CSSProperties = {
  padding: '6px 8px', verticalAlign: 'middle',
  borderBottom: '1px solid var(--surface-2)',
};
// Sticky header row for the Loose Polished Excel-style grid — same technique
// as Sales.tsx's own line-items table (STH there): position:sticky on the
// <th> itself, not the <tr>.
const STH: React.CSSProperties = { ...TH, position: 'sticky', top: 0, zIndex: 2 };
// The grid's own cell wrapper — deliberately NOT the shared TD (that one's
// borderBottom/padding is tuned for the read-only invoice-preview table
// above and must stay untouched). padding:0 so an edge-to-edge `.grid-cell`
// input fills the whole bordered cell — the connected-spreadsheet look asked
// for, borders coming from the `.excel-grid-table` CSS rule, not this object.
const GTD: React.CSSProperties = { padding: 0, verticalAlign: 'middle' };
// For the handful of non-input cells (#, Category, Total Amount, Cost/ct,
// the remove button) that still need their own text padding since they
// have no edge-to-edge input of their own to supply it.
const GTD_TEXT: React.CSSProperties = { ...GTD, padding: '6px 8px', fontSize: 11 };
// Column header cells — smaller than the body text (GTD_TEXT's 11px), same
// idea as every other table header in this app reading smaller than its rows.
const GTH: React.CSSProperties = { ...STH, ...GTD_TEXT, fontSize: 9.5, fontWeight: 700 };
// Cap either grid at ~6 visible rows before it scrolls internally, keeping
// the header pinned — bump this constant rather than letting the grid grow
// the whole modal if a future request needs more visible rows (same
// convention as Sales.tsx's own line-items scroll region). Shared by both
// the Loose and Certified grids.
const EXCEL_GRID_MAX_ROWS = 6;
const EXCEL_GRID_ROW_H  = 40;
const EXCEL_GRID_HEAD_H = 34;
// The Add Row control lives inside the table itself now, as a normal last
// row (same shape as Sales.tsx's own CodeEntryRow — not sticky, it just
// shifts down with the list as rows are added). This is its height, folded
// into the grid's own fixed height below so the modal's size stays
// constant whether there are 0 rows or EXCEL_GRID_MAX_ROWS of them, instead
// of growing/shrinking as rows are added/removed.
const EXCEL_GRID_ADD_ROW_H = 36;
const EXCEL_GRID_MAX_HEIGHT = EXCEL_GRID_HEAD_H + EXCEL_GRID_ADD_ROW_H + EXCEL_GRID_MAX_ROWS * EXCEL_GRID_ROW_H;

// ── PayBadge ──────────────────────────────────────────────────────────────────

function PayBadge({ inv }: { inv: PurchaseInvoice }) {
  const status = inv.paymentStatus ?? 'unpaid';
  const paid   = inv.paidUSD ?? 0;
  const pct    = inv.totalAmountUSD > 0 ? Math.round((paid / inv.totalAmountUSD) * 100) : 0;
  if (status === 'paid')    return <span className="badge" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid var(--success)', fontSize: 11 }}>Paid</span>;
  if (status === 'partial') return <span className="badge" style={{ background: 'var(--warning-bg)', color: '#c2410c', border: '1px solid var(--warning)', fontSize: 11 }}>{pct}% paid</span>;
  return <span className="badge" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)', fontSize: 11 }}>Unpaid</span>;
}

// ── DescriptionCombobox — free text + a type-ahead list of the saved
// descriptions from Settings › Custom Options. Same widget and same
// BusinessSettings.saleLineDescriptions list Sales' (and now Memos') own
// line items use — duplicated here rather than imported, per this app's
// per-page-helper convention. Value IS the text (anything goes); the list
// is just quick picks. ────────────────────────────────────────────────
// The Shape cells' type-ahead list (Round, Princess…; free text still allowed).
const SHAPE_OPTIONS: string[] = [...SHAPES];
// Same for the Certified grid's Lab (GIA, IGI, HRD… and None).
const LAB_OPTIONS: string[] = [...LABS];
// And the Certified grid's grading cells (standard scales; free text still allowed).
const CERT_COLOR_OPTIONS: string[]   = [...COLORS];
const CERT_CLARITY_OPTIONS: string[] = [...CLARITIES];
const GRADE_OPTIONS: string[]        = [...CUT_GRADES];
const FLUOR_OPTIONS: string[]        = [...FLUORESCENCE_GRADES];

function DescriptionCombobox({ value, onChange, options, cellRef, onKeyDown, className }: {
  value: string; onChange: (v: string) => void; options: string[];
  cellRef?: (el: HTMLInputElement | null) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** When given, replaces the default TI-styled look entirely — e.g. the
   *  Loose grid's flattened, edge-to-edge `grid-cell` class. */
  className?: string;
}) {
  return (
    <OptionCombobox
      value={value} onChange={onChange} options={options}
      className={className} style={className ? undefined : { ...TI, width: '100%' }}
      cellRef={cellRef} onKeyDown={onKeyDown} panelMinWidth={220}
      placeholder={options.length > 0 ? 'Add a note / pick…' : 'Add a note…'}
    />
  );
}


// ── Terms selector ────────────────────────────────────────────────────────────

function TermsSelector({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <input type="number" min={1} value={value}
        onChange={e => onChange(parseInt(e.target.value) || 1)}
        className="terms-input" style={{ width: 58, textAlign: 'center', fontWeight: 700 }} />
      <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 500 }}>days</span>
    </div>
  );
}

// ── Line items sub-table (read-only expanded row) ─────────────────────────────

function resolveItemType(li: PurchaseLineItem, invoiceType: string): RowType {
  if (li.itemType) return li.itemType;
  return invoiceType === 'polished-certified' ? 'certified' : 'loose';
}

function LineItemsSubTable({ items, invoiceType }: { items: PurchaseLineItem[]; invoiceType: string }) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  if (!items || items.length === 0) {
    return <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-3)' }}>No line items.</div>;
  }

  const itemTypes = items.map(li => resolveItemType(li, invoiceType));
  const allCert   = itemTypes.every(t => t === 'certified');
  const allLoose  = itemTypes.every(t => t === 'loose');

  const totalCarat  = items.reduce((s, li) => s + li.carat, 0);
  const totalAmount = items.reduce((s, li) => s + li.amountUSD, 0);
  const avgPricePerCt = totalCarat > 0 ? totalAmount / totalCarat : 0;
  // Indian purchase: the agreed price, Less 1 and Less 2 before Cost/ct (net).
  const hasLess = items.some(li => (li.less1Percent ?? 0) > 0 || (li.less2Percent ?? 0) > 0);
  const pctCell = (v?: number) => (v ?? 0) > 0 ? `${v}%` : <span style={{ opacity: 0.4 }}>—</span>;

  return (
    <div className="line-sub-scroll" style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>
      {/* Scrolls inside its own box (heads and Total pinned), not the whole popup. */}
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 26, textAlign: 'center' }} title="Transaction category">Cat.</th>
            <th style={{ ...TH, width: 28, textAlign: 'center' }}>#</th>
            <th style={TH}>Code</th>
            <th style={TH}>Description</th>
            {!allLoose  && <th style={TH}>Lab</th>}
            <th style={TH}>Shape</th>
            {!allCert   && <th style={TH}>SKU</th>}
            <th style={TH}>Ct</th>
            <th style={TH}>Color</th>
            <th style={TH}>Clarity</th>
            {!allLoose  && <th style={TH}>Cert No</th>}
            {!allLoose  && <th style={TH}>Rap/ct</th>}
            {!allLoose  && <th style={TH}>List%</th>}
            {!allCert   && <th style={TH}>Size</th>}
            {!allCert   && <th style={TH}>Sub</th>}
            {hasLess && <>
              <th style={{ ...TH, textAlign: 'right' }} title="Agreed price per ct, before Less 1 % and Less 2 %">Agreed/ct</th>
              <th style={{ ...TH, textAlign: 'right' }}>Less 1</th>
              <th style={{ ...TH, textAlign: 'right' }}>Less 2</th>
            </>}
            <th style={{ ...TH, textAlign: 'right' }} title={hasLess ? 'What we paid per ct, after Less 1 % and Less 2 %' : 'What we paid per ct'}>Cost/ct</th>
            <th style={{ ...TH, textAlign: 'right' }}>Amount</th>
            <th style={{ ...TH, textAlign: 'right' }} title="Cost/ct plus Added Value %">Price/ct</th>
          </tr>
        </thead>
        <tbody>
          {items.map((li, i) => {
            const liType   = itemTypes[i];
            const isCert   = liType === 'certified';
            const rowBg    = i % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)';
            return (
              <tr key={li.id || i} style={{ background: rowBg }}>
                <td style={{ ...TD, textAlign: 'center' }}><CategoryBadge category={li.category} /></td>
                <td style={{ ...TD, textAlign: 'center', color: 'var(--text-3)' }}>{i + 1}</td>

                <td style={TD}>{li.code ? <LotPill>{li.code}</LotPill> : <span style={{ opacity: 0.4 }}>—</span>}</td>

                <td style={{ ...TD, fontSize: 11, whiteSpace: 'nowrap' }}>{li.description || <span style={{ opacity: 0.4 }}>—</span>}</td>

                {!allLoose && (isCert
                  ? <td style={TD}><span className="badge badge-primary" style={{ fontSize: 10 }}>{li.lab || '—'}</span></td>
                  : <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                )}

                <td style={TD}>{li.shape}</td>
                {!allCert && (isCert
                  ? <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                  : <td style={TD}>{li.sku ? <LotPill>{li.sku}</LotPill> : <span style={{ opacity: 0.4 }}>—</span>}</td>
                )}
                <td style={{ ...TD, fontFamily: 'var(--font-num)' }}>{li.carat.toFixed(2)}</td>
                <td style={TD}>{li.color}</td>
                <td style={TD}>{li.clarity}</td>

                {!allLoose && (isCert
                  ? <td style={{ ...TD, fontFamily: 'var(--font-num)', fontSize: 11 }}>{li.certificateNo || '—'}</td>
                  : <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                )}
                {!allLoose && (isCert
                  ? <td style={{ ...TD, fontFamily: 'var(--font-num)' }}>{li.rapPricePerCt != null ? money(li.rapPricePerCt) : '—'}</td>
                  : <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                )}
                {!allLoose && (isCert
                  ? <td style={{ ...TD, color: (li.priceListPercent ?? 0) < 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                      {li.priceListPercent != null ? `${li.priceListPercent > 0 ? '+' : ''}${li.priceListPercent}%` : '—'}
                    </td>
                  : <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                )}

                {!allCert && (isCert
                  ? <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                  : <td style={{ ...TD, fontSize: 11 }}>{li.size || '—'}</td>
                )}
                {!allCert && (isCert
                  ? <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                  : <td style={{ ...TD, fontSize: 11 }}>{li.subSize || '—'}</td>
                )}

                {hasLess && <>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(li.grossPricePerCt ?? (li.carat > 0 ? li.amountUSD / li.carat : 0))}</td>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{pctCell(li.less1Percent)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{pctCell(li.less2Percent)}</td>
                </>}
                <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(li.carat > 0 ? li.amountUSD / li.carat : 0)}</td>
                <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(li.amountUSD)}</td>
                <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 11 }}>{money(li.costPerCt)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: 'var(--surface)', borderTop: '2px solid var(--border)' }}>
            <td colSpan={1 + 1 + 1 + 1 + (allLoose ? 0 : 1) + 1 + (allCert ? 0 : 1)} style={{ ...TD, fontWeight: 700, fontSize: 11, color: 'var(--text-2)' }}>
              Total
            </td>
            <td style={{ ...TD, fontFamily: 'var(--font-num)', fontWeight: 700 }}>{totalCarat.toFixed(2)}</td>
            <td style={TD} />
            <td style={TD} />
            {!allLoose && <td style={TD} />}
            {!allLoose && <td style={TD} />}
            {!allLoose && <td style={TD} />}
            {!allCert  && <td style={TD} />}
            {!allCert  && <td style={TD} />}
            {hasLess && <><td style={TD} /><td style={TD} /><td style={TD} /></>}
            <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(avgPricePerCt)}</td>
            <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(totalAmount)}</td>
            <td style={TD} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Invoice Preview Modal — opened by clicking a row instead of inline expand ──

// The one identifier worth calling out from the rest — same highlighted pill
// used for Lot No everywhere else in the app (the invoice list, Inventory's
// SKU column, etc.), so it's instantly recognizable rather than blending in.
function LotPill({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}

// Compact label-over-value pair for the header's right-hand fact cluster —
// small enough that several fit on one line next to the title without a box.
function MiniFact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>
        {value}
      </div>
    </div>
  );
}

// Thin vertical rule between facts inside a FactPanel.
function VDivider() {
  return <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} />;
}

// Groups the header's fact cluster into one floating pill-shaped panel —
// without this, several label/value pairs sitting bare next to the title
// just read as scattered text with no visual relationship to each other.
// Rounded ends + a soft shadow give it its own identity instead of looking
// like just another rectangular box on the page.
function FactPanel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 999, padding: '7px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {children}
    </div>
  );
}

// Exported: Accounts Payable opens this same popup for a purchase invoice
// (without Edit / Delete), adding its payment parts as `children` and its
// own buttons as `footerExtra`, so the invoice looks the same everywhere.
export function InvoicePreviewModal({ invoice, entityName, onClose, onPay, onEdit, onDelete, children, footerExtra, payLabel = 'Pay', canEditDiscounts = false }: {
  invoice: PurchaseInvoice | null;
  /** Show Apply / Edit / Delete on the Discounts list (leave out for read only). */
  canEditDiscounts?: boolean;
  entityName: (id: string) => string;
  onClose: () => void;
  children?: ReactNode;
  footerExtra?: ReactNode;
  payLabel?: string;
  // Row Actions column moved in here (see the list table below) — same
  // "close the preview first, then trigger the same flow" convention as
  // Sales' SalePreviewModal, rather than stacking Edit/Pay on top of it.
  /** Leave out to hide the Pay button (e.g. Entity History, read only). */
  onPay?: (inv: PurchaseInvoice) => void;
  onEdit?: (inv: PurchaseInvoice) => void;
  onDelete?: (inv: PurchaseInvoice) => void;
}) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings?.currency ?? 'USD');

  // Purchase Expenses linked to this invoice — "Additional Details" is
  // where they're ENTERED (in the Add/Edit form); this is the read-only
  // view, previously not shown here at all. Fetched fresh each time the
  // modal opens for a given invoice — a one-shot read, not live, same
  // "good enough for a preview" reasoning as the rest of this modal.
  const [linkedExpenses, setLinkedExpenses] = useState<PurchaseExpense[]>([]);
  useEffect(() => {
    if (!invoice) { setLinkedExpenses([]); return; }
    const db = getClientDb();
    if (!db) return;
    let cancelled = false;
    getDocs(query(collection(db, 'purchaseExpenses'), where('invoiceId', '==', invoice.id)))
      .then(snap => { if (!cancelled) setLinkedExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseExpense))); })
      .catch(() => { if (!cancelled) setLinkedExpenses([]); });
    return () => { cancelled = true; };
  }, [invoice?.id]);

  if (!invoice) return null;
  const isPaid = invoice.paymentStatus === 'paid';
  const dd = dueDateDisplay(invoice.dueDate);
  const purchaseDateLabel = invoice.purchaseDate
    ? new Date(invoice.purchaseDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  // Broker Commission — the legacy header-level broker (if set) plus every
  // per-line Broker1/Broker2 broker found across the line items, one row
  // each. A line-level broker's own % is shown only when every line it's
  // on uses the same rate — a dash otherwise, same as the Brokers page's
  // own CommissionDetailModal for a per-line deal.
  const brokerRows: { brokerEntityId: string; percent: number | null; amountUSD: number }[] = [];
  if (invoice.brokerEntityId && (invoice.brokerCommissionUSD ?? 0) > 0.005) {
    brokerRows.push({
      brokerEntityId: invoice.brokerEntityId,
      percent: invoice.brokerCommissionPercent ?? null,
      amountUSD: invoice.brokerCommissionUSD ?? 0,
    });
  }
  const linePercents = new Map<string, Set<number>>();
  for (const li of invoice.lineItems ?? []) {
    if (li.broker1EntityId && li.broker1CommissionPercent) {
      const s = linePercents.get(li.broker1EntityId) ?? new Set<number>();
      s.add(li.broker1CommissionPercent);
      linePercents.set(li.broker1EntityId, s);
    }
    if (li.broker2EntityId && li.broker2CommissionPercent) {
      const s = linePercents.get(li.broker2EntityId) ?? new Set<number>();
      s.add(li.broker2CommissionPercent);
      linePercents.set(li.broker2EntityId, s);
    }
  }
  for (const [brokerEntityId, amountUSD] of computeLineBrokerCommissions(invoice.lineItems ?? [])) {
    const percents = linePercents.get(brokerEntityId);
    brokerRows.push({
      brokerEntityId,
      percent: percents && percents.size === 1 ? [...percents][0] : null,
      amountUSD,
    });
  }
  const brokerGrandTotal = brokerRows.reduce((s, r) => s + r.amountUSD, 0);

  return (
    <Modal
      open={!!invoice}
      onClose={onClose}
      hideHead
      size="full"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {!isPaid && onDelete && (
              <Button variant="danger" icon={<IconTrash />} onClick={() => { onClose(); onDelete(invoice); }}>
                Delete Invoice
              </Button>
            )}
            <AuthorLine record={invoice} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {footerExtra}
            {!isPaid && (
              <>
                {onEdit && (
                  <Button variant="secondary" icon={<IconEdit />} onClick={() => { onClose(); onEdit(invoice); }}>
                    Edit
                  </Button>
                )}
                {onPay && (
                  <Button variant="primary" icon={<IconPay />} onClick={() => { onClose(); onPay(invoice); }}>
                    {payLabel}
                  </Button>
                )}
              </>
            )}
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={entityName(invoice.fromEntityId)}
        eyebrow="Purchase Invoice"
        right={
          <FactPanel>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                Lot No
              </div>
              <LotPill>{lotNoDisplay(invoice.invoiceNo)}</LotPill>
            </div>
            <VDivider />
            <MiniFact label="Supplier Invoice" value={invoice.supplierInvoiceNo || '—'} />
            <VDivider />
            <MiniFact label="Date" value={purchaseDateLabel} />
            <VDivider />
            <PayBadge inv={invoice} />
          </FactPanel>
        }
        onClose={onClose}
      />
      {!isPaid && dd.isOverdue && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
          background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)',
          borderRadius: 'var(--r-sm)', padding: '6px 12px', fontSize: 12, fontWeight: 700,
        }}>
          {dd.badge}
        </div>
      )}
      {invoice.notes && (
        <p className="form-hint" style={{ marginBottom: 12 }}>Notes: {invoice.notes}</p>
      )}
      {invoice.dueDateMode === 'separate' && invoice.dueGroups && invoice.dueGroups.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div className="form-section-label" style={{ marginBottom: 8 }}>Due Dates</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th style={{ textAlign: 'right' }}>Paid</th>
                  <th style={{ textAlign: 'right' }}>Outstanding</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoice.dueGroups.map(g => (
                  <tr key={g.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{dueDateDisplay(g.dueDate).label}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(g.amountUSD)}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--success)', fontFamily: 'var(--font-num)' }}>{g.paidUSD > 0 ? money(g.paidUSD) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: g.paymentStatus === 'paid' ? 'var(--text-3)' : 'var(--danger)', fontFamily: 'var(--font-num)' }}>
                      {money(Math.max(0, g.amountUSD - g.paidUSD - g.creditedUSD))}
                    </td>
                    <td><PayBadge inv={{ paymentStatus: g.paymentStatus, paidUSD: g.paidUSD, totalAmountUSD: g.amountUSD } as PurchaseInvoice} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <LineItemsSubTable items={invoice.lineItems ?? []} invoiceType={invoice.type} />
      <GstSummary inv={invoice} money={money} />

      {/* ── Additional Details (left) + Broker Commission (right), side by
          side in one row — both are secondary/reference info, so neither
          gets a full-width table; a few compact lines each, no header row
          to scan past. ── */}
      {(linkedExpenses.length > 0 || brokerRows.length > 0) && (
        <div style={{ marginTop: 16, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {linkedExpenses.length > 0 && (
            <div style={{ flex: '0 1 28%', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="form-section-label" style={{ marginBottom: 2 }}>Additional Details</div>
              {linkedExpenses.map((pe, i) => (
                <div
                  key={pe.id}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 2,
                    paddingBottom: i < linkedExpenses.length - 1 ? 7 : 0,
                    borderBottom: i < linkedExpenses.length - 1 ? '1px solid var(--border)' : 'none',
                  }}
                >
                  {[
                    ['Vendor', pe.vendorEntityId ? entityName(pe.vendorEntityId) : <span style={{ opacity: 0.4 }}>—</span>],
                    ['Date', pe.date ? new Date(pe.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'],
                    ...(pe.category ? [['Category', pe.category]] as const : []),
                    ['Status', (
                      <span key="st" className="badge" style={{
                        fontSize: 9, fontWeight: 700,
                        background: pe.paymentStatus === 'paid' ? 'var(--success-bg)' : 'var(--danger-bg)',
                        color: pe.paymentStatus === 'paid' ? 'var(--success)' : 'var(--danger)',
                      }}>{pe.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}</span>
                    )],
                    ['Amount', <span key="amt" style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}>{money(pe.amountUSD)}</span>],
                  ].map(([label, value]) => (
                    <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 11.5 }}>
                      <span style={{ color: 'var(--text-3)' }}>{label}</span>
                      <span style={{ color: 'var(--text-1)', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
                    </div>
                  ))}
                  {pe.description && (
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{pe.description}</div>
                  )}
                </div>
              ))}
            </div>
          )}
          {brokerRows.length > 0 && (
            <div style={{ flex: '0 1 28%', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div className="form-section-label" style={{ marginBottom: 2 }}>Broker Commission</div>
              {brokerRows.map(r => (
                <div key={r.brokerEntityId} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12 }}>
                  <span style={{ color: 'var(--text-2)' }}>{entityName(r.brokerEntityId)}</span>
                  <span style={{ fontFamily: 'var(--font-num)', fontWeight: 600, color: 'var(--text-1)' }}>
                    {money(r.amountUSD)}
                    {r.percent != null && <span style={{ color: 'var(--text-3)', fontWeight: 500 }}> ({r.percent}%)</span>}
                  </span>
                </div>
              ))}
              {brokerRows.length > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>Total</span>
                  <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}>{money(brokerGrandTotal)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <PurchaseDiscountsSection invoice={invoice} canEdit={canEditDiscounts} />
      {children}
    </Modal>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────

function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);

  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }

  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> invoice{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Excel import (Purchase invoices) ─────────────────────────────────────────
// One .xlsx file, Loose Polished and Certified Polished rows on two separate
// sheets (their columns differ). Every row also carries its invoice header —
// Company Name, Supplier Invoice No, Purchase Date, Payment Terms — and rows
// are grouped into invoices by Company + Supplier Invoice No + Purchase Date,
// so a whole purchase history from many suppliers/dates imports in one go,
// each invoice getting its own Lot No and a Due Date computed from its terms
// (same average-of-item-terms rule as the form). If no row fills any invoice
// column, the file is treated as ONE invoice whose header is filled in the
// regular Add Purchase Invoice form instead (the original flow).
//
// The sample file, the parser and the import preview grid are all driven by
// one column spec per sheet (IMPORT_LOOSE_COLS / IMPORT_CERT_COLS), in the
// same left-to-right order as the form's own grids, so none of them can
// drift apart. Columns map by header NAME, so a user can reorder or delete
// optional columns freely. Saving always goes through createPurchaseInvoice —
// identical data to a typed invoice.

type ImportField =
  | 'company' | 'supplierInvoiceNo' | 'purchaseDate' | 'invoiceTerms' | 'dueDate'
  | 'category' | 'code' | 'sku' | 'description' | 'lab' | 'certificateNo' | 'shape' | 'size' | 'subSize'
  | 'carat' | 'listPriceAmount' | 'priceListPercent' | 'pricePerCt' | 'terms' | 'addedValuePct'
  | 'broker1' | 'broker1CommissionPercent' | 'broker2' | 'broker2CommissionPercent'
  | 'color' | 'clarity' | 'cut' | 'polish' | 'symmetry' | 'fluorescence'
  | 'measLength' | 'measWidth' | 'measDepth'
  | 'less1Percent' | 'less2Percent'   // Indian trading system only
  | 'totalAmount' | 'costPerCt';   // formula columns in the sample — Total read back only as a Price/ct fallback

// Loose and certified rows use different Color/Clarity scales — same as the
// form: loose follows Settings' custom loose options, certified always the
// standard GIA scale (COLORS / CLARITIES).
type ImportList = 'company' | 'category' | 'shape' | 'looseColor' | 'looseClarity' | 'certColor' | 'certClarity' | 'lab' | 'grade' | 'fluor' | 'broker';

interface ImportCol extends XlsxCol<ImportField> {
  list?:    ImportList;
  /** Must be filled before save (Cert No is handled separately — required unless Lab = None). */
  required?: boolean;
  /** Required only when the file is split into invoices by its own invoice columns. */
  invoiceRequired?: boolean;
}

// A parsed row: a normal line item plus the invoice header it belongs to.
type ImportRow = BatchItem & {
  fromEntityId:      string;
  supplierInvoiceNo: string;
  purchaseDate:      string;   // YYYY-MM-DD
  invoiceTerms:      string;   // blank = the company's default terms (or 30)
};

const totalFormula = (r: number, col: (f: ImportField) => string) =>
  `IF(OR(${col('carat')}${r}="",${col('pricePerCt')}${r}=""),"",ROUND(${col('carat')}${r}*${col('pricePerCt')}${r},2))`;
const costFormula = (r: number, col: (f: ImportField) => string) =>
  `IF(OR(${col('carat')}${r}="",${col('pricePerCt')}${r}=""),"",ROUND(${col('pricePerCt')}${r}*(1+N(${col('addedValuePct')}${r})/100),2))`;
// Indian: Total Amount and Price/ct after Less 1 % then Less 2 %.
const netExpr = (r: number, col: (f: ImportField) => string) =>
  `${col('pricePerCt')}${r}*(1-N(${col('less1Percent')}${r})/100)*(1-N(${col('less2Percent')}${r})/100)`;
const totalFormulaIndian = (r: number, col: (f: ImportField) => string) =>
  `IF(OR(${col('carat')}${r}="",${col('pricePerCt')}${r}=""),"",ROUND(${col('carat')}${r}*${netExpr(r, col)},2))`;
const costFormulaIndian = (r: number, col: (f: ImportField) => string) =>
  `IF(OR(${col('carat')}${r}="",${col('pricePerCt')}${r}=""),"",ROUND(${netExpr(r, col)}*(1+N(${col('addedValuePct')}${r})/100),2))`;
const dueFormula = (r: number, col: (f: ImportField) => string) =>
  `IF(OR(${col('purchaseDate')}${r}="",${col('invoiceTerms')}${r}=""),"",${col('purchaseDate')}${r}+${col('invoiceTerms')}${r})`;

const IMPORT_INVOICE_COLS: ImportCol[] = [
  { header: 'Company Name',        field: 'company',           width: 24, list: 'company', invoiceRequired: true, headerArgb: XLSX_GROUP_ARGB,
    aliases: ['company', 'supplier', 'supplier name'],
    note: 'Supplier — must match a company saved in DiaWolt. Rows with the same Company + Supplier Invoice No + Purchase Date become one invoice.' },
  { header: 'Supplier Invoice No', field: 'supplierInvoiceNo', width: 18, headerArgb: XLSX_GROUP_ARGB,
    aliases: ['supplier inv no', 'supplier inv. no.', 'supplier invoice', 'invoice no'] },
  { header: 'Purchase Date',       field: 'purchaseDate',      width: 14, date: true, invoiceRequired: true, headerArgb: XLSX_GROUP_ARGB,
    aliases: ['date', 'invoice date'], note: 'Any Excel date, or typed as day/month/year (01/09/2026)' },
  { header: 'Payment Terms',       field: 'invoiceTerms',      width: 14, numeric: true, headerArgb: XLSX_GROUP_ARGB,
    aliases: ['payment terms (days)', 'invoice terms'], note: 'Days. Blank = the company\'s default terms (or 30)' },
  { header: 'Due Date',            field: 'dueDate',           width: 14, date: true, formula: dueFormula },
];

const IMPORT_LOOSE_COLS: ImportCol[] = [
  ...IMPORT_INVOICE_COLS,
  { header: 'Category',      field: 'category',      width: 18, list: 'category', note: 'Blank = By Books & Real' },
  { header: 'Code',          field: 'code',          width: 10, note: 'Optional — blank = auto-numbered' },
  { header: 'SKU',           field: 'sku',           width: 14, aliases: ['item no', 'item number'], note: 'Required', required: true },
  { header: 'Description',   field: 'description',   width: 24 },
  { header: 'Shape',         field: 'shape',         width: 12, list: 'shape', note: 'Required', required: true },
  { header: 'Size',          field: 'size',          width: 12 },
  { header: 'Sub Size',      field: 'subSize',       width: 10, aliases: ['subsize'] },
  { header: 'Carat',         field: 'carat',         width: 9,  numeric: true, note: 'Required', required: true },
  { header: 'Cost/ct',       field: 'pricePerCt',    width: 11, numeric: true, aliases: ['cost per ct', 'price/ct', 'price per ct', 'price'], note: 'Required. What we pay per ct', required: true },
  { header: 'Color',         field: 'color',         width: 9,  list: 'looseColor', note: 'Required', required: true },
  { header: 'Clarity',       field: 'clarity',       width: 9,  list: 'looseClarity', note: 'Required', required: true },
  { header: 'Item Terms (days)', field: 'terms',     width: 15, numeric: true, aliases: ['terms (days)', 'terms', 'line terms'], note: 'Optional — only if this stone has different terms than the invoice' },
  { header: 'Added Value %', field: 'addedValuePct', width: 13, numeric: true, aliases: ['added value', 'av %', 'av'], note: 'Blank = 3' },
  { header: 'Total Amount',  field: 'totalAmount',   width: 13, numeric: true, aliases: ['amount'], formula: totalFormula },
  { header: 'Price/ct',      field: 'costPerCt',     width: 11, numeric: true, aliases: ['cost/ct'], formula: costFormula },
  { header: 'Broker1',       field: 'broker1',       width: 20, list: 'broker', aliases: ['broker 1'] },
  { header: 'Broker1 %',     field: 'broker1CommissionPercent', width: 10, numeric: true, aliases: ['broker1%', 'broker 1 %'] },
  { header: 'Broker2',       field: 'broker2',       width: 20, list: 'broker', aliases: ['broker 2'] },
  { header: 'Broker2 %',     field: 'broker2CommissionPercent', width: 10, numeric: true, aliases: ['broker2%', 'broker 2 %'] },
];

const IMPORT_CERT_COLS: ImportCol[] = [
  ...IMPORT_INVOICE_COLS,
  { header: 'Category',      field: 'category',      width: 18, list: 'category', note: 'Blank = By Books & Real' },
  { header: 'Code',          field: 'code',          width: 10, note: 'Optional — blank = auto-numbered' },
  { header: 'Description',   field: 'description',   width: 24 },
  { header: 'Lab',           field: 'lab',           width: 9,  list: 'lab', note: 'Required', required: true },
  { header: 'Cert No',       field: 'certificateNo', width: 14, aliases: ['certificate no', 'cert'], note: 'Required unless Lab = None' },
  { header: 'Shape',         field: 'shape',         width: 12, list: 'shape', note: 'Required', required: true },
  { header: 'Carat',         field: 'carat',         width: 9,  numeric: true, note: 'Required', required: true },
  { header: 'List Price/ct', field: 'listPriceAmount', width: 13, numeric: true, aliases: ['list price', 'list price per ct'] },
  { header: 'Discount %',    field: 'priceListPercent', width: 11, numeric: true, aliases: ['discount', 'disc'], note: 'Negative = discount, e.g. -35' },
  { header: 'Cost/ct',       field: 'pricePerCt',    width: 11, numeric: true, aliases: ['cost per ct', 'price/ct', 'price per ct', 'price'], note: 'What we pay per ct. Or leave blank and give List Price + Discount %' },
  { header: 'Total Amount',  field: 'totalAmount',   width: 13, numeric: true, aliases: ['amount'], formula: totalFormula },
  { header: 'Item Terms (days)', field: 'terms',     width: 15, numeric: true, aliases: ['terms (days)', 'terms', 'line terms'], note: 'Optional — only if this stone has different terms than the invoice' },
  { header: 'Added Value %', field: 'addedValuePct', width: 13, numeric: true, aliases: ['added value', 'av %', 'av'], note: 'Blank = 3' },
  { header: 'Price/ct',      field: 'costPerCt',     width: 11, numeric: true, aliases: ['cost/ct'], formula: costFormula },
  { header: 'Broker1',       field: 'broker1',       width: 20, list: 'broker', aliases: ['broker 1'] },
  { header: 'Broker1 %',     field: 'broker1CommissionPercent', width: 10, numeric: true, aliases: ['broker1%', 'broker 1 %'] },
  { header: 'Broker2',       field: 'broker2',       width: 20, list: 'broker', aliases: ['broker 2'] },
  { header: 'Broker2 %',     field: 'broker2CommissionPercent', width: 10, numeric: true, aliases: ['broker2%', 'broker 2 %'] },
  { header: 'Color',         field: 'color',         width: 9,  list: 'certColor', note: 'Required', required: true },
  { header: 'Clarity',       field: 'clarity',       width: 9,  list: 'certClarity', note: 'Required', required: true },
  { header: 'Cut',           field: 'cut',           width: 11, list: 'grade' },
  { header: 'Polish',        field: 'polish',        width: 11, list: 'grade' },
  { header: 'Symmetry',      field: 'symmetry',      width: 11, list: 'grade' },
  { header: 'Fluorescence',  field: 'fluorescence',  width: 13, list: 'fluor', aliases: ['fluor'] },
  { header: 'Length',        field: 'measLength',    width: 9,  numeric: true, aliases: ['l'] },
  { header: 'Width',         field: 'measWidth',     width: 9,  numeric: true, aliases: ['w'] },
  { header: 'Depth',         field: 'measDepth',     width: 9,  numeric: true, aliases: ['d'] },
];

// Indian trading system: Less 1 % and Less 2 % right after Cost/ct, and the
// grey formula columns work out the net. Belgian files are unchanged.
const IMPORT_LESS_COLS: ImportCol[] = [
  { header: 'Less 1 %', field: 'less1Percent', width: 9, numeric: true, aliases: ['less1', 'less 1'], note: 'Discount on Cost/ct' },
  { header: 'Less 2 %', field: 'less2Percent', width: 9, numeric: true, aliases: ['less2', 'less 2'], note: 'Taken after Less 1 %' },
];
function importColsFor(cols: ImportCol[], indian: boolean): ImportCol[] {
  if (!indian) return cols;
  const out: ImportCol[] = [];
  for (const c of cols) {
    if (c.field === 'totalAmount') out.push({ ...c, formula: totalFormulaIndian });
    else if (c.field === 'costPerCt') out.push({ ...c, formula: costFormulaIndian });
    else out.push(c);
    if (c.field === 'pricePerCt') out.push(...IMPORT_LESS_COLS);
  }
  return out;
}

const IMPORT_LOOSE_SHEET = 'Loose Polished';
const IMPORT_CERT_SHEET  = 'Certified Polished';
type ImportSampleRow = XlsxSampleRow<ImportField>;

function importLists(settings: BusinessSettings | null, brokerNames: string[], companyNames: string[] = []): Record<ImportList, string[]> {
  return {
    company:  companyNames,
    category: CATEGORY_ORDER.map(c => CATEGORY_META[c].label),
    shape:    SHAPES,
    looseColor:   settings?.looseColorOptions?.length ? settings.looseColorOptions : COLORS,
    looseClarity: settings?.looseClarityOptions?.length ? settings.looseClarityOptions : CLARITIES,
    certColor:    COLORS,
    certClarity:  CLARITIES,
    lab:      LABS,
    grade:    CUT_GRADES,
    fluor:    FLUORESCENCE_GRADES,
    broker:   brokerNames,
  };
}

async function downloadPurchaseImportSample(settings: BusinessSettings | null, brokerNames: string[], companyNames: string[]) {
  const lists = importLists(settings, brokerNames, companyNames);

  // Two example invoices: the loose rows are one, the certified rows another
  // (different company / date / terms) — shows how rows group into invoices.
  const supplierA = companyNames[0] ?? 'Your Supplier Name';
  const supplierB = companyNames[1] ?? companyNames[0] ?? 'Another Supplier';
  const dateA = xlsxUtcDate(addDaysToDate(todayISO(), -14));
  const dateB = xlsxUtcDate(addDaysToDate(todayISO(), -7));
  const invA = { company: supplierA, supplierInvoiceNo: 'INV-1001', purchaseDate: dateA, invoiceTerms: 30 };
  const invB = { company: supplierB, supplierInvoiceNo: 'INV-2001', purchaseDate: dateB, invoiceTerms: 45 };
  const looseSample: ImportSampleRow[] = [
    { ...invA, category: 'By Books & Real', sku: 'PARCEL-A', description: 'Melee parcel', shape: 'Round', size: '1.00-1.20', subSize: '+2', carat: 5.2, pricePerCt: 850, color: 'H', clarity: 'SI1', addedValuePct: 3 },
    { ...invA, category: 'By Books & Real', sku: 'PARCEL-B', shape: 'Oval', size: '0.80-0.90', carat: 3.1, pricePerCt: 620, color: 'I', clarity: 'SI2', addedValuePct: 3 },
  ];
  const certSample: ImportSampleRow[] = [
    { ...invB, category: 'By Books & Real', lab: 'GIA', certificateNo: '1234567890', shape: 'Round', carat: 1.01, listPriceAmount: 12000, priceListPercent: -35, addedValuePct: 3, color: 'F', clarity: 'VS1', cut: 'Excellent', polish: 'Excellent', symmetry: 'Excellent', fluorescence: 'None', measLength: 6.45, measWidth: 6.48, measDepth: 3.99 },
    { ...invB, category: 'By Books & Real', lab: 'None', shape: 'Princess', carat: 0.9, pricePerCt: 5200, addedValuePct: 3, color: 'G', clarity: 'VS2' },
  ];

  await downloadImportWorkbook<ImportField>({
    fileName: 'purchase-invoice-import-sample.xlsx',
    lists,
    freeLists: ['company', 'broker'],
    sheets: [
      { name: IMPORT_LOOSE_SHEET, cols: importColsFor(IMPORT_LOOSE_COLS, isIndian(settings)), sample: looseSample, tabArgb: 'FF10B981' },
      { name: IMPORT_CERT_SHEET,  cols: importColsFor(IMPORT_CERT_COLS, isIndian(settings)),  sample: certSample,  tabArgb: 'FF4F46E5' },
    ],
    readme: [
      'Purchase Invoice import — many invoices in one file',
      '',
      `• Put loose (parcel) goods on the "${IMPORT_LOOSE_SHEET}" sheet and certified stones on the "${IMPORT_CERT_SHEET}" sheet. Leave a sheet empty if you have none of that kind.`,
      '• Each row says which invoice it belongs to (the teal columns): Company Name, Supplier Invoice No, Purchase Date, Payment Terms.',
      '• Rows with the same Company + Supplier Invoice No + Purchase Date become ONE purchase invoice — even across the two sheets.',
      '• Due Date = Purchase Date + Payment Terms (blank terms = that company\'s default terms, or 30). If stones have their own Item Terms, the app averages them, same as the invoice screen.',
      '• Company names must match a company already saved in DiaWolt (pick from the dropdown).',
      '• Only importing one invoice? You can leave all teal columns empty and fill the supplier and dates on the invoice screen instead.',
      '• Replace the example rows on each sheet with your own (delete them if not needed).',
      '• Row 1 headers must stay as they are. Column order can change, and optional columns can be deleted.',
      '• Grey columns (Due Date, Total Amount, Price/ct) are calculated for you. They are not imported.',
      '• Cost/ct is what you pay the supplier per ct. Price/ct is Cost/ct plus Added Value %.',
      '• Code: leave blank to auto-number, exactly like adding an invoice by hand.',
      '• Certified Cost/ct: give either Cost/ct, or List Price/ct + Discount % (negative = discount, e.g. -35).',
      '• Broker names must match a broker already saved in DiaWolt (pick from the dropdown).',
      '• You see and can fix every row in DiaWolt before anything is saved.',
    ],
  });
}

type ParsedImport = { items: ImportRow[]; loose: number; cert: number; warnings: string[]; hasInvoiceInfo: boolean };

async function parsePurchaseImportXlsx(
  data: ArrayBuffer, settings: BusinessSettings | null, entities: Entity[],
): Promise<ParsedImport> {
  const wb = await loadWorkbook(data);

  // Older entity docs can miss a name or types: never let that crash the import.
  const brokers = entities.filter(e => (e.types ?? []).includes('broker'));
  const warnings: string[] = [];
  const items: ImportRow[] = [];
  const lists = importLists(settings, []);
  let loose = 0, cert = 0, hasInvoiceInfo = false;

  const readSheet = (sheetName: string, type: RowType, cols: ImportCol[], match: (n: string) => boolean) => {
    const sheetRows = readSheetRows(wb, sheetName, match, cols, warnings);
    for (const { sheet, rowNo, text: raw, raw: rawVal } of sheetRows ?? []) {
      const dateRaw = rawVal.purchaseDate ?? null;
      const where = `${sheet} row ${rowNo}`;
      const get = (f: ImportField) => raw[f] ?? '';
      const n   = (f: ImportField) => cleanNum(get(f));
      const opt = (f: ImportField, options: readonly string[], label: string) => matchOption(get(f), options, label, where, warnings);
      const entity = (f: ImportField, pool: Entity[], label: string): string => {
        const v = get(f);
        if (!v) return '';
        const hit = pool.find(e => (e.companyName ?? '').trim().toLowerCase() === v.trim().toLowerCase());
        if (!hit) warnings.push(`${where}: ${label} "${v}" not found in DiaWolt — pick it in the preview (or add it under Entities first).`);
        return hit?.id ?? '';
      };

      const it = newBatchItem(type) as ImportRow;

      // Invoice header for this row.
      if (get('company') || get('supplierInvoiceNo') || (dateRaw != null && dateRaw !== '')) hasInvoiceInfo = true;
      it.fromEntityId      = entity('company', entities, 'Company');
      it.supplierInvoiceNo = get('supplierInvoiceNo');
      it.purchaseDate      = parseImportDate(dateRaw);
      if (dateRaw != null && dateRaw !== '' && !it.purchaseDate) warnings.push(`${where}: Purchase Date "${String(dateRaw)}" couldn't be read — set it in the preview.`);
      it.invoiceTerms      = n('invoiceTerms');

      const catRaw = get('category').toLowerCase();
      if (catRaw) {
        const cat = CATEGORY_ORDER.find(c =>
          c === catRaw || CATEGORY_META[c].label.toLowerCase() === catRaw || CATEGORY_META[c].short.toLowerCase() === catRaw);
        if (cat) it.category = cat;
        else warnings.push(`${where}: Category "${get('category')}" not recognised — set to By Books & Real.`);
      }
      it.code        = get('code');
      it.description = get('description');
      it.shape       = opt('shape', SHAPES, 'Shape');
      it.carat       = n('carat');
      const isCert   = type === 'certified';
      it.color       = opt('color',   isCert ? lists.certColor   : lists.looseColor,   'Color');
      it.clarity     = opt('clarity', isCert ? lists.certClarity : lists.looseClarity, 'Clarity');
      it.terms       = n('terms');
      if (n('addedValuePct')) it.addedValuePct = n('addedValuePct');
      it.less1Percent = n('less1Percent');
      it.less2Percent = n('less2Percent');
      it.broker1EntityId = entity('broker1', brokers, 'Broker');
      it.broker1CommissionPercent = it.broker1EntityId ? n('broker1CommissionPercent') : '';
      it.broker2EntityId = entity('broker2', brokers, 'Broker');
      it.broker2CommissionPercent = it.broker2EntityId ? n('broker2CommissionPercent') : '';

      // Price/ct, falling back to Total Amount ÷ Carat when only a total was given.
      const ct = num(it.carat), total = n('totalAmount');
      it.pricePerCt = n('pricePerCt') || (total && ct > 0 ? String(+(num(total) / ct).toFixed(4)) : '');

      if (isCert) {
        it.lab           = opt('lab', LABS, 'Lab');
        it.certificateNo = get('certificateNo');
        it.cut           = opt('cut', CUT_GRADES, 'Cut');
        it.polish        = opt('polish', CUT_GRADES, 'Polish');
        it.symmetry      = opt('symmetry', CUT_GRADES, 'Symmetry');
        it.fluorescence  = opt('fluorescence', FLUORESCENCE_GRADES, 'Fluorescence');
        it.measLength    = n('measLength');
        it.measWidth     = n('measWidth');
        it.measDepth     = n('measDepth');
        it.listPriceAmount  = n('listPriceAmount');
        it.priceListPercent = n('priceListPercent');
        // Same precedence as typing it in: List + Discount drives Price/ct;
        // a Price/ct on its own drives Discount % instead (see resolveCertPrice).
        const listDriven = num(it.listPriceAmount) > 0 && it.priceListPercent !== '';
        it.priceMode = listDriven ? 'discount' : (it.pricePerCt ? 'price' : undefined);
        items.push(resolveCertPrice(it) as ImportRow);
        cert++;
      } else {
        it.size    = get('size');
        it.subSize = get('subSize');
        it.sku     = get('sku');
        items.push(it);
        loose++;
      }
    }
  };

  readSheet(IMPORT_LOOSE_SHEET, 'loose',     importColsFor(IMPORT_LOOSE_COLS, isIndian(settings)), n => n.includes('loose'));
  readSheet(IMPORT_CERT_SHEET,  'certified', importColsFor(IMPORT_CERT_COLS, isIndian(settings)),  n => n.includes('cert'));

  if (!wb.worksheets.some(w => /loose|cert/i.test(w.name))) {
    warnings.push(`No "${IMPORT_LOOSE_SHEET}" or "${IMPORT_CERT_SHEET}" sheet found — start from the sample file.`);
  }
  return { items, loose, cert, warnings, hasInvoiceInfo };
}

// ── Grouping rows into invoices ──
interface ImportGroup {
  key:               string;
  fromEntityId:      string;
  supplierInvoiceNo: string;
  purchaseDate:      string;
  terms:             number;
  dueDate:           string;
  rows:              ImportRow[];
  /** Company or date still missing — can't be saved until fixed. */
  incomplete:        boolean;
}

const importGroupKey = (r: ImportRow) =>
  `${r.fromEntityId}|${r.supplierInvoiceNo.trim().toLowerCase()}|${r.purchaseDate}`;

function groupImportRows(rows: ImportRow[], entities: Entity[]): ImportGroup[] {
  const map = new Map<string, ImportGroup>();
  for (const r of rows) {
    const key = importGroupKey(r);
    let g = map.get(key);
    if (!g) {
      g = { key, fromEntityId: r.fromEntityId, supplierInvoiceNo: r.supplierInvoiceNo.trim(), purchaseDate: r.purchaseDate,
        terms: 0, dueDate: '', rows: [], incomplete: !r.fromEntityId || !r.purchaseDate };
      map.set(key, g);
    }
    g.rows.push(r);
  }
  for (const g of map.values()) {
    // Invoice terms = the first row that states any; else the company's own
    // default; else the form's default (30). Due Date then follows the
    // form's exact 'average' rule: item terms (blank → invoice terms),
    // averaged and rounded up.
    const stated = g.rows.find(r => num(r.invoiceTerms) > 0);
    g.terms = stated ? num(stated.invoiceTerms)
      : entities.find(e => e.id === g.fromEntityId)?.defaultTerms ?? EMPTY_BATCH_HEADER.terms;
    if (g.purchaseDate) {
      const eff = g.rows.map(r => (num(r.terms) > 0 ? num(r.terms) : g.terms));
      g.dueDate = addDaysToDate(g.purchaseDate, Math.ceil(eff.reduce((s, t) => s + t, 0) / eff.length));
    }
  }
  // Oldest invoice first — the order they'll be saved in (and get Lot Nos).
  return [...map.values()].sort((a, b) => (a.purchaseDate || '9999').localeCompare(b.purchaseDate || '9999'));
}

function importGroupHeader(g: ImportGroup, fileName: string): BatchHeader {
  return {
    ...EMPTY_BATCH_HEADER,
    fromEntityId: g.fromEntityId, supplierInvoiceNo: g.supplierInvoiceNo,
    purchaseDate: g.purchaseDate, terms: g.terms, dueDate: g.dueDate,
    dueDateMode: 'average', notes: `Imported from ${fileName}`,
  };
}

// Drop the per-row invoice fields — what's left is a plain BatchItem.
function importRowToItem(r: ImportRow): BatchItem {
  const item: Partial<ImportRow> = { ...r };
  delete item.fromEntityId; delete item.supplierInvoiceNo; delete item.purchaseDate; delete item.invoiceTerms;
  return item as BatchItem;
}

const fmtImportDate = (iso: string) => iso
  ? new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '';

function IconUploadFile() {
  return <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M12 17v-6" /><path d="M9.5 13.5 12 11l2.5 2.5" />
  </svg>;
}

// ImportField → the ImportRow property it edits. Only the entity columns
// differ (the sheet holds a name; the row holds the matched entity id).
function importFieldKey(f: ImportField): keyof ImportRow | null {
  if (f === 'company') return 'fromEntityId';
  if (f === 'broker1') return 'broker1EntityId';
  if (f === 'broker2') return 'broker2EntityId';
  if (f === 'totalAmount' || f === 'costPerCt' || f === 'dueDate') return null;
  return f;
}

function importCellMissing(it: ImportRow, c: ImportCol, multi: boolean): boolean {
  if (c.field === 'certificateNo') return it.lab !== 'None' && !it.certificateNo.trim();
  if (c.field === 'sku' && isDummyPurchaseLine(it)) return false;   // a Dummy line holds no SKU
  if (!c.required && !(multi && c.invoiceRequired)) return false;
  const k = importFieldKey(c.field);
  const v = k ? String(it[k] ?? '').trim() : '';
  return !v || ((c.field === 'carat' || c.field === 'pricePerCt') && num(v) <= 0);
}

const IMPORT_MISSING_BG = 'color-mix(in srgb, var(--danger) 12%, transparent)';

// Excel import: pick the file, then preview AND fix every row right here —
// the same Loose / Certified tabs and Excel-style grid as the Add Purchase
// Invoice form, with an Invoices summary on top showing how the rows group
// into invoices (company, dates, due date, totals). "Import N Invoices"
// saves them all; with no invoice columns filled (one invoice), "Review
// Invoice →" opens the regular Add Purchase Invoice form pre-filled instead.
function ImportPurchaseModal({
  open, onClose, entities, settings, onReview, onImported,
}: {
  open:     boolean;
  onClose:  () => void;
  entities: Entity[];
  settings: BusinessSettings | null;
  onReview: (items: BatchItem[], fileName: string, header?: Partial<BatchHeader>) => void;
  /** Invoices saved by "Import N Invoices"; `done` = everything saved, safe to close. */
  onImported: (lotNos: string[], done: boolean) => void;
}) {
  const { user, business } = useAuth();
  const brokers   = useMemo(() => entities.filter(e => e.types.includes('broker')), [entities]);
  const companies = useMemo(() => [...entities].sort((a, b) => a.companyName.localeCompare(b.companyName)), [entities]);
  const lists     = useMemo(() => importLists(settings, []), [settings]);
  const money     = (v: number) => fmtMoney(v, settings?.currency ?? 'USD');
  const nameOf    = (id: string) => entities.find(e => e.id === id)?.companyName ?? '';
  const [fileName, setFileName] = useState('');
  const [rows,     setRows]     = useState<ImportRow[]>([]);
  const [fileHasInvoiceInfo, setFileHasInvoiceInfo] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [tab,      setTab]      = useState<RowType>('loose');
  const [busy,     setBusy]     = useState(false);
  const [saving,   setSaving]   = useState('');   // progress text while importing, '' when idle
  const [dragOver, setDragOver] = useState(false);
  const [error,    setError]    = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Reset is handled by remounting (key={} at the call site) rather than an
  // open-effect, so this modal always starts from the useState defaults.

  async function readFile(f: File) {
    setError('');
    if (!/\.xlsx$/i.test(f.name)) { setError('Please choose an Excel (.xlsx) file — start from the sample file.'); return; }
    setBusy(true);
    try {
      const res = await parsePurchaseImportXlsx(await f.arrayBuffer(), settings, entities);
      setFileName(f.name);
      setRows(res.items);
      setFileHasInvoiceInfo(res.hasInvoiceInfo);
      setWarnings(res.warnings);
      setTab(res.loose > 0 || res.cert === 0 ? 'loose' : 'certified');
      if (res.items.length === 0) setError('No line items found in that file.');
    } catch (e) {
      // Keep the real reason visible (and in the console) so a bad file can be told apart from a bug.
      console.error('Purchase import failed', e);
      const why = e instanceof Error && e.message ? ` (${e.message})` : '';
      setError(`Could not read that file${why}. Make sure it is an .xlsx saved from Excel, Numbers or Google Sheets.`);
    }
    setBusy(false);
  }

  async function downloadSample() {
    const byName = (a: Entity, b: Entity) => a.companyName.localeCompare(b.companyName);
    try {
      await downloadPurchaseImportSample(
        settings,
        [...brokers].sort(byName).map(b => b.companyName),
        entities.filter(e => e.types.includes('supplier')).sort(byName).map(e => e.companyName),
      );
    } catch {
      setError('Could not create the sample file.');
    }
  }

  // Same rules as the form's own handlers: the certified pricing trio stays
  // in sync via resolveCertPrice, and clearing a broker clears its %.
  function update(key: string, field: ImportField, value: string) {
    setRows(prev => prev.map(it => {
      if (it._key !== key) return it;
      if (field === 'listPriceAmount')  return resolveCertPrice({ ...it, listPriceAmount: value }) as ImportRow;
      if (field === 'priceListPercent') return resolveCertPrice({ ...it, priceListPercent: value, priceMode: 'discount' }) as ImportRow;
      if (field === 'pricePerCt' && it.itemType === 'certified') return resolveCertPrice({ ...it, pricePerCt: value, priceMode: 'price' }) as ImportRow;
      if (field === 'broker1') return { ...it, broker1EntityId: value, broker1CommissionPercent: value ? it.broker1CommissionPercent : '' };
      if (field === 'broker2') return { ...it, broker2EntityId: value, broker2CommissionPercent: value ? it.broker2CommissionPercent : '' };
      const k = importFieldKey(field);
      return k ? { ...it, [k]: value } : it;
    }));
  }
  function remove(key: string) { setRows(prev => prev.filter(it => it._key !== key)); }

  // Once a file is loaded (and maybe edited here), closing asks first — same
  // Discard / keep editing prompt as the invoice modals.
  function requestClose() {
    if (saving) return;
    if (rows.length > 0) setConfirmDiscard(true);
    else onClose();
  }

  // "Multi" = the rows say which invoice they belong to, so they're split
  // into invoices and saved from here. Otherwise it's one invoice whose
  // header is filled in the regular form.
  const multi = fileHasInvoiceInfo || rows.some(r => r.fromEntityId || r.purchaseDate || r.supplierInvoiceNo.trim());
  const groups = useMemo(() => (multi ? groupImportRows(rows, entities) : []), [multi, rows, entities]);
  const groupOf = useMemo(() => {
    const m = new Map<string, ImportGroup>();
    groups.forEach(g => g.rows.forEach(r => m.set(r._key, g)));
    return m;
  }, [groups]);

  const looseRows = rows.filter(it => it.itemType === 'loose');
  const certRows  = rows.filter(it => it.itemType === 'certified');
  const tabRows   = tab === 'loose' ? looseRows : certRows;
  const cols      = importColsFor(tab === 'loose' ? IMPORT_LOOSE_COLS : IMPORT_CERT_COLS, isIndian(settings))
    .filter(c => multi || !IMPORT_INVOICE_COLS.includes(c));   // single invoice: its header is filled in the form
  const totals = {
    ct:   rows.reduce((s, it) => s + num(it.carat), 0),
    amt:  rows.reduce((s, it) => s + calcRow(it).amount, 0),
    cost: rows.reduce((s, it) => s + calcRow(it).costAmt, 0),
  };
  const missingCount = (list: ImportRow[], cs: ImportCol[]) =>
    list.reduce((n, it) => n + cs.filter(c => importCellMissing(it, c, multi)).length, 0);
  const looseMissing = missingCount(looseRows, IMPORT_LOOSE_COLS);
  const certMissing  = missingCount(certRows, IMPORT_CERT_COLS);

  async function importAll() {
    if (looseMissing + certMissing > 0) {
      setError(`Fill the ${looseMissing + certMissing} red cell${looseMissing + certMissing !== 1 ? 's' : ''} first.`);
      return;
    }
    // Validate every invoice before saving any of them.
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      const v = validateBatch(importGroupHeader(g, fileName), g.rows.map(importRowToItem));
      if (v) { setError(`Invoice ${i + 1} (${nameOf(g.fromEntityId)}${g.supplierInvoiceNo ? ` · ${g.supplierInvoiceNo}` : ''}): ${v}`); return; }
    }
    if (!business || !user) { setError('Not signed in.'); return; }
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); return; }

    setError('');
    const lotNos: string[] = [];
    const savedKeys = new Set<string>();
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      setSaving(`Saving invoice ${i + 1} of ${groups.length}…`);
      try {
        const { invoiceNo } = await createPurchaseInvoice({
          db, business, user, settings, header: importGroupHeader(g, fileName), items: g.rows.map(importRowToItem),
        });
        lotNos.push(invoiceNo);
        g.rows.forEach(r => savedKeys.add(r._key));
      } catch (err) {
        // Stop here. Already-saved invoices leave the list, so fixing the
        // problem and importing again can't create them twice.
        setRows(prev => prev.filter(r => !savedKeys.has(r._key)));
        setSaving('');
        setError(`${lotNos.length > 0 ? `Saved ${lotNos.length} of ${groups.length} invoices (${lotNos.join(', ')}) — they've been removed from this list. ` : ''}`
          + `Invoice ${i + 1} (${nameOf(g.fromEntityId)}${g.supplierInvoiceNo ? ` · ${g.supplierInvoiceNo}` : ''}) failed: `
          + (err instanceof Error ? err.message : 'unknown error'));
        if (lotNos.length > 0) onImported(lotNos, false);
        return;
      }
    }
    setSaving('');
    onImported(lotNos, true);
  }

  function renderCell(it: ImportRow, c: ImportCol) {
    if (c.formula) {
      const calc = calcRow(it);
      const content = c.field === 'dueDate'
        ? fmtImportDate(groupOf.get(it._key)?.dueDate ?? '')
        : (() => { const v = c.field === 'totalAmount' ? calc.amount : calc.costPerCt; return v > 0 ? money(v) : ''; })();
      return <td key={c.field} style={{ ...GTD_TEXT, fontFamily: 'var(--font-num)', textAlign: 'right', color: 'var(--text-2)', background: 'var(--surface-2)', whiteSpace: 'nowrap' }}>
        {content || <span style={{ opacity: 0.4 }}>—</span>}
      </td>;
    }
    if (c.field === 'category') {
      // Same icon picker as the Add Purchase Invoice grid — icons, not text.
      return <td key={c.field} style={GTD_TEXT}>
        <CategoryPicker value={it.category} onChange={v => update(it._key, 'category', v)} />
      </td>;
    }
    const k = importFieldKey(c.field)!;
    const value = String(it[k] ?? '');
    const tdStyle = { ...GTD, minWidth: c.width * 7, background: importCellMissing(it, c, multi) ? IMPORT_MISSING_BG : undefined };
    const disabled = (c.field === 'broker1CommissionPercent' && !it.broker1EntityId)
      || (c.field === 'broker2CommissionPercent' && !it.broker2EntityId)
      || ((c.field === 'listPriceAmount' || c.field === 'priceListPercent') && it.lab === 'None');

    if (c.list) {
      const opts: { value: string; label: string }[] =
        c.list === 'company'  ? companies.map(e => ({ value: e.id, label: e.companyName }))
        : c.list === 'broker'   ? brokers.map(b => ({ value: b.id, label: b.companyName }))
        : c.list === 'category' ? CATEGORY_ORDER.map(cat => ({ value: cat, label: CATEGORY_META[cat].label }))
        : lists[c.list].map(o => ({ value: o, label: o }));
      // A non-standard value from the file stays selectable instead of silently vanishing.
      if (value && !opts.some(o => o.value === value)) opts.push({ value, label: `${value} (from file)` });
      return <td key={c.field} style={tdStyle}>
        <select className="grid-cell grid-cell-select" value={value} onChange={e => update(it._key, c.field, e.target.value)}>
          <option value="">{c.list === 'broker' ? 'No broker' : c.list === 'company' ? 'Select company…' : '—'}</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>;
    }
    return <td key={c.field} style={tdStyle}>
      <input
        className={`grid-cell${c.numeric ? ' no-spinner' : ''}`}
        type={c.date ? 'date' : c.numeric ? 'number' : 'text'}
        style={c.numeric ? { textAlign: 'right', fontFamily: 'var(--font-num)' } : c.field === 'code' ? { fontFamily: 'var(--font-num)' } : undefined}
        placeholder={c.field === 'code' ? 'Auto' : c.field === 'invoiceTerms' ? String(EMPTY_BATCH_HEADER.terms) : ''}
        value={value} disabled={disabled}
        onChange={e => update(it._key, c.field, e.target.value)}
      />
    </td>;
  }

  const invTh: React.CSSProperties = { ...GTH, whiteSpace: 'nowrap' };
  const invTd: React.CSSProperties = { ...GTD_TEXT, whiteSpace: 'nowrap', color: 'var(--text-2)' };
  const invNum: React.CSSProperties = { ...invTd, fontFamily: 'var(--font-num)', textAlign: 'right' };
  const dash = <span style={{ opacity: 0.4 }}>—</span>;

  return (
    <>
    <Modal
      open={open}
      onClose={requestClose}
      title="Import Purchase Invoices"
      subtitle={fileName
        ? `${fileName} — check and fix the rows below${multi ? `, then import ${groups.length} invoice${groups.length !== 1 ? 's' : ''}` : ', then review the invoice'}`
        : 'Excel file with a Loose Polished sheet and a Certified Polished sheet'}
      size={rows.length > 0 ? 'full' : 'md'}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
          {rows.length > 0 && (
            <div style={{ marginRight: 'auto' }}>
              <TotalsFigures figures={[
                ...(multi ? [{ label: 'Invoices', value: String(groups.length) }] : []),
                { label: 'Items', value: String(rows.length) },
                { label: 'Carat', value: `${totals.ct.toFixed(2)} ct` },
                { label: 'Amount', value: money(totals.amt), strong: true },
                { label: 'Price', value: money(totals.cost), color: 'var(--primary)', title: 'Amount plus Added Value %' },
              ]} />
            </div>
          )}
          <Button variant="secondary" onClick={requestClose} disabled={!!saving}>Cancel</Button>
          {multi ? (
            <>
              {groups.length === 1 && !groups[0].incomplete && (
                <Button variant="secondary" disabled={!!saving}
                  title="Open this one invoice in the regular Add Purchase Invoice form instead"
                  onClick={() => {
                    const h = importGroupHeader(groups[0], fileName);
                    onReview(groups[0].rows.map(importRowToItem), fileName,
                      { fromEntityId: h.fromEntityId, supplierInvoiceNo: h.supplierInvoiceNo, purchaseDate: h.purchaseDate, terms: h.terms, dueDate: h.dueDate });
                  }}>
                  Open in Invoice Form
                </Button>
              )}
              <Button variant="primary" loading={!!saving} disabled={groups.length === 0} onClick={importAll}>
                {saving || `Import ${groups.length} Invoice${groups.length !== 1 ? 's' : ''}`}
              </Button>
            </>
          ) : (
            <Button variant="primary" disabled={rows.length === 0} onClick={() => onReview(rows.map(importRowToItem), fileName)}>
              Review Invoice →
            </Button>
          )}
        </div>
      }
    >
      <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) readFile(f); }} />

      {rows.length === 0 ? (
        <>
          <div
            role="button" tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); }}
            style={{
              border: `1.5px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
              background: dragOver ? 'var(--primary-light)' : 'var(--surface-2)',
              borderRadius: 'var(--r-sm)', padding: '26px 16px', textAlign: 'center', cursor: 'pointer',
              color: 'var(--text-3)', transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <IconUploadFile />
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', marginTop: 6 }}>
              {busy ? 'Reading…' : 'Choose or drop an Excel file'}
            </div>
            <div style={{ fontSize: 11.5, marginTop: 2 }}>.xlsx · one or many purchase invoices</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="line-item-link-btn" onClick={downloadSample}>Download Sample File</button>
          </div>
        </>
      ) : (
        <>
          {/* Invoices this file will create — one per Company + Supplier Inv No + Purchase Date */}
          {multi && (
            <>
              <div className="form-section-label" style={{ marginBottom: 8 }}>Invoices ({groups.length})</div>
              <div style={{
                border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
                overflow: 'auto', maxHeight: 170, marginBottom: 16, overscrollBehavior: 'contain',
              }}>
                <table className="excel-grid-table">
                  <thead>
                    <tr>
                      <th style={invTh}>#</th>
                      <th style={{ ...invTh, minWidth: 180 }}>Company</th>
                      <th style={invTh}>Supplier Inv No</th>
                      <th style={invTh}>Purchase Date</th>
                      <th style={invTh}>Terms</th>
                      <th style={invTh}>Due Date</th>
                      <th style={invTh}>Loose</th>
                      <th style={invTh}>Certified</th>
                      <th style={invTh}>Carat</th>
                      <th style={invTh}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, i) => {
                      const bg = g.incomplete ? IMPORT_MISSING_BG : undefined;
                      return (
                        <tr key={g.key}>
                          <td style={invTd}>{i + 1}</td>
                          <td style={{ ...invTd, background: !g.fromEntityId ? bg : undefined, color: 'var(--text-1)', fontWeight: 600 }}>
                            {nameOf(g.fromEntityId) || <span style={{ color: 'var(--danger)' }}>Company missing</span>}
                          </td>
                          <td style={{ ...invTd, fontFamily: 'var(--font-num)' }}>{g.supplierInvoiceNo || dash}</td>
                          <td style={{ ...invTd, background: !g.purchaseDate ? bg : undefined }}>
                            {fmtImportDate(g.purchaseDate) || <span style={{ color: 'var(--danger)' }}>Date missing</span>}
                          </td>
                          <td style={invNum}>{g.terms}d</td>
                          <td style={invTd}>{fmtImportDate(g.dueDate) || dash}</td>
                          <td style={invNum}>{g.rows.filter(r => r.itemType === 'loose').length || dash}</td>
                          <td style={invNum}>{g.rows.filter(r => r.itemType === 'certified').length || dash}</td>
                          <td style={invNum}>{g.rows.reduce((s, r) => s + num(r.carat), 0).toFixed(2)}</td>
                          <td style={invNum}>{money(g.rows.reduce((s, r) => s + calcRow(r).amount, 0))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Tabs + file actions — same tab control as the Add Purchase Invoice form */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            <PageTabs
              className="purchase-items-tabs"
              tabs={[
                { key: 'loose',     label: `Loose Polished Line Items (${looseRows.length})${looseMissing ? ` · ${looseMissing} to fix` : ''}` },
                { key: 'certified', label: `Certified Polished Line Items (${certRows.length})${certMissing ? ` · ${certMissing} to fix` : ''}` },
              ]}
              active={tab}
              onChange={k => setTab(k as RowType)}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button type="button" className="line-item-link-btn" onClick={() => fileRef.current?.click()} disabled={!!saving}>
                {busy ? 'Reading…' : 'Choose a different file'}
              </button>
              <button type="button" className="line-item-link-btn" onClick={downloadSample}>Download Sample File</button>
            </div>
          </div>

          <div style={{
            border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
            overflow: 'auto', maxHeight: multi ? 'calc(90vh - 500px)' : 'calc(90vh - 300px)', minHeight: 200, marginBottom: 12,
            overscrollBehavior: 'contain',
          }}>
            <table className="excel-grid-table">
              <thead>
                <tr>
                  <th style={{ ...GTH }}>#</th>
                  {cols.map(c => (
                    <th key={c.field} style={{ ...GTH, minWidth: c.field === 'category' ? undefined : c.width * 7, whiteSpace: 'nowrap' }}>
                      {c.field === 'category' ? 'Cat.' : c.header}{c.required || c.field === 'certificateNo' || (multi && c.invoiceRequired)
                        ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
                    </th>
                  ))}
                  <th className="excel-grid-remove-col" style={{ ...GTH }} />
                </tr>
              </thead>
              <tbody>
                {tabRows.map((it, idx) => (
                  <tr key={it._key}>
                    <td style={GTD_TEXT}>{idx + 1}</td>
                    {cols.map(c => renderCell(it, c))}
                    <td className="excel-grid-remove-col" style={{ ...GTD_TEXT, textAlign: 'center' }}>
                      <button type="button" className="line-item-remove" title="Remove row" onClick={() => remove(it._key)} disabled={!!saving}>
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                ))}
                {tabRows.length === 0 && (
                  <tr>
                    <td colSpan={cols.length + 2} style={{ ...GTD_TEXT, textAlign: 'center', color: 'var(--text-3)', padding: '28px 8px' }}>
                      No {tab === 'loose' ? 'loose' : 'certified'} rows in this file.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {warnings.length > 0 && (
            <div className="msg msg-note" style={{ fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 110, overflowY: 'auto', alignItems: 'flex-start' }}>
              {warnings.join('\n')}
            </div>
          )}
        </>
      )}

      {error && <div className="msg msg-error" style={{ marginTop: 12 }}>{error}</div>}
    </Modal>

    <ConfirmModal
      open={confirmDiscard}
      title="Discard import?"
      message="The imported rows (and any edits made here) will be lost. Discard them and close, or keep editing?"
      confirmLabel="Discard"
      onConfirm={() => { setConfirmDiscard(false); onClose(); }}
      onClose={() => setConfirmDiscard(false)}
    />
    </>
  );
}

// ── Batch Add / Edit Modal ────────────────────────────────────────────────────

function BatchAddModal({
  open, onClose, entities, settings, editInvoice, importedItems, importedFrom, importedHeader,
}: {
  open:         boolean;
  onClose:      () => void;
  entities:     Entity[];
  settings:     BusinessSettings | null;
  editInvoice?: PurchaseInvoice | null;
  /** Rows parsed from an Excel import (ImportPurchaseModal) — a new invoice
   *  opens pre-filled with these instead of empty, for review before save. */
  importedItems?: BatchItem[] | null;
  importedFrom?:  string;
  /** Invoice header that came with the imported rows (a single-invoice Excel import), if any. */
  importedHeader?: Partial<BatchHeader> | null;
}) {
  const { user, business } = useAuth();
  const isEdit = !!editInvoice;
  const money = (v: number) => fmtMoney(v, settings?.currency ?? 'USD');

  const [header, setHeader] = useState<BatchHeader>({ ...EMPTY_BATCH_HEADER });
  const brokers = useMemo(() => entities.filter(e => e.types.includes('broker')), [entities]);
  const [items,   setItems]  = useState<BatchItem[]>([]);
  const [saving,  setSaving] = useState(false);
  const [error,   setError]  = useState('');
  const [fetchNotice, setFetchNotice] = useState('');
  const [createEntityOpen, setCreateEntityOpen] = useState(false);
  const [justCreatedEntity, setJustCreatedEntity] = useState<Entity | null>(null);
  // Tracks whether the user has actually typed/changed anything since the
  // modal opened, so Escape/backdrop/× — and Cancel — can ask before
  // throwing away unsaved edits instead of closing straight away. Reset on
  // every open below; the one internal effect that calls setH on its own
  // (the average-due-date recompute) deliberately writes via setHeader
  // instead so it never marks the form dirty on its own.
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // After a failed Save, every row's empty required cells turn red — not
  // just rows the user has started typing in.
  const [showAllMissing, setShowAllMissing] = useState(false);
  const [itemsTab, setItemsTab] = useState<'loose' | 'certified'>('loose');
  // Additional Details — Purchase Expenses tied to this invoice, entered
  // right here instead of a separate trip to the Purchase Expenses page.
  // originalAddlExpensesRef holds what was actually loaded from Firestore
  // when editing, so save-time can diff current rows against it (added /
  // changed / removed) — never touched by the rows state itself.
  const [addlExpenses, setAddlExpenses] = useState<AddlExpenseRow[]>([]);
  const originalAddlExpensesRef = useRef<PurchaseExpense[]>([]);
  // Live preview of the next auto Code, seeded from the persisted counter
  // (falling back to Settings' Code Prefix if it's never been used yet) —
  // `n` is "last handed out", so the next row gets String(n + 1). This is
  // only a preview: the real guarantee is still the itemCodes collision
  // check at save time, same as a manually-typed value.
  const [codeCursor, setCodeCursor] = useState<number | null>(null);
  // Codes returned by a deleted row this session, smallest-first — addItem
  // draws from here before ever advancing codeCursor, so deleting a row
  // and adding a new one previews the SAME number back instead of always
  // climbing. Purely an in-session preview convenience; the real
  // uniqueness guarantee is still the itemCodes collision check at save
  // time, unchanged.
  const [reclaimedCodes, setReclaimedCodes] = useState<number[]>([]);
  // For Additional Details' "Paid Now" bank-account picker — same query
  // shape Expenses.tsx/PurchaseExpenses.tsx already use.
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  useEffect(() => {
    if (!open) return;
    const db = getClientDb();
    if (!db || !business) return;
    const unsub = onSnapshot(
      collection(db, 'bankAccounts'),
      snap => setBankAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount))
        .filter(a => a.isActive !== false).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))),
      () => {},
    );
    return unsub;
  }, [open, business]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      let base = settings?.itemCodeStart ?? 0;
      const db = getClientDb();
      if (db && business) {
        try {
          const snap = await getDoc(doc(db, 'counters', business.id));
          const val = snap.exists() ? snap.data().itemCode : undefined;
          if (typeof val === 'number') base = val;
        } catch { /* fall back to the Settings seed */ }
      }
      if (cancelled) return;

      if (editInvoice) {
        const pd = editInvoice.purchaseDate ?? todayISO();
        const fallback: RowType = editInvoice.type === 'polished-certified' ? 'certified' : 'loose';
        setHeader({
          fromEntityId:      editInvoice.fromEntityId,
          purchaseDate:      pd,
          terms:             editInvoice.terms,
          dueDate:           editInvoice.dueDate,
          supplierInvoiceNo: editInvoice.supplierInvoiceNo ?? '',
          notes:             editInvoice.notes ?? '',
          brokerEntityId:          editInvoice.brokerEntityId ?? '',
          brokerCommissionPercent: editInvoice.brokerCommissionPercent != null ? String(editInvoice.brokerCommissionPercent) : '',
          dueDateMode: editInvoice.dueDateMode === 'separate' ? 'separate' : 'average',
        });
        setItems(editInvoice.lineItems.map(li => lineItemToBatchItem(li, fallback)));
        setGstIn(gstInputFrom(editInvoice.gst));
        setCodeCursor(base);
        // Open on whichever tab this invoice actually has rows in — a
        // certified-only invoice landing on an empty Loose tab (with its
        // real rows sitting unseen on the other tab) would be confusing.
        setItemsTab(editInvoice.type === 'polished-certified' ? 'certified' : 'loose');
        // Additional Details — one-shot fetch (not live) of whatever's
        // already linked to this invoice, same "on-demand modal data"
        // convention as Split & Merge's History modal. The original list
        // is kept separately so save-time can diff against it.
        if (db) {
          try {
            const peSnap = await getDocs(query(collection(db, 'purchaseExpenses'), where('invoiceId', '==', editInvoice.id)));
            const existing = peSnap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseExpense));
            if (!cancelled) {
              originalAddlExpensesRef.current = existing;
              setAddlExpenses(existing.map(addlExpenseToRow));
            }
          } catch { /* leave Additional Details empty rather than block opening the modal */ }
        }
      } else {
        // A new invoice starts genuinely empty — no pre-seeded row — and
        // always opens on the Loose tab; the user adds the first row
        // themselves via "+ Add Row" (on whichever tab they actually need).
        setHeader({ ...EMPTY_BATCH_HEADER, ...(importedItems?.length ? importedHeader : null) });
        setGstIn(gstInputFrom(isIndian(settings) ? defaultGst(settings) : null));
        if (importedItems?.length) {
          // Imported rows get the same live Code preview a typed row would —
          // a blank Code takes the next number, a Code from the file is kept.
          let cursor = base;
          setItems(importedItems.map(it => (it.code.trim() ? it : { ...it, code: String(++cursor) })));
          setCodeCursor(cursor);
          setItemsTab(importedItems.some(it => it.itemType === 'loose') ? 'loose' : 'certified');
        } else {
          setItems([]);
          setCodeCursor(base);
          setItemsTab('loose');
        }
        originalAddlExpensesRef.current = [];
        setAddlExpenses([]);
      }
      setError('');
      setFetchNotice('');
      // An imported invoice is unsaved work from the moment it opens — closing
      // it should ask first, not silently drop the whole file's rows.
      setDirty(!editInvoice && !!importedItems?.length);
      setShowAllMissing(false);
      setReclaimedCodes([]);
    })();
    return () => { cancelled = true; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Indian trading system: Less 1 / Less 2 columns on every stone.
  const indian = isIndian(settings);
  const LX = indian ? 2 : 0;   // nav columns the two Less boxes add after Cost/ct
  // Indian: GST once on the invoice (CGST + SGST, or IGST), typed as %.
  const [gstIn, setGstIn] = useState<GstInput>({ cgst: '', sgst: '', igst: '' });
  const gstPcts: GstPercents = indian ? gstPercentsOf(gstIn) : {};
  const looseColors    = settings?.looseColorOptions?.length   ? settings.looseColorOptions   : COLORS;
  const looseClarities = settings?.looseClarityOptions?.length ? settings.looseClarityOptions : CLARITIES;
  const looseSizes     = settings?.looseSizeOptions    ?? [];
  const looseSubSizes  = settings?.looseSubSizeOptions ?? [];

  function setH<K extends keyof BatchHeader>(k: K, v: BatchHeader[K]) {
    setHeader(h => ({ ...h, [k]: v }));
    setDirty(true);
  }
  function updateItem(key: string, field: keyof Omit<BatchItem, '_key' | 'itemType'>, value: string) {
    setItems(prev => prev.map(it => it._key === key ? { ...it, [field]: value } : it));
    setDirty(true);
  }

  // Average mode: Due Date is derived live from every item's own Terms
  // (blank items fall back to the header's own Payment Terms), averaged
  // and rounded UP to the next whole day — e.g. 30d + 45d → 37.5 → 38d.
  // If no item overrides Terms, every item's effective value is just
  // header.terms, so the average is header.terms itself and Due Date
  // stays exactly purchaseDate + terms, today's original behavior.
  // Doesn't touch header.terms itself — that stays the plain, manually-set
  // baseline (and each new row's Terms placeholder), only Due Date is derived.
  useEffect(() => {
    if (header.dueDateMode !== 'average' || items.length === 0) return;
    const effectiveTerms = items.map(it => {
      const t = num(it.terms);
      return t > 0 ? t : header.terms;
    });
    const avgTerms = Math.ceil(effectiveTerms.reduce((s, t) => s + t, 0) / effectiveTerms.length);
    // setHeader directly, not setH — this is a programmatic recompute, not a
    // user edit, and setH also flips `dirty` (see below); going through it
    // here would mark the form dirty the instant the modal opens.
    setHeader(h => ({ ...h, dueDate: addDaysToDate(header.purchaseDate, avgTerms) }));
  }, [items, header.purchaseDate, header.terms, header.dueDateMode]); // eslint-disable-line react-hooks/exhaustive-deps
  // Certified pricing — List Price/ct, Discount %, and Price/ct are mutually
  // derived (see resolveCertPrice). Each handler marks which field is the
  // user's current source of truth before resolving the other two.
  function updateCertListPrice(key: string, value: string) {
    setItems(prev => prev.map(it => it._key === key ? resolveCertPrice({ ...it, listPriceAmount: value }) : it));
    setDirty(true);
  }
  function updateCertDiscount(key: string, value: string) {
    setItems(prev => prev.map(it => it._key === key ? resolveCertPrice({ ...it, priceListPercent: value, priceMode: 'discount' }) : it));
    setDirty(true);
  }
  function updateCertPrice(key: string, value: string) {
    setItems(prev => prev.map(it => it._key === key ? resolveCertPrice({ ...it, pricePerCt: value, priceMode: 'price' }) : it));
    setDirty(true);
  }
  function addItem(type: RowType) {
    const item = newBatchItem(type);
    if (reclaimedCodes.length > 0) {
      const [next, ...rest] = [...reclaimedCodes].sort((a, b) => a - b);
      item.code = String(next);
      setReclaimedCodes(rest);
    } else if (codeCursor !== null) {
      item.code = String(codeCursor + 1);
      setCodeCursor(codeCursor + 1);
    }
    setItems(prev => [...prev, item]);
    setDirty(true);
  }
  function removeItem(key: string) {
    setItems(prev => {
      // No minimum — the invoice can genuinely have zero rows now (a new
      // invoice starts empty); validateBatch below is what actually blocks
      // saving with nothing entered.
      const removed = prev.find(it => it._key === key);
      const n = removed ? Number(removed.code.trim()) : NaN;
      if (removed?.code.trim() && Number.isFinite(n)) {
        setReclaimedCodes(rc => rc.includes(n) ? rc : [...rc, n]);
      }
      return prev.filter(it => it._key !== key);
    });
    setDirty(true);
  }

  function addAddlExpenseRow() {
    setAddlExpenses(prev => [...prev, newAddlExpenseRow(header.purchaseDate)]);
    setDirty(true);
  }
  function updateAddlExpense<K extends keyof AddlExpenseRow>(key: string, field: K, value: AddlExpenseRow[K]) {
    setAddlExpenses(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r));
    setDirty(true);
  }
  function removeAddlExpenseRow(key: string) {
    setAddlExpenses(prev => prev.filter(r => r._key !== key));
    setDirty(true);
  }

  // ── Fetch Certificate (placeholder) ─────────────────────────────────────
  // Future integration point for a real GIA/HRD lookup by Lab + Cert No.,
  // expected to return a Partial<BatchItem> covering: lab, certificateNo,
  // shape, carat, color, clarity, cut, polish, symmetry, fluorescence,
  // measLength/measWidth/measDepth — applied via the same setItems/map
  // pattern updateItem already uses. Not wired to a real API yet; this just
  // surfaces the button/UX ahead of that integration.
  function fetchCertificate(rowKey: string) {
    const it = items.find(i => i._key === rowKey);
    if (!it) return;
    if (!it.lab || it.lab === 'None' || !it.certificateNo.trim()) {
      setFetchNotice('Select a Lab and enter a Certificate No. first.');
      return;
    }
    setFetchNotice(`Certificate fetch for ${it.lab} ${it.certificateNo} is coming soon. GIA/HRD lookup isn't connected yet.`);
  }

  // Shared with the CSV importer — see validateBatch() up top. Lab=None
  // stones skip the cert-number requirement; a blank Code is fine (it's
  // auto-numbered on save), only manually-typed duplicates are rejected.
  // Additional Details validates separately (not folded into validateBatch)
  // since the CSV importer never has any of these rows to check.
  function validate(): string {
    const v = validateBatch(header, items);
    if (v) return v;
    if (indian && gstConflict(gstPcts)) return 'Use CGST + SGST, or IGST, not both.';
    for (let i = 0; i < addlExpenses.length; i++) {
      const row = addlExpenses[i], n = i + 1;
      if (!row.description.trim())            return `Additional Detail ${n}: Enter a description.`;
      if (!row.amount || num(row.amount) <= 0) return `Additional Detail ${n}: Enter a valid amount.`;
      if (row.payNow && !row.bankAccountId)    return `Additional Detail ${n}: Choose the account it was paid from.`;
    }
    return '';
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); setShowAllMissing(true); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }

    try {
      if (isEdit && editInvoice) {
        // Shared build/totals — only the edit branch needs these inline now;
        // the create branch runs entirely inside createPurchaseInvoice().
        const lineItems: PurchaseLineItem[] = buildLineItems(items);
        const itemCount       = lineItems.length;
        const totalCarat      = lineItems.reduce((s, li) => s + li.carat, 0);
        const goodsUSD        = lineItems.reduce((s, li) => s + li.amountUSD, 0);
        const tax             = indian ? invoiceGst(round2(goodsUSD), gstPcts) : NO_GST;
        const totalAmountUSD  = tax.total > 0 ? round2(goodsUSD + tax.total) : goodsUSD;
        const totalCostingUSD = lineItems.reduce((s, li) => s + li.costingAmountUSD, 0);
        const hasCert  = items.some(it => it.itemType === 'certified');
        const hasLoose = items.some(it => it.itemType === 'loose');
        const invoiceType: PurchaseType = hasCert && hasLoose ? 'polished-mixed' : hasCert ? 'polished-certified' : 'polished-loose';

        // Re-derive dueGroups from the (possibly changed) line items — safe
        // because the lock above already guarantees no group has any
        // payment/credit yet, so there's nothing to reconcile.
        const dueGroups = spreadGstOverGroups(header.dueDateMode === 'separate'
          ? computeDueGroups(lineItems, header.purchaseDate, header.terms, li => li.terms, li => li.amountUSD)
          : undefined, tax.total, goodsUSD);
        const effectiveDueDate = dueGroups && dueGroups.length > 0 ? dueGroups[0].dueDate : header.dueDate;

        const editInvoiceId = editInvoice.id;
        const editInvoiceNo = editInvoice.invoiceNo;
        const invRef = doc(db, 'invoices', editInvoice.id);

        // paidUSD/creditedUSD are untouched by an edit, but paymentStatus is
        // stored (not derived), so it must be recomputed against the NEW
        // totalAmountUSD here — otherwise raising a paid invoice's total
        // (e.g. adding a line item) leaves it showing "Paid" while it
        // actually has fresh outstanding balance.
        const settledUSD = (editInvoice.paidUSD ?? 0) + (editInvoice.creditedUSD ?? 0);
        const editedPaymentStatus: PaymentStatus =
          settledUSD >= totalAmountUSD - 0.005 ? 'paid' : settledUSD > 0 ? 'partial' : 'unpaid';

        // Broker commission on edit — mirrors Sales' SaleAddModal exactly:
        //  · broker ADDED (invoice had none) → seed brokerPaidUSD/status
        //  · broker REMOVED → null every broker field so a partial update()
        //    can't leave a stale broker sitting on the doc, invisible on the
        //    Brokers page but still counted in its totals
        //  · unchanged → recompute USD off the (possibly new) total, leave
        //    brokerPaidUSD/status alone
        const brokerCommissionPercent = header.brokerEntityId ? parseFloat(header.brokerCommissionPercent) || 0 : undefined;
        const brokerCommissionUSD     = header.brokerEntityId ? round2(goodsUSD * (brokerCommissionPercent ?? 0) / 100) : undefined;
        const brokerAdded   = !!header.brokerEntityId && !editInvoice.brokerEntityId;
        const brokerRemoved = !header.brokerEntityId && !!editInvoice.brokerEntityId;

        // Sync InventoryItem docs created for this invoice: diff old vs. new
        // line items by id, blocking the save if any touched item already
        // moved on (memo'd / sold) — its inventory doc must stay authoritative.
        type ExistingInv = { ref: DocumentReference; data: InventoryItem };
        // A purchase with any return against it is locked — editing line
        // items would silently undo the return's carat/cost adjustments.
        const retSnap = await getDocs(query(collection(db, 'purchaseReturns'), where('invoiceId', '==', editInvoice.id)));
        if (!retSnap.empty) {
          throw new Error('This purchase has a return recorded against it — delete the return first if you need to edit the invoice.');
        }
        // Payments + discounts already take this much off; the new total
        // can't go below it or the supplier would end up owing us.
        if (settledUSD > totalAmountUSD + 0.005) {
          throw new Error('The new total is less than what is already paid or discounted on this invoice. Remove a payment or discount first.');
        }
        // A 'separate'-mode invoice with any payment already recorded against
        // one of its due dates is locked the same way — changing line items
        // or their Terms would reshuffle which due date each item belongs to,
        // and there's no safe way to re-map an already-recorded payment onto
        // a due-date grouping that no longer exists the same way.
        if ((editInvoice.dueGroups ?? []).some(g => g.paidUSD > 0 || g.creditedUSD > 0)) {
          throw new Error('This invoice has a payment recorded against one of its due dates — that payment must be removed first if you need to edit the invoice.');
        }
        const existingSnap = await getDocs(query(collection(db, 'inventory'), where('purchaseId', '==', editInvoice.id)));
        // Reconcile new line items against the inventory docs already created
        // for this invoice. Code (the true unique per-physical-item key — see
        // "SKU vs. Code" in CLAUDE.md) is the PRIMARY match key, so an edit
        // that removed and re-added a row keeps updating the SAME inventory
        // doc in place (same Code, new lineItemId) instead of deleting it and
        // creating a fresh one — which would orphan its memos subcollection /
        // Code-registry ownership. Rows with no Code yet (blank — auto-
        // numbered later in this tx) fall back to matching by lineItemId,
        // exactly as before.
        const allExisting: ExistingInv[] = existingSnap.docs.map(d => ({ ref: d.ref, data: d.data() as InventoryItem }));
        const existingByCodeId = new Map<string, ExistingInv>();
        const existingByLineId = new Map<string, ExistingInv>();
        allExisting.forEach(v => {
          if (v.data.codeId)     existingByCodeId.set(v.data.codeId, v);
          if (v.data.lineItemId) existingByLineId.set(v.data.lineItemId, v);
        });

        const claimed = new Set<string>();   // inventory doc ids already matched to a row
        const findMatch = (li: PurchaseLineItem): ExistingInv | undefined => {
          const byCode = li.code ? existingByCodeId.get(normalizeCode(li.code)) : undefined;
          if (byCode && !claimed.has(byCode.ref.id)) return byCode;
          const byLine = existingByLineId.get(li.id);
          if (byLine && !claimed.has(byLine.ref.id)) return byLine;
          return undefined;
        };
        const toUpdate: { li: PurchaseLineItem; existing: ExistingInv }[] = [];
        const toCreate: PurchaseLineItem[] = [];
        lineItems.forEach(li => {
          if (isDummyPurchaseLine(li)) return;   // no inventory doc — see dummyPurchaseAggregate
          const existing = findMatch(li);
          if (existing) { claimed.add(existing.ref.id); toUpdate.push({ li, existing }); }
          else toCreate.push(li);
        });
        const toDelete: ExistingInv[] = allExisting.filter(v => !claimed.has(v.ref.id));

        const blocked = [...toDelete, ...toUpdate.map(x => x.existing)].filter(v => v.data.status !== 'in-stock');
        if (blocked.length > 0) {
          const b = blocked[0];
          throw new Error(`Cannot save — ${b.data.shape} ${b.data.carat.toFixed(2)}ct is already ${b.data.status}. Remove it from Sales/Memo first.`);
        }

        const createRefs = toCreate.map(() => doc(collection(db, 'inventory')));
        const counterRef = doc(db, 'counters', business!.id);

        // Resolve every SKU doc this edit will need to read: any new-side value
        // (create or update) with a loose item carrying a SKU.
        const newSkuIds = [...new Set([
          ...toCreate.filter(li => li.itemType === 'loose' && li.sku).map(li => slugifySku(li.sku!)),
          ...toUpdate.filter(({ li }) => li.itemType === 'loose' && li.sku).map(({ li }) => slugifySku(li.sku!)),
        ])];
        const skuRefs = new Map(newSkuIds.map(id => [id, doc(db, 'skus', id)]));

        // Resolve every manually-typed Code doc this edit will need to read, to
        // check for a collision before committing — Code must be globally unique.
        // Blank-Code rows are resolved separately, below, by auto-numbering.
        const newCodeIds = [...new Set([
          ...toCreate.filter(li => li.code).map(li => normalizeCode(li.code!)),
          ...toUpdate.filter(({ li }) => li.code).map(({ li }) => normalizeCode(li.code!)),
        ])];
        const codeRefs = new Map(newCodeIds.map(id => [id, doc(db, 'itemCodes', id)]));

        // Additional Details — diff current rows against what was actually
        // loaded from Firestore when this modal opened (originalAddlExpensesRef),
        // by _existingId. Anything still matched is a potential update;
        // anything from the original list with no match was removed;
        // anything with no _existingId at all is brand new.
        const peDeletes: PurchaseExpense[] = [];
        const peUpdates: { row: AddlExpenseRow; original: PurchaseExpense }[] = [];
        const peCreates: AddlExpenseRow[] = addlExpenses.filter(r => !r._existingId);
        {
          const byExistingId = new Map(addlExpenses.filter(r => r._existingId).map(r => [r._existingId!, r]));
          originalAddlExpensesRef.current.forEach(orig => {
            const match = byExistingId.get(orig.id);
            if (match) peUpdates.push({ row: match, original: orig });
            else peDeletes.push(orig);
          });
        }
        // Every bank account any of the three operations might touch —
        // read once each inside the transaction below.
        const peAccountIds = new Set<string>();
        peCreates.forEach(r => { if (r.payNow && r.bankAccountId) peAccountIds.add(r.bankAccountId); });
        peUpdates.forEach(({ row, original }) => {
          if (row.payNow && row.bankAccountId) peAccountIds.add(row.bankAccountId);
          if (original.paymentStatus === 'paid' && original.bankAccountId) peAccountIds.add(original.bankAccountId);
        });
        peDeletes.forEach(o => { if (o.paymentStatus === 'paid' && o.bankAccountId) peAccountIds.add(o.bankAccountId); });
        const peAccRefs = new Map([...peAccountIds].map(id => [id, doc(db, 'bankAccounts', id)]));
        const peCreateRefs = peCreates.map(() => doc(collection(db, 'purchaseExpenses')));
        const peCreateTxRefs = peCreates.map(r => r.payNow ? doc(collection(db, 'bankTransactions')) : null);
        // Only unpaid->paid transitions need a brand-new bankTransactions doc;
        // an already-paid row being edited reuses (updates in place) its
        // existing one, and a paid->unpaid transition just deletes it.
        const peUpdateNewTxRefs = peUpdates.map(({ row, original }) =>
          row.payNow && (original.paymentStatus !== 'paid' || !original.bankTransactionId) ? doc(collection(db, 'bankTransactions')) : null);

        await runTransaction(db, async tx => {
          // All reads before any writes, per Firestore transaction rules.
          for (const v of [...toDelete, ...toUpdate.map(x => x.existing)]) {
            const snap = await tx.get(v.ref);
            if (!snap.exists() || (snap.data() as InventoryItem).status !== 'in-stock') {
              throw new Error('An item on this invoice changed status. Please reload and try again.');
            }
          }
          const skuSnaps = new Map<string, { exists: boolean; avg: number }>();
          for (const [id, ref] of skuRefs) {
            const s = await tx.get(ref);
            skuSnaps.set(id, { exists: s.exists(), avg: s.exists() ? ((s.data() as Sku).currentCostingPerCt ?? 0) : 0 });
          }
          const codeSnaps = new Map<string, string | null>();   // id -> owning inventoryItemId, or null if free
          for (const [id, ref] of codeRefs) {
            const s = await tx.get(ref);
            codeSnaps.set(id, s.exists() ? (s.data() as ItemCode).inventoryItemId : null);
          }
          // Collision check: a code already owned by a *different* item blocks the save.
          for (const { li, existing } of toUpdate) {
            if (!li.code) continue;
            const owner = codeSnaps.get(normalizeCode(li.code));
            if (owner && owner !== existing.ref.id) throw new Error(`Code "${li.code}" is already used by another item.`);
          }
          for (const li of toCreate) {
            if (!li.code) continue;
            if (codeSnaps.get(normalizeCode(li.code)) != null) throw new Error(`Code "${li.code}" is already used by another item.`);
          }

          // Blank-Code rows (either type) get auto-numbered here, before any writes —
          // mutating `li.code` in place so every later reference to it (the
          // stored `lineItems` array, upsertCode, etc.) picks up the final value.
          const needsAutoCode = [
            ...toCreate.filter(li => !li.code),
            ...toUpdate.filter(({ li }) => !li.code).map(({ li }) => li),
          ];
          // Always read the counter — even when every row already has a Code
          // (the Add/Edit form pre-fills a live preview, so this is the common
          // case) — and advance it to at least the highest numeric Code used in
          // this save. Otherwise a pre-filled/typed value never persists back
          // to the shared counter, and the *next* invoice's preview would
          // suggest the same already-used number again.
          const counterSnap  = await tx.get(counterRef);
          const storedCursor = counterSnap.exists() ? (counterSnap.data().itemCode ?? settings?.itemCodeStart ?? 0) : (settings?.itemCodeStart ?? 0);
          const peCounterStart = counterSnap.exists() ? (counterSnap.data().purchaseExpense ?? 0) : 0;
          const cursor = { n: storedCursor };
          const reserved = new Set(newCodeIds);   // manually-typed codes in this same save
          for (const li of needsAutoCode) {
            li.code = await nextFreeCode(tx, db, cursor, reserved);
          }
          for (const li of toCreate) {
            const n = li.code ? Number(li.code) : NaN;
            if (Number.isFinite(n) && n > cursor.n) cursor.n = n;
          }
          for (const { li } of toUpdate) {
            const n = li.code ? Number(li.code) : NaN;
            if (Number.isFinite(n) && n > cursor.n) cursor.n = n;
          }
          // Additional Details — every bank account any create/update/delete
          // below might touch. Must stay BEFORE the counter write below:
          // Firestore rejects any read after a write in a transaction.
          const peAccSnaps = new Map<string, BankAccount>();
          for (const [id, ref] of peAccRefs) {
            const s = await tx.get(ref);
            if (s.exists()) peAccSnaps.set(id, s.data() as BankAccount);
          }
          // Books & Dummy lines — re-credit the Dummy balance by the
          // difference from what this invoice credited last time.
          const dummyPlan = await planDummyDelta(tx, db, {
            businessId: business!.id, historyId: `dummypurchase_${editInvoiceId}`,
            aggregate: dummyPurchaseAggregate(lineItems), cursor, reserved, nextCode: nextFreeCode,
          });

          // Same merged-write reasoning as createPurchaseInvoice — a
          // transaction can't write counters/{businessId} twice.
          const editCounterUpdate: Record<string, number> = {};
          if (cursor.n !== storedCursor) editCounterUpdate.itemCode = cursor.n;
          if (peCreates.length > 0) editCounterUpdate.purchaseExpense = peCounterStart + peCreates.length;
          if (Object.keys(editCounterUpdate).length > 0) {
            tx.set(counterRef, editCounterUpdate, { merge: true });
          }
          applyDummyDelta(tx, db, dummyPlan, {
            type: 'purchase', date: header.purchaseDate || todayISO(), entityId: header.fromEntityId,
            category: 'books-dummy', notes: `Dummy purchase — Lot ${editInvoiceNo}`, createdBy: user!.id,
            extra: { purchaseInvoiceId: editInvoiceId, purchaseInvoiceNo: editInvoiceNo },
          });

          tx.update(invRef, stripUndef({
            type:              invoiceType,
            fromEntityId:      header.fromEntityId,
            supplierInvoiceNo: header.supplierInvoiceNo.trim() || undefined,
            purchaseDate:      header.purchaseDate || undefined,
            terms:             header.terms,
            dueDate:           effectiveDueDate,
            dueDateMode:       header.dueDateMode === 'separate' ? 'separate' : null,
            dueGroups:         dueGroups ?? null,
            itemCount, totalCarat, totalAmountUSD, totalCostingUSD,
            ...gstInvoiceFields(goodsUSD, gstPcts, tax),
            paymentStatus:     editedPaymentStatus,
            brokerEntityId:          header.brokerEntityId || (brokerRemoved ? null : undefined),
            brokerCommissionPercent: brokerCommissionPercent ?? (brokerRemoved ? null : undefined),
            brokerCommissionUSD:     brokerCommissionUSD     ?? (brokerRemoved ? null : undefined),
            brokerPaidUSD:       brokerAdded ? 0 : (brokerRemoved ? null : undefined),
            brokerPaymentStatus: brokerAdded ? ('unpaid' as PaymentStatus) : (brokerRemoved ? null : undefined),
            lineItems,
            notes:             header.notes.trim() || undefined,
            updatedAt:         serverTimestamp(),
            ...updatedStamp(user),
          } as Record<string, unknown>));

          logActivity(tx, db, business!.id, user, {
            module: 'purchase', action: 'edit', recordId: editInvoiceId, recordNo: editInvoiceNo,
            partyId: header.fromEntityId || undefined, itemCount, carat: totalCarat, amountUSD: totalAmountUSD,
            prevItemCount: editInvoice.itemCount, prevCarat: editInvoice.totalCarat, prevAmountUSD: editInvoice.totalAmountUSD,
          });

          toDelete.forEach(v => {
            tx.delete(v.ref);
            if (v.data.skuId) tx.delete(doc(db, 'skus', v.data.skuId, 'history', v.data.lineItemId));
            // Unlike Sku (shared across items), a Code doc is exclusively owned by
            // this one item — deleting it frees the Code for reuse.
            if (v.data.codeId) tx.delete(doc(db, 'itemCodes', v.data.codeId));
          });

          // Writes one purchase-arrival history entry (keyed by the line item's
          // own id, so a later edit self-corrects it in place) and lazily
          // creates/touches the SKU doc it belongs to. Never moves the SKU's
          // running average — only an explicit Merge does that.
          function upsertSkuAndHistory(li: PurchaseLineItem, inventoryItemId: string) {
            if (li.itemType !== 'loose' || !li.sku) return;
            const skuId  = slugifySku(li.sku);
            const skuRef = skuRefs.get(skuId)!;
            const info   = skuSnaps.get(skuId)!;
            if (!info.exists) {
              tx.set(skuRef, stripUndef({
                businessId: business!.id, sku: li.sku,
                shape: li.shape, color: li.color, clarity: li.clarity, size: li.size, subSize: li.subSize,
                currentAvgCostPerCt: 0, currentAddedValuePercent: 0, currentCostingPerCt: 0,
                lastActivityAt: todayISO(), createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
              }));
            } else {
              tx.update(skuRef, { lastActivityAt: todayISO(), updatedAt: serverTimestamp() });
            }
            const unitCostPerCt = li.carat > 0 ? li.amountUSD / li.carat : 0;
            tx.set(doc(db!, 'skus', skuId, 'history', li.id), stripUndef({
              type: 'purchase', date: header.purchaseDate || todayISO(),
              supplierEntityId: header.fromEntityId,
              purchaseId: editInvoiceId, purchaseNo: editInvoiceNo, lineItemId: li.id,
              quantityCarat: li.carat, unitCostPerCt, totalCost: li.amountUSD,
              inventoryItemId,
              previousAvgCostPerCt: info.avg, newAvgCostPerCt: info.avg,
              createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }

          // Creates (or re-points) the Code doc that reserves this item's unique
          // key. Unlike upsertSkuAndHistory, there's no history/averaging — just
          // one doc per Code, always owned by exactly one inventory item.
          function upsertCode(li: PurchaseLineItem, inventoryItemId: string) {
            if (!li.code) return;
            const codeId  = normalizeCode(li.code);
            const codeRef = doc(db!, 'itemCodes', codeId);
            tx.set(codeRef, stripUndef({
              businessId: business!.id, code: li.code, inventoryItemId,
              createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }

          toUpdate.forEach(({ li, existing }) => {
            tx.update(existing.ref, stripUndef({
              // Re-align lineItemId in case this row was matched by Code after
              // its line-item id changed (drop + re-add), so later edits can
              // still fall back to the lineItemId path.
              lineItemId: li.id,
              type: lineItemPurchaseType(li),
              shape: li.shape, carat: li.carat, color: li.color, clarity: li.clarity,
              lab: li.lab, certificateNo: li.certificateNo,
              cut: li.cut, polish: li.polish, symmetry: li.symmetry, fluorescence: li.fluorescence,
              measLength: li.measLength, measWidth: li.measWidth, measDepth: li.measDepth,
              description: li.description || null,
              size: li.size, subSize: li.subSize,
              sku:   li.itemType === 'loose' ? (li.sku || undefined) : undefined,
              skuId: li.itemType === 'loose' && li.sku ? slugifySku(li.sku) : undefined,
              code:   li.code || undefined,
              codeId: li.code ? normalizeCode(li.code) : undefined,
              category: li.category,
              costUSD: li.amountUSD, costingUSD: li.costingAmountUSD,
              updatedAt: serverTimestamp(),
            } as Record<string, unknown>));

            const oldSkuId = existing.data.skuId;
            const newSkuId = li.itemType === 'loose' && li.sku ? slugifySku(li.sku) : undefined;
            if (oldSkuId && oldSkuId !== newSkuId) {
              tx.delete(doc(db, 'skus', oldSkuId, 'history', li.id));
            }
            if (newSkuId) upsertSkuAndHistory(li, existing.ref.id);

            const oldCodeId = existing.data.codeId;
            const newCodeId = li.code ? normalizeCode(li.code) : undefined;
            if (oldCodeId && oldCodeId !== newCodeId) {
              tx.delete(doc(db, 'itemCodes', oldCodeId));
            }
            if (newCodeId) upsertCode(li, existing.ref.id);
          });

          toCreate.forEach((li, i) => {
            tx.set(createRefs[i], stripUndef({
              businessId: business!.id,
              purchaseId: editInvoiceId,
              purchaseNo: editInvoiceNo,
              lineItemId: li.id,
              type:       lineItemPurchaseType(li),
              status:     'in-stock' as InventoryStatus,
              shape: li.shape, carat: li.carat, color: li.color, clarity: li.clarity,
              lab: li.lab, certificateNo: li.certificateNo,
              cut: li.cut, polish: li.polish, symmetry: li.symmetry, fluorescence: li.fluorescence,
              measLength: li.measLength, measWidth: li.measWidth, measDepth: li.measDepth,
              description: li.description || null,
              size: li.size, subSize: li.subSize,
              sku:   li.itemType === 'loose' ? (li.sku || undefined) : undefined,
              skuId: li.itemType === 'loose' && li.sku ? slugifySku(li.sku) : undefined,
              code:   li.code || undefined,
              codeId: li.code ? normalizeCode(li.code) : undefined,
              category: li.category,
              costUSD: li.amountUSD, costingUSD: li.costingAmountUSD,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            } as Record<string, unknown>));
            upsertSkuAndHistory(li, createRefs[i].id);
            upsertCode(li, createRefs[i].id);
            // Inventory Activity's 'purchase' entry — only for a genuinely
            // new line added while editing (toCreate), never for toUpdate's
            // own Code re-point, which isn't a new arrival.
            if (li.code) {
              tx.set(doc(collection(db, 'itemCodes', normalizeCode(li.code), 'history')), stripUndef({
                type: 'purchase' as CodeEventType, date: header.purchaseDate || todayISO(),
                carat: li.carat, entityId: header.fromEntityId,
                pricePerCt: li.carat > 0 ? li.amountUSD / li.carat : 0,
                amountUSD: li.amountUSD, inventoryItemId: createRefs[i].id,
                category: li.category,
                notes: `Lot ${editInvoiceNo}`,
                purchaseInvoiceId: editInvoiceId, purchaseInvoiceNo: editInvoiceNo,
                createdAt: serverTimestamp(), createdBy: user!.id,
              }));
            }
          });

          // Ledger (additive) — overwrite this invoice's entry with the
          // recomputed Dr Inventory (Owned) / Cr Accounts Payable at the new
          // total. A now-$0 invoice loses its entry entirely.
          const peId = entryId('purchase_invoice', editInvoiceId);
          if (totalAmountUSD > 0.005) {
            postEntry(tx, db, buildEntry({
              businessId: business!.id, sourceType: 'purchase_invoice', sourceId: editInvoiceId,
              sourceCollection: 'invoices', date: header.purchaseDate || todayISO(),
              currency: settings?.currency ?? 'USD', createdBy: user!.id,
              note: `Purchase ${editInvoiceNo}`,
              lines: linesPurchaseInvoice(totalAmountUSD, header.fromEntityId, tax.total),
            }), peId);
          } else {
            tx.delete(doc(db, 'journalEntries', peId));
          }

          // Broker accrual entry — recompute it, or drop it if the broker was
          // removed or the commission is now $0 (mirrors the Sales edit path).
          const baId = entryId('broker_accrual', editInvoiceId);
          if (header.brokerEntityId && (brokerCommissionUSD ?? 0) > 0.005) {
            postEntry(tx, db, buildEntry({
              businessId: business!.id, sourceType: 'broker_accrual', sourceId: editInvoiceId,
              sourceCollection: 'invoices', date: header.purchaseDate || todayISO(),
              currency: settings?.currency ?? 'USD', createdBy: user!.id,
              note: `Broker commission — ${editInvoiceNo}`,
              lines: linesBrokerAccrual(brokerCommissionUSD!, header.brokerEntityId),
            }), baId);
          } else {
            tx.delete(doc(db, 'journalEntries', baId));
          }

          // Per-line Broker1/Broker2 commission on edit — same idea as the
          // header broker diff above, generalized to a SET of brokers: any
          // broker present in the OLD line items but not (or no longer
          // earning anything in) the NEW ones gets its accrual entry
          // deleted; every broker still earning something gets it
          // upserted at the recomputed amount.
          const oldLineBrokerTotals = computeLineBrokerCommissions(editInvoice.lineItems ?? []);
          const newLineBrokerTotals = computeLineBrokerCommissions(lineItems);
          const allLineBrokerIds = new Set([...oldLineBrokerTotals.keys(), ...newLineBrokerTotals.keys()]);
          for (const brokerEntityId of allLineBrokerIds) {
            const commissionUSD = newLineBrokerTotals.get(brokerEntityId) ?? 0;
            const lbaId = lineBrokerAccrualId(editInvoiceId, brokerEntityId);
            if (commissionUSD > 0.005) {
              postEntry(tx, db, buildEntry({
                businessId: business!.id, sourceType: 'broker_accrual', sourceId: editInvoiceId,
                sourceCollection: 'invoices', date: header.purchaseDate || todayISO(),
                currency: settings?.currency ?? 'USD', createdBy: user!.id,
                note: `Broker commission (per-line) — ${editInvoiceNo}`,
                lines: linesBrokerAccrual(commissionUSD, brokerEntityId),
              }), lbaId);
            } else {
              tx.delete(doc(db, 'journalEntries', lbaId));
            }
          }

          // Additional Details — reconcile against whatever's already
          // linked (added / changed / removed), writing the same shapes
          // createPurchaseInvoice() writes for a brand-new invoice.
          const peBalanceDelta = new Map<string, number>(); // accountId -> amount to ADD to currentBalance

          peDeletes.forEach(orig => {
            tx.delete(doc(db, 'purchaseExpenses', orig.id));
            tx.delete(doc(db, 'journalEntries', entryId('purchase_expense', orig.id)));
            if (orig.paymentStatus === 'paid') {
              if (orig.bankTransactionId) tx.delete(doc(db, 'bankTransactions', orig.bankTransactionId));
              if (orig.bankAccountId) peBalanceDelta.set(orig.bankAccountId, (peBalanceDelta.get(orig.bankAccountId) ?? 0) + (orig.amountUSD ?? 0));
            }
          });

          peUpdates.forEach(({ row, original }, i) => {
            const amt = round2(parseFloat(row.amount) || 0);
            const date = row.date || header.purchaseDate || todayISO();
            const wasPaid = original.paymentStatus === 'paid';
            const nowAcct = row.payNow ? peAccSnaps.get(row.bankAccountId) : undefined;
            const currency = nowAcct?.currency ?? original.currency;
            const peRef = doc(db, 'purchaseExpenses', original.id);

            // Full reversal of the old paid state (simplest correct way to
            // handle amount and/or account both possibly changing at once —
            // the net-delta map below sorts out same-account cancellation).
            if (wasPaid && original.bankAccountId) {
              peBalanceDelta.set(original.bankAccountId, (peBalanceDelta.get(original.bankAccountId) ?? 0) + (original.amountUSD ?? 0));
            }

            let bankTransactionId: string | undefined = original.bankTransactionId;
            if (row.payNow) {
              if (!wasPaid || !original.bankTransactionId) {
                const txRef = peUpdateNewTxRefs[i]!;
                tx.set(txRef, stripUndef({
                  businessId: business!.id, accountId: row.bankAccountId, accountName: nowAcct?.name,
                  currency, direction: 'out', amount: amt, date,
                  description: row.description.trim(), referenceType: 'purchase_expense',
                  referenceId: original.id, category: row.category,
                  createdAt: new Date().toISOString(), createdBy: user!.id,
                }));
                bankTransactionId = txRef.id;
              } else {
                tx.update(doc(db, 'bankTransactions', original.bankTransactionId), stripUndef({
                  accountId: row.bankAccountId, accountName: nowAcct?.name, currency,
                  amount: amt, date, description: row.description.trim(), category: row.category,
                }));
              }
              if (row.bankAccountId) peBalanceDelta.set(row.bankAccountId, (peBalanceDelta.get(row.bankAccountId) ?? 0) - amt);
            } else if (wasPaid && original.bankTransactionId) {
              tx.delete(doc(db, 'bankTransactions', original.bankTransactionId));
              bankTransactionId = undefined;
            }

            tx.set(peRef, stripUndef({
              businessId: business!.id, expenseNo: original.expenseNo, category: row.category,
              description: row.description.trim(), amountUSD: amt, currency, date,
              treatment: 'expense' as PurchaseExpenseTreatment,
              invoiceId: editInvoiceId, invoiceNo: editInvoiceNo,
              vendorEntityId: row.vendorEntityId || undefined,
              paymentStatus: row.payNow ? 'paid' : 'unpaid', paidUSD: row.payNow ? amt : 0,
              bankAccountId: row.payNow ? row.bankAccountId : undefined,
              bankAccountName: row.payNow ? nowAcct?.name : undefined,
              paidDate: row.payNow ? date : undefined,
              bankTransactionId: row.payNow ? bankTransactionId : undefined,
              dueDate: row.payNow ? undefined : (row.dueDate || undefined),
              createdAt: original.createdAt, updatedAt: serverTimestamp(), createdBy: original.createdBy,
            }));

            postEntry(tx, db, buildEntry({
              businessId: business!.id, sourceType: 'purchase_expense', sourceId: original.id,
              sourceCollection: 'purchaseExpenses', date, currency, createdBy: user!.id,
              note: `${original.expenseNo} — ${row.description.trim()}`,
              lines: linesPurchaseExpense(amt, 'expense', row.payNow ? { bankAccountId: row.bankAccountId } : 'accrued', row.vendorEntityId || undefined),
            }), entryId('purchase_expense', original.id));
          });

          peCreates.forEach((row, i) => {
            const amt = round2(parseFloat(row.amount) || 0);
            if (!(amt > 0)) return;
            const n = peCounterStart + i + 1;
            const expenseNo = `${(settings?.purchaseExpensePrefix || 'PE').toUpperCase()}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`;
            const peRef = peCreateRefs[i];
            const txRef = peCreateTxRefs[i];
            const acct  = row.payNow ? peAccSnaps.get(row.bankAccountId) : undefined;
            const currency = acct?.currency ?? settings?.currency ?? 'USD';
            const date = row.date || header.purchaseDate || todayISO();

            tx.set(peRef, stripUndef({
              businessId: business!.id, expenseNo, category: row.category, description: row.description.trim(),
              amountUSD: amt, currency, date, treatment: 'expense' as PurchaseExpenseTreatment,
              invoiceId: editInvoiceId, invoiceNo: editInvoiceNo,
              vendorEntityId: row.vendorEntityId || undefined,
              paymentStatus: row.payNow ? 'paid' : 'unpaid', paidUSD: row.payNow ? amt : 0,
              bankAccountId: row.payNow ? row.bankAccountId : undefined,
              bankAccountName: row.payNow ? acct?.name : undefined,
              paidDate: row.payNow ? date : undefined,
              bankTransactionId: row.payNow ? txRef!.id : undefined,
              dueDate: row.payNow ? undefined : (row.dueDate || undefined),
              createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: user!.id,
            }));
            if (row.payNow && txRef && acct) {
              tx.set(txRef, stripUndef({
                businessId: business!.id, accountId: row.bankAccountId, accountName: acct.name,
                currency: acct.currency, direction: 'out', amount: amt, date,
                description: row.description.trim(), referenceType: 'purchase_expense',
                referenceId: peRef.id, category: row.category,
                createdAt: new Date().toISOString(), createdBy: user!.id,
              }));
              peBalanceDelta.set(row.bankAccountId, (peBalanceDelta.get(row.bankAccountId) ?? 0) - amt);
            }
            postEntry(tx, db, buildEntry({
              businessId: business!.id, sourceType: 'purchase_expense', sourceId: peRef.id,
              sourceCollection: 'purchaseExpenses', date, currency, createdBy: user!.id,
              note: `${expenseNo} — ${row.description.trim()}`,
              lines: linesPurchaseExpense(amt, 'expense', row.payNow ? { bankAccountId: row.bankAccountId } : 'accrued', row.vendorEntityId || undefined),
            }), entryId('purchase_expense', peRef.id));
          });

          peBalanceDelta.forEach((delta, accId) => {
            const ref = peAccRefs.get(accId);
            const cur = peAccSnaps.get(accId);
            if (ref && cur) tx.update(ref, { currentBalance: cur.currentBalance + delta });
          });
        });
      } else {
        await createPurchaseInvoice({ db, business: business!, user: user!, settings, header, items, addlExpenses, gst: indian ? gstPcts : undefined });
      }
      // New Size / Sub Size / Color / Clarity typed on loose lines join their
      // saved lists. The invoice is already saved, so a failure here is ignored.
      if (business) {
        const loose = items.filter(it => it.itemType === 'loose');
        rememberLooseOptions(db, business.id, [
          { field: 'looseSizeOptions',    offered: looseSizes,     stored: settings?.looseSizeOptions,    values: loose.map(it => it.size) },
          { field: 'looseSubSizeOptions', offered: looseSubSizes,  stored: settings?.looseSubSizeOptions, values: loose.map(it => it.subSize) },
          { field: 'looseColorOptions',   offered: looseColors,    stored: settings?.looseColorOptions,   values: loose.map(it => it.color) },
          { field: 'looseClarityOptions', offered: looseClarities, stored: settings?.looseClarityOptions, values: loose.map(it => it.clarity) },
        ]).catch(() => {});
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save invoice.');
    }
    setSaving(false);
  }

  const totals = useMemo(() => ({
    amt:  items.reduce((s, it) => s + calcRow(it).amount,  0),
    cost: items.reduce((s, it) => s + calcRow(it).costAmt, 0),
    ct:   items.reduce((s, it) => s + num(it.carat), 0),
  }), [items]);

  const looseItems = useMemo(() => items.filter(it => it.itemType === 'loose'),     [items]);
  const certItems  = useMemo(() => items.filter(it => it.itemType === 'certified'), [items]);
  const missCell = (it: BatchItem, f: BatchReqField) =>
    (showAllMissing || batchRowStarted(it)) && batchCellMissing(it, f);
  const missBg = (it: BatchItem, f: BatchReqField): React.CSSProperties =>
    missCell(it, f) ? { background: IMPORT_MISSING_BG } : {};
  const toFix = (list: BatchItem[], t: RowType) =>
    list.reduce((n, it) => n + BATCH_REQUIRED[t].filter(f => missCell(it, f)).length, 0);
  const nCertRows  = certItems.length;
  const nLooseRows = looseItems.length;
  const typeLabel  = nCertRows > 0 && nLooseRows > 0
    ? 'Mixed — Certified + Loose'
    : nLooseRows > 0 ? 'Polished Loose' : 'Polished Certified';

  // Loose grid's keyboard nav column order (interactive cells only — Category
  // and the remove button stay mouse/Tab-only, see useGridKeyNav.ts):
  // Code · SKU · Description · Shape · Size · Sub Size · Carat · Price/ct ·
  // Color · Clarity · Terms · Added Value · Broker1 · Broker1% · Broker2 · Broker2%
  const LOOSE_NAV_COLS = 16 + LX;
  const looseNav = useGridKeyNav(looseItems.length, LOOSE_NAV_COLS);

  // Certified grid's keyboard nav column order (interactive cells only):
  // Code · Description · Lab · Cert No · Shape · Carat · List Price/ct ·
  // Discount % · Price/ct · Terms · Added Value · Broker1 · Broker1% ·
  // Broker2 · Broker2% · Color · Clarity · Cut · Polish · Symmetry ·
  // Fluorescence · L · W · D
  const CERT_NAV_COLS = 24 + LX;
  const certNav = useGridKeyNav(certItems.length, CERT_NAV_COLS);

  // Same "identity in the title, category as an eyebrow" header used by the
  // read-only invoice preview — once a supplier's picked, the modal reads as
  // "that supplier's invoice" instead of a generic form.
  const companyName = entities.find(e => e.id === header.fromEntityId)?.companyName;

  // Escape, the backdrop, the × button, and Cancel all funnel through this
  // instead of calling onClose directly — with unsaved edits, closing asks
  // first (Discard / keep editing) rather than silently throwing the form
  // away, which is exactly what a stray Escape press used to do.
  function requestClose() {
    if (dirty) setConfirmDiscard(true);
    else onClose();
  }

  return (
    <>
    <Modal
      open={open}
      onClose={requestClose}
      hideHead
      size="full"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="secondary" onClick={requestClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {isEdit ? 'Save Changes' : `Save Invoice (${items.length} item${items.length !== 1 ? 's' : ''})`}
            </Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={companyName || (isEdit ? 'Edit Purchase Invoice' : 'Add Purchase Invoice')}
        eyebrow={!isEdit && importedFrom ? `Imported · ${importedFrom}` : companyName ? (isEdit ? 'Edit Purchase Invoice' : 'New Purchase Invoice') : typeLabel}
        right={isEdit && editInvoice ? (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
              Lot No
            </div>
            <LotPill>{lotNoDisplay(editInvoice.invoiceNo)}</LotPill>
          </div>
        ) : undefined}
        onClose={requestClose}
      />

      {/* ── Invoice header ── */}
      <div className="form-section-label" style={{ marginBottom: 8 }}>Invoice Header</div>
      <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 20 }}>
        <div className="form-table-compact" style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1.3 1 220px', minWidth: 200 }}>
              <FormField label="Company Name" required>
                <EntityCombobox
                  entities={justCreatedEntity && !entities.some(e => e.id === justCreatedEntity.id)
                    ? [...entities, justCreatedEntity]
                    : entities}
                  value={header.fromEntityId}
                  onChange={v => {
                    setH('fromEntityId', v);
                    // Pre-fill Payment Terms/Due Date from the supplier's own
                    // Default Terms (set on their Entity record) — same
                    // "pre-fills the invoice" convention as Sales.tsx.
                    const ent = entities.find(e => e.id === v);
                    if (ent?.defaultTerms) {
                      setH('terms', ent.defaultTerms);
                      setH('dueDate', addDaysToDate(header.purchaseDate, ent.defaultTerms));
                    }
                    // Indian: CGST + SGST in our state, IGST for another state.
                    if (indian && !isEdit && ent) setGstIn(gstInputFrom(defaultGst(settings, (ent.billTo ?? ent.shipTo)?.state)));
                  }}
                  placeholder="Select entity…"
                  createLabel="+ Create New Entity"
                  onCreateNew={() => setCreateEntityOpen(true)}
                />
              </FormField>
            </div>
            <div style={{ width: 100, flexShrink: 0 }}>
              <FormField label="Supplier Inv. No.">
                <input value={header.supplierInvoiceNo} onChange={e => setH('supplierInvoiceNo', e.target.value)}
                  placeholder="001" style={{ fontFamily: 'var(--font-num)' }} />
              </FormField>
            </div>
            <div style={{ width: 128, flexShrink: 0 }}>
              <FormField label="Purchase Date" required>
                <input type="date" value={header.purchaseDate}
                  onChange={e => { const pd = e.target.value; setH('purchaseDate', pd); setH('dueDate', addDaysToDate(pd, header.terms)); }} />
              </FormField>
            </div>
            <div style={{ width: 128, flexShrink: 0 }}>
              <FormField label="Due Date">
                <input type="date" value={header.dueDate} onChange={e => setH('dueDate', e.target.value)} />
              </FormField>
            </div>
            <div style={{ flexShrink: 0 }}>
              <FormField label="Payment Terms">
                <TermsSelector value={header.terms} onChange={v => { setH('terms', v); setH('dueDate', addDaysToDate(header.purchaseDate, v)); }} />
              </FormField>
            </div>
            <div style={{ flexShrink: 0 }}>
              <FormField label="Due Date Mode">
                <div style={{ display: 'flex', gap: 6 }}>
                  {([
                    { key: 'average',  label: 'Avg. due date' },
                    { key: 'separate', label: 'Separate' },
                  ] as { key: DueDateMode; label: string }[]).map(({ key, label }) => (
                    <button key={key} type="button" className={`filter-chip${header.dueDateMode === key ? ' active' : ''}`}
                      onClick={() => setH('dueDateMode', key)}>{label}</button>
                  ))}
                </div>
              </FormField>
            </div>
            <div style={{ flex: '1 1 160px', minWidth: 140 }}>
              <FormField label="Notes (optional)">
                <input value={header.notes} onChange={e => setH('notes', e.target.value)}
                  placeholder="Any notes…" />
              </FormField>
            </div>
          </div>
        </div>
      </div>

      {/* ── Loose / Certified Line Items — tab-switched Excel-style grids ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <PageTabs
          className="purchase-items-tabs"
          tabs={[
            { key: 'loose',     label: `Loose Polished Line Items (${nLooseRows})${toFix(looseItems, 'loose') ? ` · ${toFix(looseItems, 'loose')} to fix` : ''}` },
            { key: 'certified', label: `Certified Polished Line Items (${nCertRows})${toFix(certItems, 'certified') ? ` · ${toFix(certItems, 'certified')} to fix` : ''}` },
          ]}
          active={itemsTab}
          onChange={k => setItemsTab(k as 'loose' | 'certified')}
        />
        {!isEdit && (
          <p className="form-hint" style={{ margin: 0, whiteSpace: 'nowrap' }}>Invoice number assigned on save</p>
        )}
      </div>

      {itemsTab === 'loose' && (
      <>
      {/* One bordered unit with a genuinely FIXED height (not maxHeight) —
          so the modal's size stays constant whether there are 0 rows or a
          full page of them, instead of growing/shrinking as rows are
          added/removed. The Add Row control is a real last row INSIDE the
          table's own <tbody> — same shape as Sales.tsx's own CodeEntryRow:
          it just sits right after the last committed row and shifts down
          with the list as rows are added, no separate floating bar and no
          sticky positioning. Purely a layout change — the add-row click
          still calls the exact same addItem/focusCell as before. */}
      <div style={{
        // Flat bottom, no gap: the totals bar below joins onto it.
        border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm) var(--r-sm) 0 0',
        overflow: 'auto', height: EXCEL_GRID_MAX_HEIGHT, marginBottom: 0,
        // Without this, once the grid's own small scroll area reaches its
        // bottom a further scroll "chains" into the modal body behind it —
        // the whole section jumps/scrolls away instead of stopping cleanly
        // on the last row, which is what made the last item hard to see.
        // This confines scrolling to the grid itself.
        overscrollBehavior: 'contain',
      }}>
          <table className="excel-grid-table">
            <thead>
              <tr>
                <th style={{ ...GTH }}>#</th>
                <th style={{ ...GTH }}>Cat.</th>
                <th style={{ ...GTH, minWidth: 55 }}>Code</th>
                <th style={{ ...GTH, minWidth: 100 }}>SKU</th>
                <th style={{ ...GTH, minWidth: 160 }}>Description</th>
                <th style={{ ...GTH, minWidth: 90 }}>Shape</th>
                <th style={{ ...GTH, minWidth: 80 }}>Size</th>
                <th style={{ ...GTH }}>Sub Size</th>
                <th style={{ ...GTH, minWidth: 80 }}>Carat</th>
                <th style={{ ...GTH }} title={indian ? 'Agreed price per ct, before Less 1 % and Less 2 %' : 'What we pay per ct'}>Cost/ct</th>
                {indian && <>
                  <th style={{ ...GTH }} title="First discount on Cost/ct">Less 1 %</th>
                  <th style={{ ...GTH }} title="Second discount, taken after Less 1 %">Less 2 %</th>
                </>}
                <th style={{ ...GTH }}>Color</th>
                <th style={{ ...GTH }}>Clarity</th>
                <th style={{ ...GTH }}>Terms</th>
                <th style={{ ...GTH }}>Added Value</th>
                <th style={{ ...GTH }}>Total Amount</th>
                <th style={{ ...GTH }} title="Cost/ct plus Added Value %">Price/ct</th>
                <th style={{ ...GTH, minWidth: 130 }}>Broker1</th>
                <th style={{ ...GTH }}>Broker1%</th>
                <th style={{ ...GTH, minWidth: 130 }}>Broker2</th>
                <th style={{ ...GTH }}>Broker2%</th>
                <th className="excel-grid-remove-col" style={{ ...GTH }} />
              </tr>
            </thead>
            <tbody>
              {looseItems.map((it, idx) => {
                const calc = calcBatchLoose(it);
                const dueTitle = header.dueDateMode === 'separate'
                  ? `Due ${new Date(lineDueDate(header.purchaseDate, header.terms, num(it.terms) || undefined) + 'T00:00:00')
                      .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                  : undefined;
                return (
                  <tr key={it._key}>
                    <td style={GTD_TEXT}>{idx + 1}</td>
                    <td style={GTD_TEXT}>
                      <CategoryPicker value={it.category} onChange={v => updateItem(it._key, 'category', v)} />
                    </td>
                    <td style={GTD}>
                      <input ref={looseNav.cellRef(idx, 0)} onKeyDown={looseNav.onCellKeyDown(idx, 0)}
                        className="grid-cell" placeholder={isDummyPurchaseLine(it) ? 'Dummy' : 'Auto'}
                        disabled={isDummyPurchaseLine(it)} title={isDummyPurchaseLine(it) ? DUMMY_ROW_HINT : undefined}
                        value={isDummyPurchaseLine(it) ? '' : it.code} onChange={e => updateItem(it._key, 'code', e.target.value)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 100, ...missBg(it, 'sku') }}>
                      <input ref={looseNav.cellRef(idx, 1)} onKeyDown={looseNav.onCellKeyDown(idx, 1)}
                        className="grid-cell" placeholder={isDummyPurchaseLine(it) ? '—' : 'JEW1'}
                        disabled={isDummyPurchaseLine(it)} title={isDummyPurchaseLine(it) ? DUMMY_ROW_HINT : undefined}
                        value={isDummyPurchaseLine(it) ? '' : it.sku} onChange={e => updateItem(it._key, 'sku', e.target.value)} />
                      {CLARITIES.includes(it.sku.trim().toUpperCase() as DiamondClarity) && (
                        <div style={{ fontSize: 9.5, color: 'var(--danger)', padding: '0 8px 4px', whiteSpace: 'nowrap' }}>Looks like a Clarity grade</div>
                      )}
                    </td>
                    <td style={{ ...GTD, minWidth: 160 }}>
                      <DescriptionCombobox
                        className="grid-cell"
                        cellRef={looseNav.cellRef(idx, 2)} onKeyDown={looseNav.onCellKeyDown(idx, 2)}
                        value={it.description} onChange={v => updateItem(it._key, 'description', v)}
                        options={settings?.saleLineDescriptions ?? []}
                      />
                    </td>
                    <td style={{ ...GTD, minWidth: 90, ...missBg(it, 'shape') }}>
                      <OptionCombobox cellRef={looseNav.cellRef(idx, 3)} onKeyDown={looseNav.onCellKeyDown(idx, 3)}
                        className="grid-cell" placeholder="Shape" options={SHAPE_OPTIONS} panelMinWidth={130}
                        value={it.shape} onChange={v => updateItem(it._key, 'shape', v)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 80 }}>
                      <OptionCombobox cellRef={looseNav.cellRef(idx, 4)} onKeyDown={looseNav.onCellKeyDown(idx, 4)}
                        className="grid-cell" placeholder="+2" options={looseSizes}
                        value={it.size} onChange={v => updateItem(it._key, 'size', v)} />
                    </td>
                    <td style={GTD}>
                      <OptionCombobox cellRef={looseNav.cellRef(idx, 5)} onKeyDown={looseNav.onCellKeyDown(idx, 5)}
                        className="grid-cell" placeholder="+1.5" options={looseSubSizes}
                        value={it.subSize} onChange={v => updateItem(it._key, 'subSize', v)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 80, ...missBg(it, 'carat') }}>
                      <input ref={looseNav.cellRef(idx, 6)} onKeyDown={looseNav.onCellKeyDown(idx, 6)}
                        className="grid-cell no-spinner" type="number" step="0.01" min="0" placeholder="0.00"
                        value={it.carat} onChange={e => updateItem(it._key, 'carat', e.target.value)} />
                    </td>
                    <td style={{ ...GTD, ...missBg(it, 'pricePerCt') }}>
                      <input ref={looseNav.cellRef(idx, 7)} onKeyDown={looseNav.onCellKeyDown(idx, 7)}
                        className="grid-cell no-spinner" type="number" step="0.01" min="0" placeholder="0.00"
                        value={it.pricePerCt} onChange={e => updateItem(it._key, 'pricePerCt', e.target.value)} />
                    </td>
                    {indian && (['less1Percent', 'less2Percent'] as const).map((k, ki) => (
                      <td key={k} style={GTD}>
                        <input ref={looseNav.cellRef(idx, 8 + ki)} onKeyDown={looseNav.onCellKeyDown(idx, 8 + ki)}
                          className="grid-cell no-spinner" type="number" step="0.01" min="0" max="99" placeholder="0"
                          value={it[k]} onChange={e => updateItem(it._key, k, e.target.value)} />
                      </td>
                    ))}
                    <td style={{ ...GTD, ...missBg(it, 'color') }}>
                      <OptionCombobox cellRef={looseNav.cellRef(idx, 8 + LX)} onKeyDown={looseNav.onCellKeyDown(idx, 8 + LX)}
                        className="grid-cell" placeholder="Col" options={looseColors} panelMinWidth={110}
                        value={it.color} onChange={v => updateItem(it._key, 'color', v)} />
                    </td>
                    <td style={{ ...GTD, ...missBg(it, 'clarity') }}>
                      <OptionCombobox cellRef={looseNav.cellRef(idx, 9 + LX)} onKeyDown={looseNav.onCellKeyDown(idx, 9 + LX)}
                        className="grid-cell" placeholder="Clr" options={looseClarities} panelMinWidth={110}
                        value={it.clarity} onChange={v => updateItem(it._key, 'clarity', v)} />
                    </td>
                    <td style={GTD}>
                      <input ref={looseNav.cellRef(idx, 10 + LX)} onKeyDown={looseNav.onCellKeyDown(idx, 10 + LX)}
                        className="grid-cell no-spinner" type="number" step="1" min="0" placeholder={`${header.terms}`}
                        title={dueTitle}
                        value={it.terms} onChange={e => updateItem(it._key, 'terms', e.target.value)} />
                    </td>
                    <td style={GTD}>
                      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <input ref={looseNav.cellRef(idx, 11 + LX)} onKeyDown={looseNav.onCellKeyDown(idx, 11 + LX)}
                          className="grid-cell no-spinner" type="number" step="0.1" min="0" placeholder="3"
                          style={{ paddingRight: 16, textAlign: 'right' }}
                          value={it.addedValuePct} onChange={e => updateItem(it._key, 'addedValuePct', e.target.value)} />
                        <span style={{
                          position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                          fontSize: 10, color: 'var(--text-3)', pointerEvents: 'none',
                        }}>%</span>
                      </div>
                    </td>
                    <td style={{ ...GTD_TEXT, fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{calc.amount > 0 ? money(calc.amount) : '—'}</td>
                    <td style={{ ...GTD_TEXT, fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{calc.costPerCt > 0 ? money(calc.costPerCt) : '—'}</td>
                    <td style={{ ...GTD, minWidth: 130 }}>
                      <EntityCombobox
                        inputClassName="grid-cell"
                        cellRef={looseNav.cellRef(idx, 12 + LX)} onKeyDown={looseNav.onCellKeyDown(idx, 12 + LX)}
                        entities={brokers} value={it.broker1EntityId}
                        onChange={v => { updateItem(it._key, 'broker1EntityId', v); if (!v) updateItem(it._key, 'broker1CommissionPercent', ''); }}
                        placeholder="Broker 1" clearLabel="No broker"
                      />
                    </td>
                    <td style={GTD}>
                      <input ref={looseNav.cellRef(idx, 13 + LX)} onKeyDown={looseNav.onCellKeyDown(idx, 13 + LX)}
                        className="grid-cell no-spinner" type="number" step="0.1" min="0" max="100" placeholder="0"
                        disabled={!it.broker1EntityId}
                        value={it.broker1CommissionPercent} onChange={e => updateItem(it._key, 'broker1CommissionPercent', e.target.value)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 130 }}>
                      <EntityCombobox
                        inputClassName="grid-cell"
                        cellRef={looseNav.cellRef(idx, 14 + LX)} onKeyDown={looseNav.onCellKeyDown(idx, 14 + LX)}
                        entities={brokers} value={it.broker2EntityId}
                        onChange={v => { updateItem(it._key, 'broker2EntityId', v); if (!v) updateItem(it._key, 'broker2CommissionPercent', ''); }}
                        placeholder="Broker 2" clearLabel="No broker"
                      />
                    </td>
                    <td style={GTD}>
                      <input ref={looseNav.cellRef(idx, 15 + LX)} onKeyDown={looseNav.onCellKeyDown(idx, 15 + LX)}
                        className="grid-cell no-spinner" type="number" step="0.1" min="0" max="100" placeholder="0"
                        disabled={!it.broker2EntityId}
                        value={it.broker2CommissionPercent} onChange={e => updateItem(it._key, 'broker2CommissionPercent', e.target.value)} />
                    </td>
                    <td className="excel-grid-remove-col" style={{ ...GTD_TEXT, textAlign: 'center' }}>
                      <button type="button" className="line-item-remove" onClick={() => removeItem(it._key)} title="Remove row">
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {/* Add Row — a normal last row, not sticky/pinned, same shape
                  as Sales.tsx's own CodeEntryRow: it just sits right after
                  the last committed row and shifts down with the list as
                  rows are added, no separate floating bar. */}
              <tr>
                <td colSpan={100} style={{ padding: 0, background: 'var(--primary-light)' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const newIdx = looseItems.length;
                      addItem('loose');
                      setTimeout(() => looseNav.focusCell(newIdx, 0), 0);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                      height: EXCEL_GRID_ADD_ROW_H, padding: '0 12px',
                      fontSize: 12, fontWeight: 700, color: 'var(--primary)',
                      background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <IconPlus /> Add Loose Row
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
      </div>
      </>
      )}

      {itemsTab === 'certified' && (
      <>
      {fetchNotice && (
        <div className="msg msg-note" style={{ marginBottom: 8 }}>
          <span className="msg-note-icon"><IconNoteInfo /></span>
          <span style={{ flex: 1 }}>{fetchNotice}</span>
          <button type="button" className="line-item-link-btn" onClick={() => setFetchNotice('')}>Dismiss</button>
        </div>
      )}
      {/* Same fixed-height grid with a normal last-row Add Row control as
          the Loose grid above — see the comment there. Purely a layout
          change. */}
      <div style={{
        // Flat bottom, no gap: the totals bar below joins onto it.
        border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm) var(--r-sm) 0 0',
        overflow: 'auto', height: EXCEL_GRID_MAX_HEIGHT, marginBottom: 0,
        overscrollBehavior: 'contain',
      }}>
          <table className="excel-grid-table">
            <thead>
              <tr>
                <th style={{ ...GTH }}>#</th>
                <th style={{ ...GTH }}>Cat.</th>
                <th style={{ ...GTH, minWidth: 55 }}>Code</th>
                <th style={{ ...GTH, minWidth: 160 }}>Description</th>
                <th style={{ ...GTH, minWidth: 80 }}>Lab</th>
                <th style={{ ...GTH, minWidth: 130 }}>Cert No</th>
                <th style={{ ...GTH, minWidth: 90 }}>Shape</th>
                <th style={{ ...GTH, minWidth: 80 }}>Carat</th>
                <th style={{ ...GTH }}>List Price/ct</th>
                <th style={{ ...GTH }}>Discount %</th>
                <th style={{ ...GTH }} title={indian ? 'Agreed price per ct, before Less 1 % and Less 2 %' : 'What we pay per ct'}>Cost/ct</th>
                {indian && <>
                  <th style={{ ...GTH }} title="First discount on Cost/ct">Less 1 %</th>
                  <th style={{ ...GTH }} title="Second discount, taken after Less 1 %">Less 2 %</th>
                </>}
                <th style={{ ...GTH }}>Total Amount</th>
                <th style={{ ...GTH }}>Terms</th>
                <th style={{ ...GTH }}>Added Value</th>
                <th style={{ ...GTH }} title="Cost/ct plus Added Value %">Price/ct</th>
                <th style={{ ...GTH, minWidth: 130 }}>Broker1</th>
                <th style={{ ...GTH }}>Broker1%</th>
                <th style={{ ...GTH, minWidth: 130 }}>Broker2</th>
                <th style={{ ...GTH }}>Broker2%</th>
                <th style={{ ...GTH }}>Color</th>
                <th style={{ ...GTH }}>Clarity</th>
                <th style={{ ...GTH, minWidth: 90 }}>Cut</th>
                <th style={{ ...GTH, minWidth: 90 }}>Polish</th>
                <th style={{ ...GTH, minWidth: 90 }}>Symmetry</th>
                <th style={{ ...GTH }}>Fluorescence</th>
                <th style={{ ...GTH, minWidth: 130 }}>Measurement</th>
                <th className="excel-grid-remove-col" style={{ ...GTH }} />
              </tr>
            </thead>
            <tbody>
              {certItems.map((it, idx) => {
                const calc = calcBatchCert(it);
                const isNoLab = it.lab === 'None';
                const dueTitle = header.dueDateMode === 'separate'
                  ? `Due ${new Date(lineDueDate(header.purchaseDate, header.terms, num(it.terms) || undefined) + 'T00:00:00')
                      .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                  : undefined;
                return (
                  <tr key={it._key}>
                    <td style={GTD_TEXT}>{idx + 1}</td>
                    <td style={GTD_TEXT}>
                      <CategoryPicker value={it.category} onChange={v => updateItem(it._key, 'category', v)} />
                    </td>
                    <td style={GTD}>
                      <input ref={certNav.cellRef(idx, 0)} onKeyDown={certNav.onCellKeyDown(idx, 0)}
                        className="grid-cell" placeholder={isDummyPurchaseLine(it) ? 'Dummy' : 'Auto'}
                        disabled={isDummyPurchaseLine(it)} title={isDummyPurchaseLine(it) ? DUMMY_ROW_HINT : undefined}
                        value={isDummyPurchaseLine(it) ? '' : it.code} onChange={e => updateItem(it._key, 'code', e.target.value)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 160 }}>
                      <DescriptionCombobox
                        className="grid-cell"
                        cellRef={certNav.cellRef(idx, 1)} onKeyDown={certNav.onCellKeyDown(idx, 1)}
                        value={it.description} onChange={v => updateItem(it._key, 'description', v)}
                        options={settings?.saleLineDescriptions ?? []}
                      />
                    </td>
                    <td style={{ ...GTD, ...missBg(it, 'lab') }}>
                      <OptionCombobox cellRef={certNav.cellRef(idx, 2)} onKeyDown={certNav.onCellKeyDown(idx, 2)}
                        className="grid-cell" placeholder="Lab" options={LAB_OPTIONS} panelMinWidth={110}
                        value={it.lab} onChange={v => updateItem(it._key, 'lab', v as typeof it.lab)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 130, ...missBg(it, 'certificateNo') }}>
                      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <input ref={certNav.cellRef(idx, 3)} onKeyDown={certNav.onCellKeyDown(idx, 3)}
                          className="grid-cell" style={{ paddingRight: 40, fontFamily: 'var(--font-num)' }}
                          placeholder={isNoLab ? 'n/a' : 'Cert No'} disabled={isNoLab}
                          value={it.certificateNo} onChange={e => updateItem(it._key, 'certificateNo', e.target.value)} />
                        <button
                          type="button" disabled={isNoLab}
                          title="Fetch certificate details from GIA/HRD (coming soon)"
                          onClick={() => fetchCertificate(it._key)}
                          style={{
                            position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                            fontSize: 9.5, fontWeight: 700,
                            color: isNoLab ? 'var(--text-3)' : 'var(--primary)',
                            background: 'none', border: 'none', padding: 2,
                            cursor: isNoLab ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Fetch
                        </button>
                      </div>
                    </td>
                    <td style={{ ...GTD, minWidth: 90, ...missBg(it, 'shape') }}>
                      <OptionCombobox cellRef={certNav.cellRef(idx, 4)} onKeyDown={certNav.onCellKeyDown(idx, 4)}
                        className="grid-cell" placeholder="Shape" options={SHAPE_OPTIONS} panelMinWidth={130}
                        value={it.shape} onChange={v => updateItem(it._key, 'shape', v)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 80, ...missBg(it, 'carat') }}>
                      <input ref={certNav.cellRef(idx, 5)} onKeyDown={certNav.onCellKeyDown(idx, 5)}
                        className="grid-cell no-spinner" type="number" step="0.01" min="0" placeholder="0.00"
                        value={it.carat} onChange={e => updateItem(it._key, 'carat', e.target.value)} />
                    </td>
                    <td style={GTD}>
                      <input ref={certNav.cellRef(idx, 6)} onKeyDown={certNav.onCellKeyDown(idx, 6)}
                        className="grid-cell no-spinner" type="number" step="0.01" min="0" placeholder="0.00"
                        disabled={isNoLab}
                        value={it.listPriceAmount} onChange={e => updateCertListPrice(it._key, e.target.value)} />
                    </td>
                    <td style={GTD}>
                      <input ref={certNav.cellRef(idx, 7)} onKeyDown={certNav.onCellKeyDown(idx, 7)}
                        className="grid-cell no-spinner" type="number" step="0.1" placeholder="−30"
                        disabled={isNoLab}
                        value={it.priceListPercent} onChange={e => updateCertDiscount(it._key, e.target.value)} />
                    </td>
                    <td style={{ ...GTD, ...missBg(it, 'pricePerCt') }}>
                      <input ref={certNav.cellRef(idx, 8)} onKeyDown={certNav.onCellKeyDown(idx, 8)}
                        className="grid-cell no-spinner" type="number" step="0.01" min="0" placeholder="0.00"
                        value={it.pricePerCt} onChange={e => updateCertPrice(it._key, e.target.value)} />
                    </td>
                    {indian && (['less1Percent', 'less2Percent'] as const).map((k, ki) => (
                      <td key={k} style={GTD}>
                        <input ref={certNav.cellRef(idx, 9 + ki)} onKeyDown={certNav.onCellKeyDown(idx, 9 + ki)}
                          className="grid-cell no-spinner" type="number" step="0.01" min="0" max="99" placeholder="0"
                          value={it[k]} onChange={e => updateItem(it._key, k, e.target.value)} />
                      </td>
                    ))}
                    <td style={{ ...GTD_TEXT, fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{calc.amount > 0 ? money(calc.amount) : '—'}</td>
                    <td style={GTD}>
                      <input ref={certNav.cellRef(idx, 9 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 9 + LX)}
                        className="grid-cell no-spinner" type="number" step="1" min="0" placeholder={`${header.terms}`}
                        title={dueTitle}
                        value={it.terms} onChange={e => updateItem(it._key, 'terms', e.target.value)} />
                    </td>
                    <td style={GTD}>
                      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <input ref={certNav.cellRef(idx, 10 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 10 + LX)}
                          className="grid-cell no-spinner" type="number" step="0.1" min="0" placeholder="3"
                          style={{ paddingRight: 16, textAlign: 'right' }}
                          value={it.addedValuePct} onChange={e => updateItem(it._key, 'addedValuePct', e.target.value)} />
                        <span style={{
                          position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                          fontSize: 10, color: 'var(--text-3)', pointerEvents: 'none',
                        }}>%</span>
                      </div>
                    </td>
                    <td style={{ ...GTD_TEXT, fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{calc.costPerCt > 0 ? money(calc.costPerCt) : '—'}</td>
                    <td style={{ ...GTD, minWidth: 130 }}>
                      <EntityCombobox
                        inputClassName="grid-cell"
                        cellRef={certNav.cellRef(idx, 11 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 11 + LX)}
                        entities={brokers} value={it.broker1EntityId}
                        onChange={v => { updateItem(it._key, 'broker1EntityId', v); if (!v) updateItem(it._key, 'broker1CommissionPercent', ''); }}
                        placeholder="Broker 1" clearLabel="No broker"
                      />
                    </td>
                    <td style={GTD}>
                      <input ref={certNav.cellRef(idx, 12 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 12 + LX)}
                        className="grid-cell no-spinner" type="number" step="0.1" min="0" max="100" placeholder="0"
                        disabled={!it.broker1EntityId}
                        value={it.broker1CommissionPercent} onChange={e => updateItem(it._key, 'broker1CommissionPercent', e.target.value)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 130 }}>
                      <EntityCombobox
                        inputClassName="grid-cell"
                        cellRef={certNav.cellRef(idx, 13 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 13 + LX)}
                        entities={brokers} value={it.broker2EntityId}
                        onChange={v => { updateItem(it._key, 'broker2EntityId', v); if (!v) updateItem(it._key, 'broker2CommissionPercent', ''); }}
                        placeholder="Broker 2" clearLabel="No broker"
                      />
                    </td>
                    <td style={GTD}>
                      <input ref={certNav.cellRef(idx, 14 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 14 + LX)}
                        className="grid-cell no-spinner" type="number" step="0.1" min="0" max="100" placeholder="0"
                        disabled={!it.broker2EntityId}
                        value={it.broker2CommissionPercent} onChange={e => updateItem(it._key, 'broker2CommissionPercent', e.target.value)} />
                    </td>
                    <td style={{ ...GTD, ...missBg(it, 'color') }}>
                      <OptionCombobox cellRef={certNav.cellRef(idx, 15 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 15 + LX)}
                        className="grid-cell" placeholder="Col" options={CERT_COLOR_OPTIONS} panelMinWidth={100}
                        value={it.color} onChange={v => updateItem(it._key, 'color', v as typeof it.color)} />
                    </td>
                    <td style={{ ...GTD, ...missBg(it, 'clarity') }}>
                      <OptionCombobox cellRef={certNav.cellRef(idx, 16 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 16 + LX)}
                        className="grid-cell" placeholder="Clr" options={CERT_CLARITY_OPTIONS} panelMinWidth={100}
                        value={it.clarity} onChange={v => updateItem(it._key, 'clarity', v as typeof it.clarity)} />
                    </td>
                    <td style={GTD}>
                      <OptionCombobox cellRef={certNav.cellRef(idx, 17 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 17 + LX)}
                        className="grid-cell" placeholder="Cut" options={GRADE_OPTIONS} panelMinWidth={120}
                        value={it.cut} onChange={v => updateItem(it._key, 'cut', v as typeof it.cut)} />
                    </td>
                    <td style={GTD}>
                      <OptionCombobox cellRef={certNav.cellRef(idx, 18 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 18 + LX)}
                        className="grid-cell" placeholder="Polish" options={GRADE_OPTIONS} panelMinWidth={120}
                        value={it.polish} onChange={v => updateItem(it._key, 'polish', v as typeof it.polish)} />
                    </td>
                    <td style={GTD}>
                      <OptionCombobox cellRef={certNav.cellRef(idx, 19 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 19 + LX)}
                        className="grid-cell" placeholder="Symmetry" options={GRADE_OPTIONS} panelMinWidth={120}
                        value={it.symmetry} onChange={v => updateItem(it._key, 'symmetry', v as typeof it.symmetry)} />
                    </td>
                    <td style={GTD}>
                      <OptionCombobox cellRef={certNav.cellRef(idx, 20 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 20 + LX)}
                        className="grid-cell" placeholder="Fluor." options={FLUOR_OPTIONS} panelMinWidth={150}
                        value={it.fluorescence} onChange={v => updateItem(it._key, 'fluorescence', v as typeof it.fluorescence)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 130 }}>
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <input ref={certNav.cellRef(idx, 21 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 21 + LX)}
                          className="grid-cell" style={{ textAlign: 'center', padding: '6px 2px' }} placeholder="L"
                          value={it.measLength} onChange={e => updateItem(it._key, 'measLength', e.target.value)} />
                        <span style={{ color: 'var(--text-3)', fontSize: 10 }}>-</span>
                        <input ref={certNav.cellRef(idx, 22 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 22 + LX)}
                          className="grid-cell" style={{ textAlign: 'center', padding: '6px 2px' }} placeholder="W"
                          value={it.measWidth} onChange={e => updateItem(it._key, 'measWidth', e.target.value)} />
                        <span style={{ color: 'var(--text-3)', fontSize: 10 }}>x</span>
                        <input ref={certNav.cellRef(idx, 23 + LX)} onKeyDown={certNav.onCellKeyDown(idx, 23 + LX)}
                          className="grid-cell" style={{ textAlign: 'center', padding: '6px 2px' }} placeholder="D"
                          value={it.measDepth} onChange={e => updateItem(it._key, 'measDepth', e.target.value)} />
                      </div>
                    </td>
                    <td className="excel-grid-remove-col" style={{ ...GTD_TEXT, textAlign: 'center' }}>
                      <button type="button" className="line-item-remove" onClick={() => removeItem(it._key)} title="Remove row">
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {/* Add Row — same normal-last-row shape as the Loose grid
                  above; see the comment there. */}
              <tr>
                <td colSpan={100} style={{ padding: 0, background: 'var(--primary-light)' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const newIdx = certItems.length;
                      addItem('certified');
                      setTimeout(() => certNav.focusCell(newIdx, 0), 0);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                      height: EXCEL_GRID_ADD_ROW_H, padding: '0 12px',
                      fontSize: 12, fontWeight: 700, color: 'var(--primary)',
                      background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <IconPlus /> Add Certified Row
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
      </div>
      </>
      )}

      {/* ── Line items totals: right under the grid, above Additional
          Details (moved here from the modal footer). Covers both tabs. ── */}
      {indian && <div className="purchase-gst-wrap"><GstBoxes value={gstIn} onChange={g => { setGstIn(g); setDirty(true); }} /></div>}
      <div className="purchase-items-totals">
        <TotalsFigures figures={[
          { label: 'Items', value: String(items.length) },
          { label: 'Carat', value: `${totals.ct.toFixed(2)} ct` },
          { label: 'Amount', value: money(totals.amt), strong: !indian },
          { label: 'Price', value: money(totals.cost), color: 'var(--primary)', title: 'Amount plus Added Value %' },
          ...(indian ? (() => {
            const t = invoiceGst(round2(totals.amt), gstPcts);
            const m = gstMode(gstPcts);
            return [
              ...(m === 'split' ? [{ label: 'CGST', value: money(t.cgst) }, { label: 'SGST', value: money(t.sgst) }] : []),
              ...(m === 'igst' ? [{ label: 'IGST', value: money(t.igst) }] : []),
              { label: 'Total Amount', value: money(round2(totals.amt + t.total)), strong: true, title: 'Amount plus GST: what we owe the supplier' },
            ];
          })() : []),
        ]} />
      </div>

      {/* ── Additional Details — Purchase Expenses tied to this invoice ──
          Same purchaseExpenses collection the standalone Purchase Expenses
          page writes to (see createPurchaseInvoice / the edit branch above)
          — this is just a second, one-save entry point into it. Always
          treatment:'expense'; "Capitalise into stock cost" stays a
          Purchase Expenses page-only option. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="form-section-label" style={{ marginBottom: 0 }}>
          Additional Details
          <span style={{ fontWeight: 400, color: 'var(--text-3)', marginLeft: 6 }}>
            — Freight, duty, certification and the like, tied to this invoice ({addlExpenses.length})
          </span>
        </div>
      </div>
      {addlExpenses.length > 0 && (
        <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'auto', marginBottom: 8 }}>
          <table className="excel-grid-table">
            <thead>
              <tr>
                <th style={{ ...GTH, minWidth: 130 }}>Category</th>
                <th style={{ ...GTH, minWidth: 160 }}>Description</th>
                <th style={{ ...GTH }}>Amount</th>
                <th style={{ ...GTH, minWidth: 128 }}>Date</th>
                <th style={{ ...GTH, minWidth: 130 }}>Vendor</th>
                <th style={{ ...GTH, textAlign: 'center' }}>Paid?</th>
                <th style={{ ...GTH, minWidth: 150 }}>Bank Account / Due Date</th>
                <th className="excel-grid-remove-col" style={{ ...GTH }} />
              </tr>
            </thead>
            <tbody>
              {addlExpenses.map(row => {
                const acct = bankAccounts.find(a => a.id === row.bankAccountId);
                return (
                  <tr key={row._key}>
                    <td style={GTD}>
                      <select className="grid-cell grid-cell-select" value={row.category}
                        onChange={e => updateAddlExpense(row._key, 'category', e.target.value)}>
                        {ADDL_EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td style={{ ...GTD, minWidth: 160 }}>
                      <input className="grid-cell" placeholder="e.g. DHL shipment from Antwerp"
                        value={row.description} onChange={e => updateAddlExpense(row._key, 'description', e.target.value)} />
                    </td>
                    <td style={GTD}>
                      <input className="grid-cell no-spinner" type="number" step="0.01" min="0.01" placeholder="0.00"
                        value={row.amount} onChange={e => updateAddlExpense(row._key, 'amount', e.target.value)} />
                    </td>
                    <td style={GTD}>
                      <input className="grid-cell" type="date" value={row.date}
                        onChange={e => updateAddlExpense(row._key, 'date', e.target.value)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 130 }}>
                      <EntityCombobox
                        inputClassName="grid-cell"
                        entities={entities.map(e => ({ id: e.id, companyName: e.companyName }))}
                        value={row.vendorEntityId}
                        onChange={v => updateAddlExpense(row._key, 'vendorEntityId', v)}
                        placeholder="No vendor" clearLabel="No vendor"
                      />
                    </td>
                    <td style={{ ...GTD_TEXT, textAlign: 'center' }}>
                      <input type="checkbox" checked={row.payNow}
                        onChange={e => updateAddlExpense(row._key, 'payNow', e.target.checked)} />
                    </td>
                    <td style={{ ...GTD, minWidth: 150 }}>
                      {row.payNow ? (
                        <select className="grid-cell grid-cell-select" value={row.bankAccountId}
                          onChange={e => updateAddlExpense(row._key, 'bankAccountId', e.target.value)}>
                          <option value="">Select account…</option>
                          {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
                        </select>
                      ) : (
                        <input className="grid-cell" type="date" placeholder="Due date"
                          value={row.dueDate} onChange={e => updateAddlExpense(row._key, 'dueDate', e.target.value)} />
                      )}
                      {row.payNow && !acct && <span style={{ fontSize: 9.5, color: 'var(--danger)', padding: '0 8px 4px', display: 'block' }}>Choose an account</span>}
                    </td>
                    <td className="excel-grid-remove-col" style={{ ...GTD_TEXT, textAlign: 'center' }}>
                      <button type="button" className="line-item-remove" onClick={() => removeAddlExpenseRow(row._key)} title="Remove">
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginBottom: 20, display: 'flex', gap: 8 }}>
        <Button variant="secondary" size="sm" icon={<IconPlus />} onClick={addAddlExpenseRow}>Add Expense</Button>
      </div>
    </Modal>

    <EntityFormModal
      open={createEntityOpen}
      onClose={() => setCreateEntityOpen(false)}
      defaultTypes={['supplier']}
      onSaved={newEntity => {
        setJustCreatedEntity(newEntity);
        setH('fromEntityId', newEntity.id);
        setCreateEntityOpen(false);
      }}
    />

    <ConfirmModal
      open={confirmDiscard}
      title="Discard changes?"
      message="This invoice has unsaved changes. Discard them and close, or keep editing?"
      confirmLabel="Discard"
      onConfirm={() => { setConfirmDiscard(false); onClose(); }}
      onClose={() => setConfirmDiscard(false)}
    />
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PurchasePage() {
  const { business, user } = useAuth();

  const [invoices,     setInvoices]    = useState<PurchaseInvoice[]>([]);
  const [entities,     setEntities]    = useState<Entity[]>([]);
  const [loading,      setLoading]     = useState(true);
  const [settings,     setSettings]    = useState<BusinessSettings | null>(null);
  const money = (v: number) => fmtMoney(v, settings?.currency ?? 'USD');
  // Indian: Amount and GST columns before Total Amount; Belgian never shows them.
  const indianList = isIndian(settings);
  const SHOWN_COLUMNS = indianList ? COLUMNS : COLUMNS.filter(c => !GST_COLUMN_KEYS.has(c.key));
  const [previewInvoice, setPreviewInvoice] = useState<PurchaseInvoice | null>(null);
  const [selectedIds,  setSelectedIds] = useState<Set<string>>(new Set());
  const [batchOpen,    setBatchOpen]   = useState(false);
  const [editInvoice,  setEditInvoice] = useState<PurchaseInvoice | null>(null);
  const [importOpen,   setImportOpen]  = useState(false);
  const [imported,     setImported]    = useState<{ items: BatchItem[]; fileName: string; header?: Partial<BatchHeader> } | null>(null);
  const [importedNos,  setImportedNos] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget]= useState<PurchaseInvoice | null>(null);
  const [deleteError,  setDeleteError] = useState('');
  const [deleting,     setDeleting]    = useState(false);
  const [payTarget,    setPayTarget]   = useState<PurchaseInvoice | null>(null);
  const [searchQ,      setSearchQ]     = useState('');
  // See TransactionCategory. 'real' hides invoices whose every line is
  // books-dummy (books-only, no physical stock); 'books' hides invoices
  // whose every line is cash-real (physical but off the books) — an
  // invoice with a mix of categories across its lines passes both views,
  // same "exclude, don't require-match" rule Inventory's own toggle uses.
  // Default 'real'.
  const { categoryFilter } = useCategoryFilter();
  const [dateFilter,   setDateFilter]  = useState<DateFilter>('3-months');
  const [customFrom,   setCustomFrom]  = useState('');
  const [customTo,     setCustomTo]    = useState('');
  const [currentPage,  setCurrentPage] = useState(1);
  const [sortBy,       setSortBy]      = useState<SortKey>('purchaseDate');
  const [sortDir,      setSortDir]     = useState<'asc' | 'desc'>('desc');
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>(loadVisibleColumns);

  useEffect(() => {
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  function toggleColumn(key: ColumnKey) {
    setVisibleColumns(v => ({ ...v, [key]: !v[key] }));
  }

  const dateRange = useMemo(
    () => computeDateRange(dateFilter, customFrom, customTo),
    [dateFilter, customFrom, customTo],
  );

  // ── Settings ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    return onSnapshot(
      doc(db, 'settings', business.id),
      snap => { if (snap.exists()) setSettings(withBusinessSystem(snap.data() as BusinessSettings, business)); },
      () => {},
    );
  }, [business]);

  // ── Firestore ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!dateRange) { setInvoices([]); setLoading(false); return; }
    const db = getClientDb();
    if (!db) return;
    setLoading(true);

    const u1 = onSnapshot(
      query(
        collection(db, 'invoices'),
        where('purchaseDate', '>=', dateRange.start),
        where('purchaseDate', '<',  dateRange.end),
        orderBy('purchaseDate', 'desc'),
      ),
      snap => { setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice))); setLoading(false); },
      () => setLoading(false),
    );
    const u2 = onSnapshot(
      query(collection(db, 'entities'), orderBy('companyName')),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))),
    );
    return () => { u1(); u2(); };
  }, [dateRange?.start, dateRange?.end]); // eslint-disable-line react-hooks/exhaustive-deps

  const fromEntities = useMemo(
    () => entities.filter(e => e.types.includes('supplier') || e.types.includes('broker')),
    [entities],
  );
  // O(1) lookup instead of entities.find() — the previous linear scan was
  // called twice per comparison inside compareInvoices' 'company' case, so
  // sorting/printing the full (unpaginated) list turned into an
  // O(n log n × entities) hot path that got noticeably slow at scale.
  const entityMap = useMemo(() => new Map(entities.map(e => [e.id, e])), [entities]);
  function entityName(id: string) { return entityMap.get(id)?.companyName ?? '—'; }

  // ── Filtering ─────────────────────────────────────────────────────────────
  const baseVisible = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return invoices.filter(inv => {
      const lines = inv.lineItems ?? [];
      if (categoryFilter === 'real'  && !lines.some(li => (li.category ?? 'books-real') !== 'books-dummy')) return false;
      if (categoryFilter === 'books' && !lines.some(li => (li.category ?? 'books-real') !== 'cash-real'))   return false;
      if (!q) return true;
      const name = (entities.find(e => e.id === inv.fromEntityId)?.companyName ?? '').toLowerCase();
      const basic = invoiceNoMatches(inv.invoiceNo, q) ||
                    (inv.supplierInvoiceNo ?? '').toLowerCase().includes(q) ||
                    name.includes(q);
      if (basic) return true;
      return lines.some(li => (li.certificateNo ?? '').toLowerCase().includes(q));
    });
  }, [invoices, searchQ, categoryFilter, entities]);

  // Excel-style column filters (tick several values per column). Pay
  // Status replaced the old Status header filter.
  const filterColumns = useMemo<FilterColumn<PurchaseInvoice>[]>(() => [
    { key: 'category',  label: 'Cat.',       get: inv => purchaseCategoryOf(inv) ?? '',
      sort: (a, b) => CATEGORY_RANK.indexOf(a) - CATEGORY_RANK.indexOf(b),
      render: v => <InvoiceCategoryBadge category={v as TransactionCategory | 'mixed'} /> },
    { key: 'company',   label: 'Company',    get: inv => entityName(inv.fromEntityId) },
    { key: 'dueDate', label: 'Due Date', get: inv => {
      if (inv.paymentStatus === 'paid') return 'Paid';
      const d = dueDateDisplay(inv.dueDate);
      return d.isOverdue ? 'Overdue' : d.isSoon ? 'Due soon' : 'Not due yet';
    }, sort: (a, b) => ['Overdue', 'Due soon', 'Not due yet', 'Paid'].indexOf(a) - ['Overdue', 'Due soon', 'Not due yet', 'Paid'].indexOf(b) },
    { key: 'payStatus', label: 'Pay Status', get: inv => ({ paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid' } as Record<string, string>)[inv.paymentStatus ?? 'unpaid'] ?? 'Unpaid',
      sort: (a, b) => ['Unpaid', 'Partial', 'Paid'].indexOf(a) - ['Unpaid', 'Partial', 'Paid'].indexOf(b) },
    { key: 'addedBy',   label: 'Added by',   get: inv => addedByName(inv, user) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entityMap, user]);
  const { rows: visible, filters: colFilters } = useColumnFilters(baseVisible, filterColumns);
  const filterable = new Set(filterColumns.map(c => c.key));

  useEffect(() => { setCurrentPage(1); setSelectedIds(new Set()); }, [dateFilter, customFrom, customTo, searchQ, categoryFilter, colFilters.stamp]);

  // ── Sorting ───────────────────────────────────────────────────────────────
  function handleSort(key: SortKey) {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  }
  function compareInvoices(a: PurchaseInvoice, b: PurchaseInvoice): number {
    switch (sortBy) {
      case 'category':          return CATEGORY_RANK.indexOf(purchaseCategoryOf(a) ?? '') - CATEGORY_RANK.indexOf(purchaseCategoryOf(b) ?? '');
      case 'invoiceNo':         return (a.invoiceNo ?? '').localeCompare(b.invoiceNo ?? '');
      case 'purchaseDate':      return (a.purchaseDate ?? '').localeCompare(b.purchaseDate ?? '');
      case 'supplierInvoiceNo': return (a.supplierInvoiceNo ?? '').localeCompare(b.supplierInvoiceNo ?? '');
      case 'company':           return entityName(a.fromEntityId).localeCompare(entityName(b.fromEntityId));
      case 'carat':             return (a.totalCarat ?? 0) - (b.totalCarat ?? 0);
      case 'pricePerCt':        return invoiceMetrics(a).pricePerCt - invoiceMetrics(b).pricePerCt;
      case 'netAmount':         return ((a.totalAmountUSD ?? 0) - (a.taxTotalUSD ?? 0)) - ((b.totalAmountUSD ?? 0) - (b.taxTotalUSD ?? 0));
      case 'gstAmount':         return (a.taxTotalUSD ?? 0) - (b.taxTotalUSD ?? 0);
      case 'totalAmount':       return (a.totalAmountUSD ?? 0) - (b.totalAmountUSD ?? 0);
      case 'dueDate':           return (a.dueDate ?? '').localeCompare(b.dueDate ?? '');
      case 'payStatus':         return PAY_RANK[a.paymentStatus ?? 'unpaid'] - PAY_RANK[b.paymentStatus ?? 'unpaid'];
      case 'addedBy':           return addedByName(a, user).localeCompare(addedByName(b, user));
    }
  }
  const sortedVisible = useMemo(() => {
    const arr = [...visible];
    arr.sort((a, b) => sortDir === 'asc' ? compareInvoices(a, b) : compareInvoices(b, a));
    return arr;
  }, [visible, sortBy, sortDir, entities]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages    = Math.ceil(sortedVisible.length / PAGE_SIZE);
  const pagedInvoices = sortedVisible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Footer totals — grand totals of the whole filtered/sorted list (same
  // "whole filtered set, not just the current page" rule CSV export/other
  // tfoot totals in this app already follow), shown both on screen and
  // when printing.
  const footerTotals = useMemo(() => {
    const items  = sortedVisible.reduce((s, inv) => s + (inv.itemCount ?? inv.lineItems?.length ?? 0), 0);
    const ct     = sortedVisible.reduce((s, inv) => s + (inv.totalCarat ?? 0), 0);
    const amount = sortedVisible.reduce((s, inv) => s + (inv.totalAmountUSD ?? 0), 0);
    const goods  = amount - sortedVisible.reduce((s, inv) => s + (inv.taxTotalUSD ?? 0), 0);   // without GST
    return { items, ct, amount, avgPricePerCt: ct > 0 ? goods / ct : 0 };
  }, [sortedVisible]);

  // ── Export — CSV of every filtered/sorted invoice (not just the current
  // page), using whatever columns are currently visible so the export
  // matches what's on screen. Print reuses the same @media print rules
  // every list page shares (see index.css) via a plain window.print(). ──
  function exportRowFor(inv: PurchaseInvoice, key: ColumnKey): string {
    switch (key) {
      case 'category': {
        const c = purchaseCategoryOf(inv);
        return c ? (c === 'mixed' ? 'Mixed' : CATEGORY_META[c].label) : '';
      }
      case 'invoiceNo':         return inv.invoiceNo ?? '';
      case 'purchaseDate':      return inv.purchaseDate ?? '';
      case 'company':           return entityName(inv.fromEntityId);
      case 'carat':             return (inv.totalCarat ?? 0).toFixed(2);
      case 'pricePerCt':        return invoiceMetrics(inv).pricePerCt.toFixed(2);
      case 'netAmount':         return ((inv.totalAmountUSD ?? 0) - (inv.taxTotalUSD ?? 0)).toFixed(2);
      case 'gstAmount':         return (inv.taxTotalUSD ?? 0).toFixed(2);
      case 'totalAmount':       return (inv.totalAmountUSD ?? 0).toFixed(2);
      case 'supplierInvoiceNo': return inv.supplierInvoiceNo ?? '';
      case 'dueDate':           return inv.dueDate ?? '';
      case 'payStatus':         return inv.paymentStatus ?? 'unpaid';
      case 'addedBy':           return addedByName(inv, user);
    }
  }
  function handleExportCSV() {
    const cols = SHOWN_COLUMNS.filter(c => visibleColumns[c.key]);
    downloadCSV(
      'purchase-invoices',
      cols.map(c => c.label),
      sortedVisible.map(inv => cols.map(c => exportRowFor(inv, c.key))),
    );
  }
  // Printing the on-screen page would only print PAGE_SIZE rows — bypass
  // pagination for the print pass (every filtered/sorted invoice instead),
  // then restore normal pagination once the print dialog closes. Waits for
  // two animation frames (not a fixed setTimeout) before calling print() —
  // a guessed delay is either wasted time on a short list or, worse, fires
  // before a long list has actually finished rendering; two rAFs guarantee
  // the browser has painted the just-swapped-in full list first, which is
  // what actually fixes "Print List feels slow to open."
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    window.addEventListener('afterprint', after);
    requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => { if (!cancelled) window.print(); });
    });
    return () => { cancelled = true; window.removeEventListener('afterprint', after); };
  }, [printAll]);
  const displayedInvoices = printAll ? sortedVisible : pagedInvoices;
  // Scrolls inside, header row pinned, ending just above the screen's bottom edge.
  const purchaseWrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(purchaseWrapRef, 56, 280, [displayedInvoices.length > 0, loading]);

  // ── Selection ─────────────────────────────────────────────────────────────
  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const pageIds = pagedInvoices.map(i => i.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
  function toggleSelectPage() {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach(id => next.delete(id));
      else pageIds.forEach(id => next.add(id));
      return next;
    });
  }
  const selectedInvoices = useMemo(
    () => visible.filter(inv => selectedIds.has(inv.id)),
    [visible, selectedIds],
  );
  const selectionTotals = useMemo(() => ({
    items: selectedInvoices.reduce((s, i) => s + (i.itemCount ?? i.lineItems?.length ?? 0), 0),
    ct:    selectedInvoices.reduce((s, i) => s + (i.totalCarat ?? 0), 0),
    amt:   selectedInvoices.reduce((s, i) => s + (i.totalAmountUSD ?? 0), 0),
  }), [selectedInvoices]);

  // ── Actions ───────────────────────────────────────────────────────────────
  function openAdd()  { setEditInvoice(null); setBatchOpen(true); }
  // Dashboard "+ New" opens this form via ?new=1.
  useNewParam(openAdd);
  function openEdit(inv: PurchaseInvoice) { setEditInvoice(inv); setBatchOpen(true); }
  function closeBatch() { setBatchOpen(false); setEditInvoice(null); setImported(null); }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError('');
    const db = getClientDb();
    if (db) {
      try {
        const retSnap = await getDocs(query(collection(db, 'purchaseReturns'), where('invoiceId', '==', deleteTarget.id)));
        if (!retSnap.empty) {
          setDeleteError('Cannot delete — this purchase has a return recorded against it. Delete the return first.');
          setDeleting(false);
          return;
        }
        // paidUSD covers both real payments (invoices/{id}/payments) and any
        // supplier-advance applied to this invoice — deleting straight through
        // would orphan that money (bank balance already moved, or the advance
        // permanently marked "used") with no invoice left to explain it. Both
        // have their own correct reversal flow (delete the payment on AP /
        // "Un-apply" on Supplier Advances) — send the user there first.
        if ((deleteTarget.paidUSD ?? 0) > 0.005) {
          setDeleteError('Cannot delete — this purchase has a payment or an applied advance recorded against it. Remove those first (AP → delete the payment, or Supplier Advances → Un-apply).');
          setDeleting(false);
          return;
        }
        if ((deleteTarget.discountUSD ?? 0) > 0.005) {
          setDeleteError('Cannot delete: this purchase has a discount on it. Delete the discount first (open the invoice, Discounts).');
          setDeleting(false);
          return;
        }
        if ((deleteTarget.brokerPaidUSD ?? 0) > 0.005) {
          setDeleteError('Cannot delete — a broker commission payment is recorded against this purchase. Delete that payment on the Brokers page first.');
          setDeleting(false);
          return;
        }
        // Same guard, for a payment against one of the per-line Broker1/
        // Broker2 brokers — those don't update deleteTarget.brokerPaidUSD
        // (that field is the header broker's own), so they need their own
        // check against the shared brokerPayments subcollection. Fetched
        // unfiltered (this subcollection is always small) and checked
        // client-side rather than an inequality query, to sidestep any
        // Firestore index/missing-field subtlety on `!= null`.
        const lineBrokerPaySnap = await getDocs(collection(db, 'invoices', deleteTarget.id, 'brokerPayments'));
        if (lineBrokerPaySnap.docs.some(d => !!(d.data() as BrokerPayment).brokerEntityId)) {
          setDeleteError('Cannot delete — a broker commission payment is recorded against this purchase. Delete that payment on the Brokers page first.');
          setDeleting(false);
          return;
        }
        // Additional Details (Purchase Expenses) linked to this invoice —
        // a still-paid one already moved real money, same "go undo it
        // properly first" reasoning as the payment/broker guards above.
        // An unpaid one never touched a bank balance, so it's safe to
        // just remove along with the invoice (see the cascade below).
        const peSnap = await getDocs(query(collection(db, 'purchaseExpenses'), where('invoiceId', '==', deleteTarget.id)));
        const linkedExpenses = peSnap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseExpense));
        if (linkedExpenses.some(pe => pe.paymentStatus === 'paid')) {
          setDeleteError('Cannot delete — this purchase has a paid Additional Detail (Purchase Expense) recorded against it. Delete that payment on the Purchase Expenses page first.');
          setDeleting(false);
          return;
        }
        const invItems = await getDocs(query(collection(db, 'inventory'), where('purchaseId', '==', deleteTarget.id)));
        const notInStock = invItems.docs.filter(d => (d.data() as InventoryItem).status !== 'in-stock');
        if (notInStock.length > 0) {
          setDeleteError(`Cannot delete — ${notInStock.length} item(s) from this invoice are already sold or on memo.`);
          setDeleting(false);
          return;
        }
        // Inventory Activity reads itemCodes/{codeId}/history — deleting the
        // invoice must also remove the 'purchase' entry it wrote there (or
        // the row keeps showing for a purchase that no longer exists) and
        // free the Code registration itself (or that Code can never be
        // typed again — the create/edit collision check would keep finding
        // an itemCodes doc that still points at a now-deleted item).
        // Matched precisely by type + inventoryItemId, never a blanket wipe
        // of the whole history subcollection — an item can stay 'in-stock'
        // (passing the guard above) after a partial Split & Merge cut, which
        // would already have written unrelated 'split-out' entries under the
        // same Code that must not be touched here.
        const codeCleanup = await Promise.all(invItems.docs.map(async d => {
          const item = d.data() as InventoryItem;
          if (!item.codeId) return { codeDocRef: null, historyRefs: [] as DocumentReference[] };
          const codeSnap = await getDoc(doc(db, 'itemCodes', item.codeId));
          const stillOwned = codeSnap.exists() && (codeSnap.data() as ItemCode).inventoryItemId === d.id;
          const histSnap = await getDocs(query(
            collection(db, 'itemCodes', item.codeId, 'history'),
            where('type', '==', 'purchase'), where('inventoryItemId', '==', d.id),
          ));
          return {
            codeDocRef: stillOwned ? doc(db, 'itemCodes', item.codeId) : null,
            historyRefs: histSnap.docs.map(h => h.ref),
          };
        }));
        // A transaction (not a plain batch) because taking this invoice's
        // Books & Dummy credit back off the Dummy balance needs a read first.
        const hasDummyLines = (deleteTarget.lineItems ?? []).some(isDummyPurchaseLine);
        await runTransaction(db, async batch => {
          const dummyPlan = hasDummyLines
            ? await planDummyDelta(batch, db, {
                businessId: business!.id, historyId: `dummypurchase_${deleteTarget.id}`,
                aggregate: { carat: 0, costUSD: 0, costingUSD: 0 },
                cursor: { n: 0 }, reserved: new Set(), nextCode: nextFreeCode,
              })
            : null;
          applyDummyDelta(batch, db, dummyPlan, {
            type: 'purchase', date: deleteTarget.purchaseDate || todayISO(), entityId: deleteTarget.fromEntityId,
            category: 'books-dummy', notes: `Dummy purchase — Lot ${deleteTarget.invoiceNo}`, createdBy: user!.id,
          });
          batch.delete(doc(db, 'invoices', deleteTarget.id));
          logActivity(batch, db, business!.id, user, {
            module: 'purchase', action: 'delete', recordId: deleteTarget.id, recordNo: deleteTarget.invoiceNo,
            partyId: deleteTarget.fromEntityId || undefined, itemCount: deleteTarget.itemCount,
            carat: deleteTarget.totalCarat, amountUSD: deleteTarget.totalAmountUSD,
          });
          batch.delete(doc(db, 'journalEntries', entryId('purchase_invoice', deleteTarget.id)));
          batch.delete(doc(db, 'journalEntries', entryId('broker_accrual', deleteTarget.id)));
          for (const brokerEntityId of computeLineBrokerCommissions(deleteTarget.lineItems ?? []).keys()) {
            batch.delete(doc(db, 'journalEntries', lineBrokerAccrualId(deleteTarget.id, brokerEntityId)));
          }
          invItems.docs.forEach(d => {
            batch.delete(d.ref);
            const item = d.data() as InventoryItem;
            if (item.skuId) batch.delete(doc(db, 'skus', item.skuId, 'history', item.lineItemId));
          });
          codeCleanup.forEach(({ codeDocRef, historyRefs }) => {
            if (codeDocRef) batch.delete(codeDocRef);
            historyRefs.forEach(ref => batch.delete(ref));
          });
          // Every linked Additional Detail is unpaid at this point (paid ones
          // already blocked the delete above) — nothing to reverse, just
          // remove them along with the invoice they belong to.
          linkedExpenses.forEach(pe => {
            batch.delete(doc(db, 'purchaseExpenses', pe.id));
            batch.delete(doc(db, 'journalEntries', entryId('purchase_expense', pe.id)));
          });
        });
        setDeleteTarget(null);
      } catch (e) {
        setDeleteError(e instanceof Error ? e.message : 'Failed to delete.');
      }
    }
    setDeleting(false);
  }

  const hasActiveFilters = !!searchQ || colFilters.active.length > 0;

  // Skeleton column count always matches the real table's visible columns,
  // so loading → loaded never "jumps" to a different number of columns.
  const skeletonWidths = [
    CHECKBOX_SKELETON_WIDTH,
    ...SHOWN_COLUMNS.filter(c => visibleColumns[c.key]).map(c => c.skeletonWidth),
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AppLayout title="Purchase" subtitle="Polished certified, loose diamonds, and rough" toolbar={<>
          {/* Left: find. Search, then the filters (each names its choice). */}
          <div className="tb-search">
            <span className="tb-search-icon"><IconSearch /></span>
            <input
              value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Invoice no, cert no, supplier…"
              aria-label="Search purchases"
            />
            {searchQ && (
              <button type="button" className="tb-search-clear" onClick={() => setSearchQ('')} title="Clear search">×</button>
            )}
          </div>

          {/* Date: one button naming the range; Custom shows two date boxes. */}
          <Dropdown
            trigger={() => (
              <button type="button" className={`icon-btn${dateFilter !== '3-months' ? ' active' : ''}`} style={{ height: 34 }}>
                <IconCalendarSm /> {DATE_FILTER_LABELS[dateFilter]}
                <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconChevronDownSm /></span>
              </button>
            )}
          >
            {([
              { key: 'this-month', label: 'This Month' },
              { key: '3-months',   label: '3 Months'   },
              { key: 'this-year',  label: 'This Year'  },
              { key: 'custom',     label: 'Custom'     },
            ] as { key: DateFilter; label: string }[]).map(({ key, label }) => (
              <button key={key} type="button" className="dropdown-item" onClick={() => setDateFilter(key)}>
                <span style={{ width: 14, display: 'flex', color: 'var(--primary)', flexShrink: 0 }}>
                  {dateFilter === key && <IconCheckSm />}
                </span>
                {label}
              </button>
            ))}
          </Dropdown>
          {dateFilter === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="date" className="tb-date" value={customFrom} max={customTo || todayISO()}
                onChange={e => setCustomFrom(e.target.value)} aria-label="From date" />
              <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>→</span>
              <input type="date" className="tb-date" value={customTo} min={customFrom || undefined} max={todayISO()}
                onChange={e => setCustomTo(e.target.value)} aria-label="To date" />
            </div>
          )}

          {hasActiveFilters && (
            <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); colFilters.clear(); }}>
              Clear
            </button>
          )}
      </>}
      headerActions={<>
          {/* Right: tools as one joined group, then the main action last. */}
          <div className="tb-group">
            <Dropdown
              align="right"
              closeOnInnerClick={false}
              trigger={() => (
                <button type="button" className="tb-group-btn" title="Columns" aria-label="Columns"><IconColumns /></button>
              )}
            >
              <div className="dropdown-group-label">Show Columns</div>
              {SHOWN_COLUMNS.map(c => (
                <label key={c.key} className="dropdown-item" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[c.key]}
                    onChange={() => toggleColumn(c.key)}
                    style={{ accentColor: 'var(--primary)' }}
                  />
                  {c.label}
                </label>
              ))}
              <div className="dropdown-divider" />
              <button type="button" className="dropdown-item" onClick={() => setVisibleColumns({ ...DEFAULT_VISIBLE })}>
                Select All
              </button>
            </Dropdown>
            <button type="button" className="tb-group-btn" title="Import from Excel" aria-label="Import from Excel"
              onClick={() => { setImportedNos([]); setImportOpen(true); }}>
              <IconImport />
            </button>
            <Dropdown
              align="right"
              trigger={() => (
                <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExport /></button>
              )}
            >
              <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
              <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
            </Dropdown>
          </div>
          <Button variant="primary" size="sm" icon={<IconPlus />} onClick={openAdd} style={{ height: 34, boxSizing: 'border-box' }}>
            Add Invoice
          </Button>
      </>}>


      {/* ── Selection details + Column customizer — one row. The strip is
           always rendered with real content, never hidden: with nothing
           selected it shows totals for the whole filtered list; selecting
           any invoices swaps it to totals for just the selection. Columns
           lives as a sibling outside it, so it stays clickable regardless
           of selection — and is given the same explicit height so the two
           line up. Chips use the same small compact-strip style as Memo
           Out's client-scoped totals / Inventory's "Items / Total ct /
           Total Amount" row, instead of one primary-tinted bar. ── */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: '1 1 auto',
          height: 38, boxSizing: 'border-box',
        }}>
          {(selectedIds.size > 0 ? [
            { label: 'Selected', val: `${selectedIds.size} invoice${selectedIds.size !== 1 ? 's' : ''}` },
            { label: 'Items', val: String(selectionTotals.items) },
            { label: 'Total ct', val: `${selectionTotals.ct.toFixed(2)} ct` },
            { label: 'Amount', val: money(selectionTotals.amt) },
          ] : [
            { label: 'All Invoices', val: `${sortedVisible.length} invoice${sortedVisible.length !== 1 ? 's' : ''}` },
            { label: 'Items', val: String(footerTotals.items) },
            { label: 'Total ct', val: `${footerTotals.ct.toFixed(2)} ct` },
            { label: 'Amount', val: money(footerTotals.amount) },
          ]).map(({ label, val }) => (
            <div key={label} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)', padding: '6px 12px',
              boxShadow: 'var(--shadow-xs)', fontSize: 12,
            }}>
              <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>{label}</span>
              <span style={{ width: 1, height: 12, background: 'var(--border)', flexShrink: 0 }} />
              <span style={{ color: 'var(--text-1)', fontWeight: 700, fontFamily: 'var(--font-num)' }}>{val}</span>
            </div>
          ))}
          {selectedIds.size > 0 && (
            <button onClick={() => setSelectedIds(new Set())} style={{
              display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 700,
              color: 'var(--danger)', background: 'var(--danger-bg)',
              border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
              borderRadius: 'var(--r-sm)', padding: '6px 12px', cursor: 'pointer',
            }}>
              Clear selection
            </button>
          )}
        </div>
        <ColumnFilterChips filters={colFilters} inline />
      </div>

      {importedNos.length > 0 && (
        <div className="msg msg-note no-print" style={{ marginBottom: 12 }}>
          <span className="msg-note-icon"><IconNoteInfo /></span>
          <span style={{ flex: 1 }}>
            Imported {importedNos.length} purchase invoice{importedNos.length !== 1 ? 's' : ''}:{' '}
            <strong>{importedNos.join(', ')}</strong>
          </span>
          <button type="button" className="line-item-link-btn" onClick={() => setImportedNos([])}>Dismiss</button>
        </div>
      )}

      {/* ── Custom date prompt ── */}
      {dateFilter === 'custom' && !dateRange && (
        <div style={{
          background: 'var(--surface)', border: '1.5px dashed var(--border)',
          borderRadius: 'var(--r-md)', padding: '36px 24px', textAlign: 'center', marginBottom: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: 'var(--text-3)' }}>
            <IconEmptyCalendar />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>Select a date range</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Choose a From and To date above to load invoices.</div>
        </div>
      )}

      {/* ── Table / states ── */}
      {loading ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>{skeletonWidths.map((w, i) => (
                <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>
              ))}</tr>
            </thead>
            <tbody>{[...Array(5)].map((_, i) => (
              <tr key={i} style={{ opacity: 1 - i * 0.18 }}>
                {skeletonWidths.map((w, j) => (
                  <td key={j}><div className="skeleton" style={{ height: 13, width: w, borderRadius: 4 }} /></td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : dateRange && visible.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--text-3)' }}>
            {invoices.length === 0 ? <IconEmptyDiamond /> : <IconEmptySearch />}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>
            {invoices.length === 0 ? 'No invoices in this period' : 'No invoices match your filters'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 320, margin: '0 auto' }}>
            {invoices.length === 0
              ? 'Click "Add Invoice" to record your first purchase.'
              : 'Try adjusting your payment filter or search.'}
          </div>
          {invoices.length === 0
            ? <div style={{ marginTop: 20 }}><Button variant="primary" size="sm" icon={<IconPlus />} onClick={openAdd}>Add Invoice</Button></div>
            : <div style={{ marginTop: 16 }}>
                <button onClick={() => { setSearchQ(''); colFilters.clear(); }}
                  style={{ fontSize: 13, padding: '5px 10px', border: '1.5px solid color-mix(in srgb, var(--danger) 35%, transparent)', borderRadius: 'var(--r-sm)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                  Clear all filters
                </button>
              </div>
          }
        </div>
      ) : dateRange ? (
        <>
          <div ref={purchaseWrapRef} className="table-wrap table-fit table-fit-stats">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32, textAlign: 'center' }}>
                    <input type="checkbox" checked={allPageSelected} onChange={toggleSelectPage} title="Select all on page" />
                  </th>
                  {SHOWN_COLUMNS.filter(c => visibleColumns[c.key]).map(c => (
                    <SortableTh key={c.key} label={c.label} sortKey={c.sortKey} activeKey={sortBy} dir={sortDir} onSort={handleSort} minWidth={c.minWidth}
                      width={c.key === 'company' ? 220 : c.key === 'category' ? 56 : undefined}
                      filter={filterable.has(c.key) ? <ColumnFilterButton filters={colFilters} colKey={c.key} label={c.label} /> : undefined} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedInvoices.map(inv => {
                  const isPaid  = inv.paymentStatus === 'paid';
                  const dd      = dueDateDisplay(inv.dueDate);
                  const overdue = !isPaid && dd.isOverdue;
                  const soon    = !isPaid && dd.isSoon;
                  const accent  = overdue ? 'var(--danger)' : soon ? '#f59e0b' : 'transparent';
                  const { ct, pricePerCt } = invoiceMetrics(inv);
                  const purchaseDateLabel = inv.purchaseDate
                    ? new Date(inv.purchaseDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : null;
                  return (
                    <tr key={inv.id} style={{ transition: 'background 0.12s', cursor: 'pointer' }}
                      onClick={() => setPreviewInvoice(inv)}>
                        <td style={{ width: 32, textAlign: 'center', paddingLeft: 6, paddingRight: 2, borderLeft: `3px solid ${accent}`, transition: 'border-color 0.15s' }}
                          onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedIds.has(inv.id)} onChange={() => toggleSelect(inv.id)} />
                        </td>
                        {visibleColumns.category && (
                          <td style={{ textAlign: 'center' }}><InvoiceCategoryBadge category={purchaseCategoryOf(inv)} /></td>
                        )}
                        {visibleColumns.invoiceNo && (
                          <td>
                            <span
                              title={inv.invoiceNo || undefined}
                              style={{
                                display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
                                fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
                                padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em',
                              }}>
                              {lotNoDisplay(inv.invoiceNo)}
                            </span>
                          </td>
                        )}
                        {visibleColumns.purchaseDate && (
                          <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                            {purchaseDateLabel ?? <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                        )}
                        {visibleColumns.company && (
                          <td style={{ fontSize: 12, color: 'var(--text-2)', maxWidth: 220 }}>
                            <span className="cell-clip" title={entityName(inv.fromEntityId)}>{entityName(inv.fromEntityId)}</span>
                          </td>
                        )}
                        {visibleColumns.carat && (
                          <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>
                            {ct.toFixed(2)} ct
                          </td>
                        )}
                        {visibleColumns.pricePerCt && (
                          <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>
                            {pricePerCt > 0 ? money(pricePerCt) : <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                        )}
                        {indianList && visibleColumns.netAmount && (
                          <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                            {money((inv.totalAmountUSD ?? 0) - (inv.taxTotalUSD ?? 0))}
                          </td>
                        )}
                        {indianList && visibleColumns.gstAmount && (
                          <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap' }}
                            title={inv.taxTotalUSD ? [inv.cgstUSD ? `CGST ${money(inv.cgstUSD)}` : '', inv.sgstUSD ? `SGST ${money(inv.sgstUSD)}` : '', inv.igstUSD ? `IGST ${money(inv.igstUSD)}` : ''].filter(Boolean).join(' · ') : undefined}>
                            {inv.taxTotalUSD ? money(inv.taxTotalUSD) : <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                        )}
                        {visibleColumns.totalAmount && (
                          <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                            {(inv.totalAmountUSD ?? 0) > 0 ? money(inv.totalAmountUSD ?? 0) : <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                        )}
                        {visibleColumns.supplierInvoiceNo && (
                          <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-3)' }}>
                            {inv.supplierInvoiceNo || <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                        )}
                        {visibleColumns.dueDate && (
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span style={{ fontSize: 12, fontWeight: 500, color: overdue ? 'var(--danger)' : soon ? '#c2410c' : 'var(--text-1)' }}>{dd.label}</span>
                              {!isPaid && <span style={{ fontSize: 10, fontWeight: 600, lineHeight: 1, color: overdue ? 'var(--danger)' : soon ? '#f59e0b' : 'var(--text-3)' }}>{dd.badge}</span>}
                            </div>
                          </td>
                        )}
                        {visibleColumns.payStatus && <td><PayBadge inv={inv} /></td>}
                        {visibleColumns.addedBy && (
                          <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{addedByName(inv, user) || <span style={{ opacity: 0.4 }}>—</span>}</td>
                        )}
                      </tr>
                  );
                })}
              </tbody>
              {printAll && sortedVisible.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={SHOWN_COLUMNS.filter(c => visibleColumns[c.key]).length + 1}>
                      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
                        <span style={{ color: 'var(--text-2)' }}>
                          Total ct <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{footerTotals.ct.toFixed(2)}</strong>
                        </span>
                        <span style={{ color: 'var(--text-2)' }}>
                          Total Amount <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(footerTotals.amount)}</strong>
                        </span>
                        <span style={{ color: 'var(--text-2)' }}>
                          Avg. Cost/ct <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(footerTotals.avgPricePerCt)}</strong>
                        </span>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="no-print">
            <Pagination
              current={currentPage} total={totalPages}
              totalItems={sortedVisible.length} pageSize={PAGE_SIZE}
              onChange={setCurrentPage}
            />
          </div>
        </>
      ) : null}

      <BatchAddModal
        open={batchOpen} onClose={closeBatch}
        entities={fromEntities} settings={settings}
        editInvoice={editInvoice}
        importedItems={imported?.items}
        importedFrom={imported?.fileName}
        importedHeader={imported?.header}
      />

      <ImportPurchaseModal
        key={importOpen ? 'import-open' : 'import-closed'}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        entities={fromEntities}
        settings={settings}
        onReview={(items, fileName, header) => {
          setImportOpen(false);
          setEditInvoice(null);
          setImported({ items, fileName, header });
          setBatchOpen(true);
        }}
        onImported={(nos, done) => {
          setImportedNos(prev => [...prev, ...nos]);
          if (done) setImportOpen(false);
        }}
      />

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => { setDeleteTarget(null); setDeleteError(''); }}
        onConfirm={handleDelete}
        title="Delete Invoice"
        message={`Delete invoice ${deleteTarget?.invoiceNo ?? ''}? This cannot be undone.`}
        confirmLabel="Delete Invoice"
        loading={deleting} error={deleteError}
      />

      <RecordPaymentModal invoice={payTarget} onClose={() => setPayTarget(null)} entityName={payTarget ? entityName(payTarget.fromEntityId) : undefined} />

      <InvoicePreviewModal
        invoice={previewInvoice}
        entityName={entityName}
        onClose={() => setPreviewInvoice(null)}
        onPay={inv => setPayTarget(inv)}
        onEdit={inv => openEdit(inv)}
        onDelete={inv => { setDeleteTarget(inv); setDeleteError(''); }}
        canEditDiscounts
      />
    </AppLayout>
  );
}
